import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ChevronRight, ChevronLeft, User, Building2, FileText, Scale, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import LargeDatePicker from './LargeDatePicker';
import { getCourtTypeLabel } from '../utils/courtLabels';
import InlineDatePicker from './InlineDatePicker';
import DeadlinePreview from './DeadlinePreview';
import SearchableClientSelect from './SearchableClientSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ιδιότητα εντολέα ανά είδος δικογράφου/διαδικασία
export const getClientRoleOptions = (procedureType, documentType) => {
  if (procedureType === 'criminal') return ['Κατηγορούμενος', 'Πολιτική αγωγή'];
  if (procedureType === 'administrative') {
    const adminMap = {
      lawsuit: ['Ενάγων', 'Εναγόμενος'],
      admin_agogi: ['Ενάγων', 'Εναγόμενος'],
      admin_efesi: ['Εκκαλών', 'Εφεσίβλητος'],
      appeal_admin: ['Εκκαλών', 'Εφεσίβλητος'],
      cassation_admin: ['Αναιρεσείων', 'Αναιρεσίβλητος'],
      cancellation_request: ['Αιτών', "Καθ' ου η αίτηση"],
    };
    return adminMap[documentType] || ['Προσφεύγων', "Καθ' ου η προσφυγή"];
  }
  const civilMap = {
    lawsuit: ['Ενάγων', 'Εναγόμενος'],
    special_lawsuit: ['Ενάγων', 'Εναγόμενος'],
    small_claims: ['Ενάγων', 'Εναγόμενος'],
    voluntary_app: ['Αιτών', "Καθ' ου η αίτηση"],
    interim_app: ['Αιτών', "Καθ' ου η αίτηση"],
    injunction: ['Αιτών', "Καθ' ου η διαταγή"],
    revision: ['Αιτών', "Καθ' ου η αίτηση"],
    appeal: ['Εκκαλών', 'Εφεσίβλητος'],
    appeal_peace: ['Εκκαλών', 'Εφεσίβλητος'],
    appeal_single_first: ['Εκκαλών', 'Εφεσίβλητος'],
    criminal_appeal: ['Εκκαλών', 'Εφεσίβλητος'],
    cassation: ['Αναιρεσείων', 'Αναιρεσίβλητος'],
    criminal_cassation: ['Αναιρεσείων', 'Αναιρεσίβλητος'],
    objection: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    objection_dp: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    objection_933: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    objection_954: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    objection_default: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    rent_objection: ['Ανακόπτων', "Καθ' ου η ανακοπή"],
    third_party_obj: ['Τριτανακόπτων', "Καθ' ου η τριτανακοπή"],
  };
  return civilMap[documentType] || ['Ενάγων', 'Εναγόμενος'];
};

const ClientRoleSelect = ({ value, options, onChange, testId }) => (
  <div>
    <Label>Ιδιότητα εντολέα *</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-11 bg-white border-stone-300 rounded-lg items-center">
        <SelectValue placeholder="Επιλέξτε ιδιότητα εντολέα" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

// Helper function to convert to uppercase and remove Greek accents
const shiftWorkingDays = (dateStr, n) => {
  const d = new Date(`${(dateStr || '').slice(0, 10)}T12:00:00`);
  const step = n > 0 ? 1 : -1;
  let left = Math.abs(n);
  while (left > 0) {
    d.setDate(d.getDate() + step);
    if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
  }
  return d.toISOString().slice(0, 10);
};

const toUpperCaseNoAccents = (str) => {
  if (!str) return '';
  const accentMap = {
    'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
    'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
    'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
  };
  return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
};

// Helper function to capitalize only first letter of first word (keep accents)
const capitalizeFirst = (str) => {
  if (!str) return '';
  const s = str.trim();
  if (s === s.toUpperCase()) {
    const low = s.toLowerCase();
    return low.charAt(0).toUpperCase() + low.slice(1);
  }
  // Μικτή γραφή: διατήρησε λέξεις γραμμένες ΟΛΟΚΕΦΑΛΑΙΑ (ονόματα πελατών, ακρωνύμια)
  const result = s.split(/(\s+)/).map((w) => {
    const letters = w.replace(/[^\p{L}]/gu, '');
    if (letters.length >= 2 && letters === letters.toUpperCase()) return w;
    return w.toLowerCase();
  }).join('');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

// Ερώτημα πολιτικών υποθέσεων: αντίδικος το Ελληνικό Δημόσιο (αναστολή 1/7–15/9)
const MediationToggle = ({ value, onChange }) => (
  <div>
    <Label>Απαιτείται διαμεσολάβηση; *</Label>
    <p className="text-xs text-slate-600 mt-1 mb-2">
      Αν ναι, θα προστεθούν οι εργασίες: Αίτημα για ορισμό διαμεσολαβητή, Αποστολή στοιχείων σε διαμεσολαβητή
    </p>
    <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
      <button
        type="button"
        data-testid="mediation-yes-btn"
        onClick={() => onChange(true)}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          value === true ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
        }`}
      >
        Ναι
      </button>
      <button
        type="button"
        data-testid="mediation-no-btn"
        onClick={() => onChange(false)}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          value === false ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
        }`}
      >
        Όχι
      </button>
    </div>
  </div>
);

const PublicEntityToggle = ({ value, onChange, testidPrefix = 'public-entity' }) => (
  <div>
    <Label>Αντίδικος είναι το Ελληνικό Δημόσιο; *</Label>
    <p className="text-xs text-slate-600 mt-1 mb-2">
      Αν ναι, η αναστολή εκτείνεται σε όλες τις δικαστικές διακοπές (1 Ιουλίου – 15 Σεπτεμβρίου) αντί μόνο του Αυγούστου
    </p>
    <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
      <button
        type="button"
        data-testid={`${testidPrefix}-yes-btn`}
        onClick={() => onChange(true)}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          value === true ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
        }`}
      >
        Ναι
      </button>
      <button
        type="button"
        data-testid={`${testidPrefix}-no-btn`}
        onClick={() => onChange(false)}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
          value === false ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
        }`}
      >
        Όχι
      </button>
    </div>
  </div>
);

// Case description type labels
const CASE_DESCRIPTION_LABELS = {
  'debt': 'Αγωγή χρέους', 'property': 'Εμπράγματη αγωγή', 'fraud': 'Αγωγή καταδολίευσης',
  'inheritance': 'Κληρονομική διαφορά', 'divorce': 'Διαζύγιο', 'alimony': 'Διατροφή',
  'custody': 'Επιμέλεια τέκνων', 'lease': 'Μισθωτική διαφορά', 'labor': 'Εργατική διαφορά',
  'tort': 'Αδικοπραξία', 'contract': 'Συμβατική διαφορά', 'unjust_enrichment': 'Αδικαιολόγητος πλουτισμός',
  'compensation': 'Αποζημίωση', 'ownership': 'Αναγνώριση κυριότητας', 'possession': 'Προστασία νομής',
  'easement': 'Δουλεία', 'condominium': 'Διαφορά οροφοκτησίας', 'insurance': 'Ασφαλιστική διαφορά',
  'medical': 'Ιατρική αμέλεια', 'traffic': 'Τροχαίο ατύχημα', 'other': 'Άλλο'
};

// Helper function to get case display text from entries
const getCaseDisplayText = (caseObj, entries) => {
  // Find an entry for this case that has description/opponent info
  const caseEntry = entries?.find(e => e.case_id === caseObj.id && (e.case_description_type || e.opponent_details));
  
  let description = '';
  let opponent = '';
  
  // Try to get case description from entry first
  if (caseEntry) {
    if (caseEntry.case_description_type === 'other' && caseEntry.case_description_custom) {
      description = caseEntry.case_description_custom;
    } else if (caseEntry.case_description_type) {
      description = CASE_DESCRIPTION_LABELS[caseEntry.case_description_type] || caseEntry.case_description_type;
    }
  }
  
  // Fallback to case description if no entry description
  if (!description && caseObj.description) {
    description = caseObj.description;
  }
  
  // Get opponent details - first from entry, then from case object
  if (caseEntry && caseEntry.opponent_details) {
    opponent = caseEntry.opponent_details;
  } else if (caseObj.opponent_details) {
    opponent = caseObj.opponent_details;
  }
  
  // Get GAK from case object
  const gak = caseObj.gak;
  
  // Build the parts array
  const parts = [];
  
  if (description) {
    parts.push(description);
  }
  
  if (opponent) {
    parts.push(`Αντίδικος: ${opponent}`);
  }
  
  if (gak) {
    parts.push(`ΓΑΚ: ${gak}`);
  }
  
  // Return formatted string or fallback
  if (parts.length > 0) {
    return parts.join(' | ');
  }
  
  return 'Υπόθεση χωρίς στοιχεία';
};

const EntryWizard = ({ onComplete, onCancel, onOpenCaseModal, prefillData }) => {
  const [selectedDeadlines, setSelectedDeadlines] = useState([]);
  const [dateError, setDateError] = useState('');
  const [postponementConflicts, setPostponementConflicts] = useState([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    entryType: 'court_action',
    clientOption: prefillData?.clientId ? 'existing' : '',
    clientType: '',
    caseOption: prefillData ? 'new' : '',
    selectedClientId: prefillData?.clientId || '',
    client: {
      surname: '',
      name: '',
      company_name: '',
      vat: '',
      phone: '',
      email: ''
    },
    case: {
      procedure_type: prefillData?.procedure_type || 'civil',
      description: '',
      court_type: prefillData?.court_type || '',
      court_location: prefillData?.court_location || '',
      document_type: prefillData?.document_type || '',
      opponent_details: prefillData?.opponent_details || '',
      gak: prefillData?.gak || '',  // ΓΑΚ (Γενικός αριθμός κατάθεσης)
      case_description_type: prefillData?.case_description_type || '',
      case_description_custom: prefillData?.case_description_custom || '',
      action_type: prefillData?.action_type || 'document',
      is_preliminary: '',
      preliminary_date: '',
      criminal_action_type: '',
      direct_summons: '',
      administrative_action_type: '',
      administrative_notice_type: '',
      administrative_notice_date: '',
      civil_other_type: '',
      civil_other_date: '',
      is_first_instance: '',
      is_small_claims: '',
      is_default_judgment: '',
      is_final: '',
      decision_result: '',
      payment_order_service_type: '',
      enforcement_remedy: '',
      enforcement_article: '',
      criminal_other_type: '',
      criminal_other_date: '',
      criminal_decision_level: '',
      criminal_was_present: '',
      criminal_decision_date: '',
      criminal_hearing_date: '',
      criminal_service_date: '',
      criminal_clean_copy_date: '',
      administrative_other_type: '',
      administrative_decision_court: '',
      administrative_decision_type: '',
      administrative_service_date: '',
      administrative_publication_date: '',
      administrative_decision_number: '',
      postponement_date: '',
      postponement_accepted: '',
      postponement_alt_date: '',
      postponement_is_new_case: '',
      postponement_selected_case_id: '',
      has_foreign_defendant: '',
      is_real_property: '',
      // Φάση Β: custom service dates (Εκουσία 1.3 / Ασφαλιστικά 1.4 — όπως ορίστηκε από το δικαστήριο)
      custom_voluntary_service_date: '',
      custom_interim_service_date: '',
      // ΠΕΔΙΟ 4 - Ραντεβού / Εξωδικαστική
      appointment_type: '', // 'appointment' or 'task'
      appointment_date: '',
      appointment_time: '',
      appointment_duration: '60',
      task_date: '',
      task_deadline: '',
      task_has_deadline: '', // 'specific_date' or 'deadline'
      appointment_description: '',
      appointment_fee: '',
      appointment_add_to_financial: false,
      lawsuit_filing_date: '',
      lawsuit_hearing_date: '', // Δικάσιμος (237 ΚΠολΔ)
      lawsuit_service_date: '',
      // Ομάδα Β: λειτουργία υπολογισμού για doc types objection_dp/appeal/objection_default/cassation/objection_933
      // 'deadline_calc' = υπολογισμός προθεσμίας πριν ασκηθεί | 'filed' = προθεσμίες για ήδη ασκηθέν δικόγραφο
      calculation_mode: '',
      // Τριτανακοπή — διαδικασία & κατάθεση
      trit_diadikasia: '',       // 'ordinary' | 'special'
      trit_filing_date: '',
      // Δικάσιμος 3.2 ΣτΕ — καθεστώς ν. 5119/2024
      admin_32_new_regime: '',   // 'yes' | 'no'
      // Ανακοπή 954 — επιταγή προ 1.1.2026
      enf_954_old: '',           // 'yes' | 'no'
      // Ανακοπή 933 — branching 934α/934β
      enf_934_kind: '',          // '934a' | '934b'
      enf_934a: '',              // 'seizure' (45 ημ.) | 'immediate' (30 ημ.)
      enf_934b: '',              // 'last_action' (30 ημ.) | 'auction_movables' (45 ημ.) | 'auction_immovables' (60 ημ.)
      enf_event_date: '',        // ημερομηνία γεγονότος (κατάσχεση, επίδοση επιταγής, τελ. πράξη, πλειστηριασμός, μεταγραφή)
      hearing_date: '',  // Ημερομηνία δικασίμου για ειδική διαδικασία, εκουσία, ασφαλιστικά
      hearing_status: '',  // Κατάσταση δικασίμου (Εκδίκαση, Αναβολή, κλπ)
      is_public_entity: false,
      mediation_required: false,
      opponent_details: '',  // Στοιχεία αντιδίκου
      case_description_type: '',  // Περιγραφή υπόθεσης (από λίστα)
      case_description_custom: ''  // Περιγραφή υπόθεσης (χειρόγραφα)
    },
    entry: {
      title: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      priority: 'medium'
    },
    selectedClientId: '',
    selectedCaseId: ''
  });

  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [userCourts, setUserCourts] = useState([]);
  
  // Ref for auto-scroll functionality
  const formEndRef = useRef(null);
  
  // Auto-scroll: μετά από κάθε επιλογή, το τέλος της φόρμας κεντράρεται ώστε το
  // νέο πεδίο να εμφανίζεται περίπου στο μέσο του ύψους του παραθύρου.
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (formEndRef.current) {
        formEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  useEffect(() => {
    fetchClients(true);  // show toast if API fails
    fetchAllEntries();
    fetchUserCourts();
  }, []);

  // Handle prefillData when document type changes from edit case modal
  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({
        ...prev,
        entryType: 'court_action',
        clientOption: 'existing',
        caseOption: 'new',
        selectedClientId: prefillData.clientId || '',
        case: {
          ...prev.case,
          procedure_type: prefillData.procedure_type || 'civil',
          action_type: 'document',
          court_type: prefillData.court_type || '',
          court_location: prefillData.court_location || '',
          document_type: prefillData.document_type || '',
          opponent_details: prefillData.opponent_details || '',
          case_description_type: prefillData.case_description_type || '',
          case_description_custom: prefillData.case_description_custom || ''
        }
      }));
      // Fetch cases for the selected client
      if (prefillData.clientId) {
        fetchClientCases(prefillData.clientId);
      }
    }
  }, [prefillData]);

  // Auto-scroll when key form sections change
  useEffect(() => {
    if (formData.entryType) {
      scrollToBottom();
    }
  }, [formData.entryType, scrollToBottom]);

  useEffect(() => {
    if (formData.clientOption) {
      scrollToBottom();
    }
  }, [formData.clientOption, scrollToBottom]);

  useEffect(() => {
    if (formData.clientType) {
      scrollToBottom();
    }
  }, [formData.clientType, scrollToBottom]);

  useEffect(() => {
    if (formData.caseOption) {
      scrollToBottom();
    }
  }, [formData.caseOption, scrollToBottom]);

  // Fetch conflicts when postponement date changes
  useEffect(() => {
    const fetchPostponementConflicts = async () => {
      const postponementDate = formData.case.postponement_date || formData.case.postponement_alt_date;
      if (!postponementDate) {
        setPostponementConflicts([]);
        return;
      }
      
      setLoadingConflicts(true);
      try {
        const response = await axios.get(`${API}/entries`);
        const allEntries = response.data;
        
        // Filter entries for the selected date
        const conflicts = allEntries.filter(entry => {
          const entryDate = new Date(entry.date).toISOString().split('T')[0];
          return entryDate === postponementDate;
        });
        
        setPostponementConflicts(conflicts);
      } catch (error) {
        console.error('Error fetching conflicts:', error);
        setPostponementConflicts([]);
      } finally {
        setLoadingConflicts(false);
      }
    };
    
    fetchPostponementConflicts();
  }, [formData.case.postponement_date, formData.case.postponement_alt_date]);

  useEffect(() => {
    if (formData.case.procedure_type) {
      scrollToBottom();
    }
  }, [formData.case.procedure_type, scrollToBottom]);

  useEffect(() => {
    if (formData.case.action_type) {
      scrollToBottom();
    }
  }, [formData.case.action_type, scrollToBottom]);

  useEffect(() => {
    if (formData.case.court_type) {
      scrollToBottom();
    }
    // Auto-set Athens for Athens-only courts (ΣτΕ, Άρειος Πάγος)
    const athensOnly = ['supreme', 'supreme_criminal', 'council_of_state'];
    if (athensOnly.includes(formData.case.court_type) && formData.case.court_location !== 'Αθήνα') {
      updateFormData('case', 'court_location', 'Αθήνα');
    }
    // If switching to a court_type whose available locations no longer include the current selection, clear it
    if (formData.case.court_type && !athensOnly.includes(formData.case.court_type)) {
      const allowed = getLocationsForCourtType(formData.case.court_type);
      if (formData.case.court_location && !allowed.includes(formData.case.court_location)) {
        updateFormData('case', 'court_location', '');
      }
    }
  }, [formData.case.court_type]);

  useEffect(() => {
    if (formData.case.document_type) {
      // On mobile, skip auto-scroll: it jumps past the newly revealed fields
      // (court/dates) that the user still needs to fill, landing at the very
      // end of the form. On desktop, keep the original behavior since the
      // viewport usually shows the whole form anyway.
      const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
      if (!isMobile) {
        scrollToBottom();
      }
    }
  }, [formData.case.document_type, scrollToBottom]);

  // Clear selected deadlines when document type changes
  useEffect(() => {
    setSelectedDeadlines([]);
    setFormData((prev) => (prev.case.client_role ? { ...prev, case: { ...prev.case, client_role: '' } } : prev));
  }, [formData.case.document_type]);

  useEffect(() => {
    if (formData.selectedClientId) {
      scrollToBottom();
    }
  }, [formData.selectedClientId, scrollToBottom]);

  const fetchClients = async (showError = false) => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (showError) {
        toast.error(error?.response?.data?.detail || 'Αδυναμία φόρτωσης πελατών. Δοκίμασε επανασύνδεση.');
      }
    }
  };

  const fetchAllEntries = async () => {
    try {
      const response = await axios.get(`${API}/entries`);
      setAllEntries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const fetchUserCourts = async () => {
    try {
      const response = await axios.get(`${API}/settings/courts`);
      setUserCourts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching user courts:', error);
    }
  };

  const fetchClientCases = async (clientId) => {
    try {
      const response = await axios.get(`${API}/cases/client/${clientId}`);
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const setField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format YYYY-MM-DD to DD/MM/YYYY for notes
  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    return `${parseInt(parts[2])}/${parseInt(parts[1])}/${parts[0]}`;
  };

  const courtLocations = [
    'Αγρίνιο', 'Αθήνα', 'Αλεξανδρούπολη', 'Άμφισσα', 'Άργος', 'Άρτα', 'Βέροια', 'Βόλος',
    'Γρεβενά', 'Δράμα', 'Έδεσσα', 'Ζάκυνθος', 'Ηγουμενίτσα', 'Ηράκλειο', 'Θήβα', 'Θεσσαλονίκη',
    'Ιωάννινα', 'Καβάλα', 'Καλαμάτα', 'Καλάμπακα', 'Καρδίτσα', 'Καστοριά', 'Κατερίνη', 'Κέρκυρα',
    'Κιλκίς', 'Κοζάνη', 'Κομοτηνή', 'Κόρινθος', 'Κως', 'Λαμία', 'Λάρισα', 'Λασίθι', 'Λευκάδα',
    'Λιβαδειά', 'Μεσολόγγι', 'Μυτιλήνη', 'Ναύπλιο', 'Ξάνθη', 'Πάτρα', 'Πειραιάς', 'Πολύγυρος',
    'Πρέβεζα', 'Πύργος', 'Ρέθυμνο', 'Ρόδος', 'Σέρρες', 'Σύρος', 'Σπάρτη', 'Τρίκαλα', 'Τρίπολη',
    'Φλώρινα', 'Χαλκίδα', 'Χανιά', 'Χίος'
  ];

  // Έδρες πολιτικών/ποινικών Εφετείων
  const civilAppealLocations = [
    'Αθήνα', 'Θεσσαλονίκη', 'Πειραιάς', 'Πάτρα', 'Ναύπλιο', 'Λάρισα',
    'Ιωάννινα', 'Κέρκυρα', 'Λαμία', 'Καλαμάτα', 'Κομοτηνή', 'Χανιά',
    'Ρόδος', 'Σύρος', 'Μυτιλήνη', 'Χαλκίδα'
  ];

  // Έδρες Διοικητικών Εφετείων
  const adminAppealLocations = [
    'Αθήνα', 'Πειραιάς', 'Θεσσαλονίκη', 'Πάτρα', 'Ιωάννινα',
    'Λάρισα', 'Κομοτηνή', 'Τρίπολη', 'Χανιά'
  ];

  // Έδρες Διοικητικών Πρωτοδικείων
  const adminFirstLocations = [
    'Αθήνα', 'Πειραιάς', 'Θεσσαλονίκη', 'Πάτρα', 'Ιωάννινα', 'Λάρισα',
    'Βόλος', 'Καβάλα', 'Σέρρες', 'Βέροια', 'Κοζάνη', 'Κομοτηνή',
    'Αλεξανδρούπολη', 'Τρίπολη', 'Καλαμάτα', 'Κέρκυρα', 'Ηράκλειο',
    'Χανιά', 'Ρόδος', 'Σύρος', 'Μυτιλήνη', 'Χαλκίδα', 'Λιβαδειά',
    'Λαμία', 'Μεσολόγγι', 'Άρτα', 'Πύργος', 'Ναύπλιο', 'Πολύγυρος',
    'Κατερίνη'
  ];

  // Επιστρέφει τις διαθέσιμες έδρες ανά είδος δικαστηρίου
  const getLocationsForCourtType = (courtType) => {
    // Εφετεία πολιτικά/ποινικά
    if (['single_appeal', 'triple_appeal',
         'single_criminal_appeal', 'triple_criminal_appeal', 'five_criminal_appeal',
         'mixed_jury'].includes(courtType)) {
      return civilAppealLocations;
    }
    // Διοικητικό Εφετείο
    if (courtType === 'admin_appeal') {
      return adminAppealLocations;
    }
    // Διοικητικό Πρωτοδικείο
    if (courtType === 'single_admin_first' || courtType === 'triple_admin_first') {
      return adminFirstLocations;
    }
    // Όλες οι έδρες (default)
    return courtLocations;
  };

  // Get user's favorite locations for a category (procedure type)
  const getUserFavoriteLocations = (procedureCategory) => {
    const categoryMap = {
      'civil': 'civil',
      'criminal': 'criminal',
      'administrative': 'administrative'
    };
    const category = categoryMap[procedureCategory] || procedureCategory;
    return userCourts
      .filter(c => c.procedure_category === category)
      .map(c => c.location);
  };

  // Sort locations with favorites first (filtered by court_type if provided)
  const getSortedLocations = (procedureCategory, courtType = null) => {
    const baseList = courtType ? getLocationsForCourtType(courtType) : courtLocations;
    const baseSet = new Set(baseList);
    const favorites = getUserFavoriteLocations(procedureCategory).filter(loc => baseSet.has(loc));
    const favoriteSet = new Set(favorites);

    // Get unique favorites (preserve order) + rest of allowed locations
    const sortedFavorites = [...new Set(favorites)];
    const otherLocations = baseList.filter(loc => !favoriteSet.has(loc));

    return [...sortedFavorites, ...otherLocations];
  };

  // Λίστα περιγραφών υπόθεσης για αστικές διαφορές
  const caseDescriptionTypes = [
    { value: 'debt', label: 'Αγωγή χρέους' },
    { value: 'property', label: 'Εμπράγματη αγωγή' },
    { value: 'fraud', label: 'Αγωγή καταδολίευσης' },
    { value: 'inheritance', label: 'Κληρονομική διαφορά' },
    { value: 'divorce', label: 'Διαζύγιο' },
    { value: 'alimony', label: 'Διατροφή' },
    { value: 'custody', label: 'Επιμέλεια τέκνων' },
    { value: 'lease', label: 'Μισθωτική διαφορά' },
    { value: 'labor', label: 'Εργατική διαφορά' },
    { value: 'tort', label: 'Αδικοπραξία' },
    { value: 'contract', label: 'Συμβατική διαφορά' },
    { value: 'unjust_enrichment', label: 'Αδικαιολόγητος πλουτισμός' },
    { value: 'compensation', label: 'Αποζημίωση' },
    { value: 'ownership', label: 'Αναγνώριση κυριότητας' },
    { value: 'possession', label: 'Προστασία νομής' },
    { value: 'easement', label: 'Δουλεία' },
    { value: 'condominium', label: 'Διαφορά οροφοκτησίας' },
    { value: 'insurance', label: 'Ασφαλιστική διαφορά' },
    { value: 'medical', label: 'Ιατρική αμέλεια' },
    { value: 'traffic', label: 'Τροχαίο ατύχημα' },
    { value: 'other', label: 'Άλλο (χειρόγραφη καταχώρηση)' }
  ];

  const getDocumentTypes = (courtType) => {
    const common = [
      { value: 'lawsuit', label: 'Αγωγή, ανταγωγή, παρέμβαση (τακτική διαδικασία)' },
      { value: 'special_lawsuit', label: 'Αγωγή, ανταγωγή, παρέμβαση (ειδική διαδικασία)' },
      { value: 'objection_dp', label: 'Ανακοπή κατά ΔΠ' },
      { value: 'voluntary_app', label: 'Αίτηση εκουσίας' },
      { value: 'interim_app', label: 'Αίτηση ασφαλιστικών' },
      { value: 'objection_933', label: 'Ανακοπή άρθρου 933 ΚΠολΔ' },
      { value: 'appeal', label: 'Έφεση' },
      { value: 'objection_default', label: 'Ανακοπή ερημοδικίας' },
      { value: 'cassation', label: 'Αίτηση αναίρεσης' },
      { value: 'revision', label: 'Αναψηλάφηση' },
      { value: 'third_party_obj', label: 'Τριτανακοπή' }
    ];

    if (courtType === 'single_first') {
      return [
        ...common,
        { value: 'small_claims', label: 'Αγωγή, ανταγωγή, παρέμβαση (διαδικασία μικροδιαφορών)' },
        { value: 'rent_objection', label: 'Ανακοπή κατά διαταγής απόδοσης μισθίου' }
      ];
    }

    if (courtType === 'multi_first') {
      return [
        ...common,
        { value: 'appeal_peace', label: 'Έφεση κατά απόφασης Ειρηνοδικείου' },
        { value: 'objection_954', label: 'Ανακοπή άρθρου 954 ΚΠολΔ' }
      ];
    }

    if (courtType === 'single_appeal') {
      return common;
    }

    if (courtType === 'triple_appeal') {
      return [
        { value: 'appeal_single_first', label: 'Έφεση κατά απόφασης Μονομελούς Πρωτοδικείου' },
        { value: 'interim_app', label: 'Αίτηση ασφαλιστικών' },
        { value: 'objection_default', label: 'Ανακοπή ερημοδικίας' },
        { value: 'revision', label: 'Αναψηλάφηση' },
        { value: 'third_party_obj', label: 'Τριτανακοπή' }
      ];
    }

    if (courtType === 'supreme') {
      return [
        { value: 'cassation', label: 'Αίτηση αναίρεσης' },
        { value: 'objection_default', label: 'Ανακοπή ερημοδικίας' },
        { value: 'revision', label: 'Αναψηλάφηση' },
        { value: 'third_party_obj', label: 'Τριτανακοπή' }
      ];
    }

    return common;
  };

  // Έλεγχος αν ημερομηνία είναι αργία
  const checkIfHoliday = async (dateStr) => {
    if (!dateStr) return false;
    
    try {
      const response = await axios.get(`${API}/holidays/check?date=${dateStr}`);
      return response.data.is_holiday;
    } catch (error) {
      console.error('Error checking holiday:', error);
      return false;
    }
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Έλεγχος για αργίες σε κρίσιμες ημερομηνίες
      const filingDate = formData.case.lawsuit_filing_date;
      const hearingDate = formData.case.hearing_date || formData.case.preliminary_date;
      
      if (filingDate) {
        const isHoliday = await checkIfHoliday(filingDate);
        if (isHoliday) {
          toast.error('Η καταχώρηση είναι εσφαλμένη', {
            description: 'Η ημερομηνία κατάθεσης δεν μπορεί να είναι αργία.'
          });
          return;
        }
      }
      
      if (hearingDate) {
        // Έλεγχος αν είναι παλαιότερη από σήμερα
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(hearingDate);
        
        if (selectedDate < today) {
          toast.error('Η καταχώρηση είναι εσφαλμένη', {
            description: 'Η ημερομηνία δικασίμου δεν μπορεί να είναι προγενέστερη της σημερινής.'
          });
          return;
        }
        
        // Έλεγχος αν είναι αργία
        const isHoliday = await checkIfHoliday(hearingDate);
        if (isHoliday) {
          toast.error('Η καταχώρηση είναι εσφαλμένη', {
            description: 'Η ημερομηνία δικασίμου δεν μπορεί να είναι αργία.'
          });
          return;
        }
      }
      
      // If replacing old entries (document type changed), delete old entries and case first
      if (prefillData?.replaceOldEntries) {
        // Delete old entries
        if (prefillData.oldEntryIds && prefillData.oldEntryIds.length > 0) {
          const deletePromises = prefillData.oldEntryIds.map(entryId => 
            axios.delete(`${API}/entries/${entryId}`)
          );
          await Promise.all(deletePromises);
        }
        // Delete old case
        if (prefillData.oldCaseId) {
          await axios.delete(`${API}/cases/${prefillData.oldCaseId}`);
        }
      }
      
      let clientId = formData.selectedClientId;
      let caseId = formData.selectedCaseId;

      // Create new client if needed
      if (formData.clientOption === 'new') {
        const clientData = {
          client_type: formData.clientType,
          ...formData.client
        };
        const clientResponse = await axios.post(`${API}/clients`, clientData);
        clientId = clientResponse.data.id;
      }

      // Create new case if needed
      if (formData.entryType === 'court_action' && formData.caseOption === 'new') {
        const caseData = {
          client_id: clientId,
          ...formData.case
        };
        const caseResponse = await axios.post(`${API}/cases`, caseData);
        caseId = caseResponse.data.id;
      }

      // Generate automatic title based on entry type
      let autoTitle = 'Νέα εγγραφή';
      if (formData.entryType === 'court_action') {
        if (formData.case.procedure_type === 'civil') autoTitle = 'Αστική υπόθεση';
        if (formData.case.procedure_type === 'criminal') autoTitle = 'Ποινική υπόθεση';
        if (formData.case.procedure_type === 'administrative') autoTitle = 'Διοικητική διαφορά';
      } else {
        autoTitle = 'Άλλη απασχόληση';
      }

      // Αγωγή 237 (Τακτική Διαδικασία)
      if (formData.case.lawsuit_filing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'lawsuit') {
        // Get selected deadlines from checkboxes
        const selectedDeadlines = [];
        
        if (formData.case.is_real_property === 'yes' && document.querySelector('[data-testid="deadline-registry-checkbox"]')?.checked) {
          selectedDeadlines.push('eggrafi_ktimatologio');
        }
        if (document.querySelector('[data-testid="deadline-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_agogis');
        }
        if (document.querySelector('[data-testid="deadline-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon');
        }
        if (document.querySelector('[data-testid="deadline-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_antikrousi');
        }
        if (document.querySelector('[data-testid="deadline-counterclaim-checkbox"]')?.checked) {
          selectedDeadlines.push('antagogi_paremvasi');
        }
        if (document.querySelector('[data-testid="deadline-intervention-checkbox"]')?.checked) {
          selectedDeadlines.push('paremvasi_meta_anakoinosi');
        }

        if (selectedDeadlines.length > 0) {
          // Call API to calculate deadlines
          const deadlineRequest = {
            template_id: 'agogi_237',
            base_date: formData.case.lawsuit_filing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              foreign_resident: formData.case.has_foreign_defendant === 'yes',
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true,
              is_real_property: formData.case.is_real_property === 'yes'
            }
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          // Create one entry for each calculated deadline
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Από ${fmtDate(formData.case.lawsuit_filing_date)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // ΣΗΜΑΝΤΙΚΟ: ΔΕΝ δημιουργούμε "γενική" εγγραφή για δικαστικές πράξεις
        // Μόνο οι συγκεκριμένες προθεσμίες που επιλέγει ο χρήστης

        // Δικάσιμος (αν συμπληρώθηκε)
        if (formData.case.lawsuit_hearing_date) {
          await axios.post(`${API}/entries`, {
            entry_type: 'hearing',
            client_id: clientId,
            case_id: caseId || '',
            title: 'Δικάσιμος',
            date: formData.case.lawsuit_hearing_date,
            priority: 'high',
            notes: 'Άρθρο 237 ΚΠολΔ',
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          });
        }
      }
      // Μικροδιαφορές - Αγωγή
      else if (formData.case.lawsuit_filing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'small_claims') {
        const selectedDeadlines = [];
        if (document.querySelector('[data-testid="deadline-smallclaims-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_mikrodif');
        }
        if (document.querySelector('[data-testid="deadline-smallclaims-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('protaseis_mikrodif');
        }
        if (document.querySelector('[data-testid="deadline-smallclaims-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_mikrodif');
        }
        if (document.querySelector('[data-testid="deadline-smallclaims-counterclaim-checkbox"]')?.checked) {
          selectedDeadlines.push('antagogi_paremvasi_mikrodif');
        }
        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'mikrodifores_katathesi',
            base_date: formData.case.lawsuit_filing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          };
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: deadline.name,
              date: deadline.deadline_date,
              priority: 'high',
              notes: `Κατάθεση αγωγής μικροδιαφορών: ${fmtDate(formData.case.lawsuit_filing_date)} - ${deadline.article}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // Optional hearing entry
        if (formData.case.lawsuit_hearing_date) {
          await axios.post(`${API}/entries`, {
            entry_type: 'hearing',
            client_id: clientId,
            case_id: caseId || '',
            title: 'Δικάσιμος Μικροδιαφορών',
            date: formData.case.lawsuit_hearing_date,
            priority: 'high',
            notes: 'Άρθρο 468 ΚΠολΔ',
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          });
        }
      }
      // Ειδική Διαδικασία - Δικάσιμος
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'special_lawsuit') {
        // Δημιουργία εγγραφής Δικασίμου
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        const opponentDetails = formData.case.opponent_details || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (hearingNotes) hearingNotes += ' | Ειδική Διαδικασία';
        if (opponentDetails) hearingNotes += ` | Αντίδικος: ${opponentDetails}`;

        const hearingEntryData = {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
        const hearingId = hearingResponse.data.id;

        const selectedDeadlines = [];
        
        if (document.querySelector('[data-testid="deadline-special-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_agogis_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-special-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-special-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-special-counterclaim-checkbox"]')?.checked) {
          selectedDeadlines.push('antagogi_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-special-intervention-checkbox"]')?.checked) {
          selectedDeadlines.push('paremvasi_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-special-intervention-after-checkbox"]')?.checked) {
          selectedDeadlines.push('paremvasi_meta_prosepiklisi_eidiki');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'dikasimos_eidiki',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // Αναψηλάφηση - Δικάσιμος
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'revision') {
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (hearingNotes) hearingNotes += ' | Αναψηλάφηση';
        else hearingNotes = 'Αναψηλάφηση';
        if (formData.case.opponent_details) hearingNotes += ` | Αντίδικος: ${formData.case.opponent_details}`;

        const hearingResponse = await axios.post(`${API}/entries`, {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        });
        const hearingId = hearingResponse.data.id;

        const selectedDeadlines = [];
        if (document.querySelector('[data-testid="deadline-revision-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_anapsylafisis');
        }
        if (document.querySelector('[data-testid="deadline-revision-service-foreign-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_anapsylafisis_foreign');
        }
        if (document.querySelector('[data-testid="deadline-revision-additional-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthetoi_logoi_anapsyl');
        }
        if (document.querySelector('[data-testid="deadline-revision-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('protaseis_anapsyl');
        }
        if (document.querySelector('[data-testid="deadline-revision-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_anapsyl');
        }

        if (selectedDeadlines.length > 0) {
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, {
            template_id: 'dikasimos_anapsylafisi',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          });
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => ({
            entry_type: 'deadline',
            client_id: clientId,
            case_id: caseId || '',
            title: capitalizeFirst(deadline.name),
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
            parent_hearing_id: hearingId,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          }));
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Εκουσία Δικαιοδοσία - Δικάσιμος
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'voluntary_app') {
        // Δημιουργία εγγραφής Δικασίμου
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (hearingNotes) hearingNotes += ' | Εκουσία Δικαιοδοσία';

        const hearingEntryData = {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
        const hearingId = hearingResponse.data.id;

        const selectedDeadlines = [];
        
        // Φάση Β: αν συμπληρώθηκαν ημέρες, υπολόγισε date = hearing_date - days και δημιούργησε deadline
        if (formData.case.custom_voluntary_service_days && formData.case.hearing_date) {
          const days = parseInt(formData.case.custom_voluntary_service_days, 10);
          if (!Number.isNaN(days) && days >= 0) {
            const h = new Date(formData.case.hearing_date);
            h.setDate(h.getDate() - days);
            const serviceDateISO = h.toISOString().split('T')[0];
            await axios.post(`${API}/entries`, {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: 'Επίδοση αίτησης',
              date: serviceDateISO,
              priority: 'high',
              notes: `${days} ημέρες πριν τη δικάσιμο (όπως ορίστηκε από το δικαστήριο - Δικάσιμος: ${fmtDate(formData.case.hearing_date)})`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            });
          }
        }
        if (document.querySelector('[data-testid="deadline-voluntary-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon_ekousia');
        }
        if (document.querySelector('[data-testid="deadline-voluntary-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_ekousia');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'dikasimos_ekousia',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // Ασφαλιστικά Μέτρα - Δικάσιμος
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'interim_app') {
        // Δημιουργία εγγραφής Δικασίμου
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        const opponentDetails = formData.case.opponent_details || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (hearingNotes) hearingNotes += ' | Ασφαλιστικά Μέτρα';
        if (opponentDetails) hearingNotes += ` | Αντίδικος: ${opponentDetails}`;

        const hearingEntryData = {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
        const hearingId = hearingResponse.data.id;

        const selectedDeadlines = [];
        
        // Φάση Β': custom days για Επίδοση ασφαλιστικών (όπως ορίστηκε)
        if (formData.case.custom_interim_service_days && formData.case.hearing_date) {
          const days = parseInt(formData.case.custom_interim_service_days, 10);
          if (!Number.isNaN(days) && days >= 0) {
            const h = new Date(formData.case.hearing_date);
            h.setDate(h.getDate() - days);
            const serviceDateISO = h.toISOString().split('T')[0];
            await axios.post(`${API}/entries`, {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: 'Επίδοση αίτησης Ασφαλιστικών Μέτρων',
              date: serviceDateISO,
              priority: 'high',
              notes: `${days} ημέρες πριν τη δικάσιμο (όπως ορίστηκε από το δικαστήριο - Δικάσιμος: ${fmtDate(formData.case.hearing_date)})`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            });
          }
        }
        if (document.querySelector('[data-testid="deadline-interim-memo-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_simeiomatos');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'dikasimos_asfalistika',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // Έφεση - Δικάσιμος
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && 
               (formData.case.document_type === 'appeal_single_first' || formData.case.document_type === 'appeal_peace')) {
        // Δημιουργία εγγραφής Δικασίμου
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const docTypeNames = { 'appeal_single_first': 'Έφεση κατά απόφασης Μονομελούς Πρωτοδικείου', 'appeal_peace': 'Έφεση κατά απόφασης Ειρηνοδικείου' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        const docTypeName = docTypeNames[formData.case.document_type] || '';
        const opponentDetails = formData.case.opponent_details || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (docTypeName) hearingNotes += ` | ${docTypeName}`;
        if (opponentDetails) hearingNotes += ` | Αντίδικος: ${opponentDetails}`;

        const hearingEntryData = {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
        const hearingId = hearingResponse.data.id;

        const selectedDeadlines = [];
        
        if (document.querySelector('[data-testid="deadline-appeal-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_efesis');
        }
        if (document.querySelector('[data-testid="deadline-appeal-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon_efesi');
        }
        if (document.querySelector('[data-testid="deadline-appeal-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_efesi');
        }
        if (document.querySelector('[data-testid="deadline-appeal-objection-checkbox"]')?.checked) {
          selectedDeadlines.push('antirrisi_efesis');
        }
        if (document.querySelector('[data-testid="deadline-appeal-prosthetoi-logoi-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthetoi_logoi_efesis');
        }
        if (document.querySelector('[data-testid="deadline-appeal-antefesi-checkbox"]')?.checked) {
          selectedDeadlines.push('antefesi');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'dikasimos_efesi',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
              parent_hearing_id: hearingId,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // Αναίρεση - Από επίδοση Απόφασης
      else if (formData.case.lawsuit_service_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'cassation') {
        const selectedDeadlines = [];
        
        if (document.querySelector('[data-testid="deadline-cassation-filing-checkbox"]')?.checked) {
          if (formData.case.has_foreign_defendant === 'yes') {
            selectedDeadlines.push('anairesi_filing_foreign');
          } else {
            selectedDeadlines.push('anairesi_filing');
          }
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'anairesi',
            base_date: formData.case.lawsuit_service_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              foreign_resident: formData.case.has_foreign_defendant === 'yes',
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Επίδοση απόφασης: ${fmtDate(formData.case.lawsuit_service_date)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // ΔΕΝ δημιουργούμε "γενική" εγγραφή - μόνο επιλεγμένες προθεσμίες
      }
      // Ανακοπή κατά Διαταγής Πληρωμής
      else if (formData.case.lawsuit_service_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'objection_dp') {
        const selectedDeadlines = [];
        
        if (document.querySelector('[data-testid="deadline-dp-filing-checkbox"]')?.checked) {
          selectedDeadlines.push('anakopi_dp_filing');
        }
        if (document.querySelector('[data-testid="deadline-dp-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_anakopis');
        }
        if (formData.case.hearing_date && document.querySelector('[data-testid="deadline-dp-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('protaseis_anakopis');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'anakopi_dp',
            base_date: formData.case.lawsuit_service_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              foreign_resident: formData.case.has_foreign_defendant === 'yes',
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            // Για προτάσεις χρησιμοποιούμε την ημερομηνία δικασίμου
            const baseDate = deadline.id === 'protaseis_anakopis' && formData.case.hearing_date 
              ? formData.case.hearing_date 
              : formData.case.lawsuit_service_date;
            
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Επίδοση ΔΠ: ${fmtDate(formData.case.lawsuit_service_date)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // ΔΕΝ δημιουργούμε "γενική" εγγραφή - μόνο επιλεγμένες προθεσμίες
      }
      // Ανακοπή κατά Διαταγής Απόδοσης Μισθίου — υπολογισμός προθεσμίας
      else if (formData.case.lawsuit_service_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'rent_objection' && formData.case.calculation_mode === 'deadline_calc') {
        if (document.querySelector('[data-testid="deadline-dapm-filing-checkbox"]')?.checked !== false) {
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, {
            template_id: 'astiki_epidosi_dapm',
            base_date: formData.case.lawsuit_service_date,
            selected_deadline_ids: ['anakopi_dapm'],
            options: {}
          });
          const deadline = deadlinesResponse.data.deadlines[0];
          await axios.post(`${API}/entries`, {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Προθεσμία ανακοπής κατά Διαταγής Απόδοσης Μισθίου',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Επίδοση ΔΑΠΜ: ${fmtDate(formData.case.lawsuit_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          });
        }
      }
      // Ανακοπή άρθρου 954 ΚΠολΔ — υπολογισμός προθεσμίας (από πλειστηριασμό)
      else if (formData.case.enf_event_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'objection_954' && formData.case.calculation_mode === 'deadline_calc') {
        if (!formData.case.enf_954_old) {
          toast.error('Διευκρινίστε πότε επιδόθηκε η επιταγή προς εκτέλεση');
          return;
        }
        if (document.querySelector('[data-testid="deadline-954-filing-checkbox"]')?.checked !== false) {
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, {
            template_id: 'astiki_anakopi_954',
            base_date: formData.case.enf_event_date,
            selected_deadline_ids: ['anakopi_954'],
            options: formData.case.enf_954_old === 'yes' ? { pre_2026_epitagi: true } : {}
          });
          const deadline = deadlinesResponse.data.deadlines[0];
          await axios.post(`${API}/entries`, {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Προθεσμία κατάθεσης ανακοπής (954 ΚΠολΔ)',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Πλειστηριασμός: ${fmtDate(formData.case.enf_event_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          });
        }
      }
      // Τριτανακοπή — τακτική διαδικασία (υπολογισμός από κατάθεση, ν. 5221/2025)
      else if (formData.case.trit_filing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'third_party_obj' && formData.case.trit_diadikasia === 'ordinary') {
        const tritTitles = {
          epidosi_agogis: 'Επίδοση τριτανακοπής',
          katathesi_protaseon: 'Κατάθεση προτάσεων',
          prosthiki_antikrousi: 'Προσθήκη - Αντίκρουση'
        };
        const selectedDeadlines = [];
        if (document.querySelector('[data-testid="deadline-trit-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_agogis');
        }
        if (document.querySelector('[data-testid="deadline-trit-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon');
        }
        if (document.querySelector('[data-testid="deadline-trit-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_antikrousi');
        }
        if (selectedDeadlines.length > 0) {
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, {
            template_id: 'agogi_237',
            base_date: formData.case.trit_filing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              foreign_resident: formData.case.has_foreign_defendant === 'yes',
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          });
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => ({
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: tritTitles[deadline.id] || capitalizeFirst(deadline.name),
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} (Τριτανακοπή) - Κατάθεση: ${fmtDate(formData.case.trit_filing_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          }));
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Τριτανακοπή — ειδική διαδικασία (υπολογισμός από δικάσιμο)
      else if (formData.case.hearing_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'third_party_obj' && formData.case.trit_diadikasia === 'special') {
        const courtNameMap = { 'single_first': 'Μονομελές Πρωτοδικείο', 'multi_first': 'Πολυμελές Πρωτοδικείο', 'single_appeal': 'Μονομελές Εφετείο', 'triple_appeal': 'Τριμελές Εφετείο', 'supreme': 'Άρειος Πάγος' };
        const courtName = courtNameMap[formData.case.court_type] || '';
        const courtLocation = formData.case.court_location || '';
        let hearingNotes = `${courtName} ${courtLocation}`.trim();
        if (hearingNotes) hearingNotes += ' | Τριτανακοπή';
        else hearingNotes = 'Τριτανακοπή';
        if (formData.case.opponent_details) hearingNotes += ` | Αντίδικος: ${formData.case.opponent_details}`;

        const hearingResponse = await axios.post(`${API}/entries`, {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        });
        const hearingId = hearingResponse.data.id;

        const tritSpecialTitles = {
          epidosi_agogis_eidiki: 'Επίδοση τριτανακοπής',
          katathesi_protaseon_eidiki: 'Κατάθεση προτάσεων',
          prosthiki_eidiki: 'Προσθήκη - Αντίκρουση'
        };
        const selectedDeadlines = [];
        if (document.querySelector('[data-testid="deadline-trit-special-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_agogis_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-trit-special-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('katathesi_protaseon_eidiki');
        }
        if (document.querySelector('[data-testid="deadline-trit-special-addition-checkbox"]')?.checked) {
          selectedDeadlines.push('prosthiki_eidiki');
        }
        if (selectedDeadlines.length > 0) {
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, {
            template_id: 'dikasimos_eidiki',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlines,
            options: {}
          });
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => ({
            entry_type: 'deadline',
            client_id: clientId,
            case_id: caseId || '',
            title: tritSpecialTitles[deadline.id] || capitalizeFirst(deadline.name),
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} (Τριτανακοπή) - Δικάσιμος: ${fmtDate(formData.case.hearing_date)}`,
            parent_hearing_id: hearingId,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          }));
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Ανακοπή άρθρου 933 ΚΠολΔ — branching 934α/934β
      else if (formData.case.enf_event_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'objection_933' && formData.case.calculation_mode === 'deadline_calc') {
        // Map subcase → backend template_id / deadline_id / human label
        let template_id = '', deadline_id = '', subcaseLabel = '';
        if (formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'seizure') {
          template_id = 'astiki_anakopi_933_katasxesi'; deadline_id = 'anakopi_933_katasxesi'; subcaseLabel = 'κατάσχεση';
        } else if (formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'immediate') {
          template_id = 'astiki_anakopi_933_amesi'; deadline_id = 'anakopi_933_amesi'; subcaseLabel = 'άμεση εκτέλεση';
        } else if (formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'last_action') {
          template_id = 'astiki_anakopi_933_teleytaia'; deadline_id = 'anakopi_933_teleytaia'; subcaseLabel = 'τελευταία πράξη';
        } else if (formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_movables') {
          template_id = 'astiki_anakopi_933_plist_kiniton'; deadline_id = 'anakopi_933_plist_kin'; subcaseLabel = 'πλειστηριασμός κινητών';
        } else if (formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_immovables') {
          template_id = 'astiki_anakopi_933_plist_akiniton'; deadline_id = 'anakopi_933_plist_akin'; subcaseLabel = 'πλειστηριασμός ακινήτων';
        }

        if (template_id && document.querySelector('[data-testid="deadline-933-filing-checkbox"]')?.checked !== false) {
          const deadlineRequest = {
            template_id,
            base_date: formData.case.enf_event_date,
            selected_deadline_ids: [deadline_id],
            options: {
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);

          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} (${subcaseLabel}) - Ημ/νία γεγονότος: ${fmtDate(formData.case.enf_event_date)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // Ομάδα Β: calculation_mode === 'filed' — έχει ασκηθεί δικόγραφο, υπολογίζουμε προθεσμίες ΑΠΟ τη δικάσιμο (calendar days back / working days forward)
      else if (formData.case.hearing_date && formData.case.calculation_mode === 'filed' && ['appeal', 'cassation', 'objection_default', 'objection_933', 'objection_dp', 'rent_objection', 'objection_954'].includes(formData.case.document_type)) {
        // Ορίζουμε ποια checkbox testids αντιστοιχούν σε ποιο doc_type + offset/direction/article
        const filedSpecs = {
          appeal: [
            { testid: 'filed-appeal-service-checkbox',    title: 'Επίδοση έφεσης',          duration: 30, type: 'calendar_days', direction: 'before', article: '498 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-appeal-additional-checkbox', title: 'Πρόσθετοι λόγοι Έφεσης',  duration: 30, type: 'calendar_days', direction: 'before', article: '520 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-appeal-counter-checkbox',    title: 'Αντέφεση',                duration: 30, type: 'calendar_days', direction: 'before', article: '523 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-appeal-proposals-checkbox',  title: 'Κατάθεση προτάσεων',      duration: 0,  type: 'calendar_days', direction: 'on',     article: 'Δικάσιμος' },
            { testid: 'filed-appeal-addition-checkbox',   title: 'Προσθήκη - Αντίκρουση',   duration: 3,  type: 'working_days',  direction: 'after',  article: '524 παρ. 2 ΚΠολΔ (ώρα 12:00)' },
          ],
          cassation: [
            { testid: 'filed-cassation-service-checkbox',    title: 'Επίδοση αίτησης Αναίρεσης', duration: 60, type: 'calendar_days', direction: 'before', article: '568 παρ. 4 ΚΠολΔ' },
            { testid: 'filed-cassation-additional-checkbox', title: 'Πρόσθετοι λόγοι Αναίρεσης', duration: 30, type: 'calendar_days', direction: 'before', article: '569 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-cassation-proposals-checkbox',  title: 'Κατάθεση προτάσεων',        duration: 20, type: 'calendar_days', direction: 'before', article: '570 παρ. 1 ΚΠολΔ' },
            { testid: 'filed-cassation-memo-checkbox',       title: 'Κατάθεση υπομνήματος',      duration: 3,  type: 'working_days',  direction: 'after',  article: '570 παρ. 1 ΚΠολΔ (ώρα 12:00)' },
          ],
          objection_default: [
            { testid: 'filed-erimodik-service-checkbox',    title: 'Επίδοση ανακοπής Ερημοδικίας', duration: 30, type: 'calendar_days', direction: 'before', article: '498 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-erimodik-additional-checkbox', title: 'Πρόσθετοι λόγοι',              duration: 30, type: 'calendar_days', direction: 'before', article: '520 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-erimodik-proposals-checkbox',  title: 'Κατάθεση προτάσεων',           duration: 0,  type: 'calendar_days', direction: 'on',     article: 'Δικάσιμος' },
            { testid: 'filed-erimodik-addition-checkbox',   title: 'Προσθήκη - Αντίκρουση',        duration: 3,  type: 'working_days',  direction: 'after',  article: '524 παρ. 2 ΚΠολΔ (ώρα 12:00)' },
          ],
          objection_933: [
            { testid: 'filed-933-service-checkbox',    title: 'Επίδοση ανακοπής 933',  duration: 20, type: 'calendar_days', direction: 'before', article: '933 παρ. 2 ΚΠολΔ' },
            { testid: 'filed-933-additional-checkbox', title: 'Πρόσθετοι λόγοι',       duration: 8,  type: 'calendar_days', direction: 'before', article: '933 παρ. 1 ΚΠολΔ' },
            { testid: 'filed-933-proposals-checkbox',  title: 'Κατάθεση προτάσεων',    duration: 0,  type: 'calendar_days', direction: 'on',     article: 'Έως τη συζήτηση (933 ΚΠολΔ)' },
            { testid: 'filed-933-addition-checkbox',   title: 'Προσθήκη - Αντίκρουση', duration: 5,  type: 'calendar_days', direction: 'after',  article: '933 ΚΠολΔ (ώρα 12:00)' },
          ],
          objection_dp: [],  // Ειδική διαδικασία — μόνο εγγραφή δικασίμου, χωρίς extra προθεσμίες
          rent_objection: [],  // Ειδική διαδικασία (περιουσιακές) — μόνο εγγραφή δικασίμου
          objection_954: [],  // Ασφαλιστικά (686 επ.) — μόνο εγγραφή δικασίμου
        };

        let specs = filedSpecs[formData.case.document_type] || [];
        // Έφεση κατατεθείσα από 1.1.2026 → νέο καθεστώς ν. 5221/2025 (υπολογισμός από κατάθεση)
        const appealNewRegime = formData.case.document_type === 'appeal' && formData.case.filed_appeal_old_regime !== 'yes';
        if (appealNewRegime) {
          specs = [
            { testid: 'filed-appeal-service-checkbox',    title: 'Επίδοση έφεσης',          duration: 30, type: 'calendar_days', direction: 'from', base: 'filing', article: '495 παρ. 2 ΚΠολΔ (ν. 5221/2025)' },
            { testid: 'filed-appeal-additional-checkbox', title: 'Πρόσθετοι λόγοι Έφεσης',  duration: 70, type: 'calendar_days', direction: 'from', base: 'filing', article: '520 παρ. 2 ΚΠολΔ (ν. 5221/2025)' },
            { testid: 'filed-appeal-counter-checkbox',    title: 'Αντέφεση',                duration: 70, type: 'calendar_days', direction: 'from', base: 'filing', article: '523 ΚΠολΔ (ν. 5221/2025)' },
            { testid: 'filed-appeal-proposals-checkbox',  title: 'Κατάθεση προτάσεων',      duration: 0,  type: 'calendar_days', direction: 'on',     article: 'Δικάσιμος' },
            { testid: 'filed-appeal-addition-checkbox',   title: 'Προσθήκη - Αντίκρουση',   duration: 5,  type: 'working_days',  direction: 'after',  article: '524 ΚΠολΔ ν. 5221/2025 (ώρα 12:00)' },
          ];
        }
        const hearingDate = formData.case.hearing_date;

        // Helper: compute deadline date
        const computeDate = async (spec) => {
          if (spec.direction === 'on') return hearingDate;
          const startDate = spec.base === 'filing' ? formData.case.efesi_filing_date : hearingDate;
          const apiDirection = spec.direction === 'before' ? 'before' : 'from'; // 'after' uses 'from'
          const resp = await axios.post(`${API}/calculate-deadline`, {
            start_date: startDate,
            duration: spec.duration,
            deadline_type: spec.type,
            direction: apiDirection,
            is_public_entity: false,
            mediation_required: false,
      mediation_required: false,
            exclude_august: false,
          });
          return resp.data.deadline_date;
        };

        // Πάντα δημιουργούμε εγγραφή για τη δικάσιμο
        const hearingEntry = {
          entry_type: formData.entryType,
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: hearingDate,
          priority: 'high',
          status: 'ΣΕ ΑΝΑΜΟΝΗ',
          notes: 'Καταχωρημένο δικόγραφο - Δικάσιμος',
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        await axios.post(`${API}/entries`, hearingEntry);

        // Δημιουργία επιλεγμένων προθεσμιών
        for (const spec of specs) {
          if (document.querySelector(`[data-testid="${spec.testid}"]`)?.checked) {
            if (spec.base === 'filing' && !formData.case.efesi_filing_date) {
              toast.error(`Δεν υπολογίστηκε «${spec.title}» — συμπληρώστε την ημερομηνία κατάθεσης της έφεσης`);
              continue;
            }
            const deadlineDate = await computeDate(spec);
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(spec.title),
              date: deadlineDate,
              priority: 'high',
              notes: `${spec.article} - Δικάσιμος: ${fmtDate(hearingDate)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
            await axios.post(`${API}/entries`, entryData);
          }
        }
      }
      // Ανακοπή ερημοδικίας
      else if (formData.case.lawsuit_service_date && formData.case.procedure_type === 'civil' && formData.case.document_type === 'objection_default') {
        const selectedDeadlines = [];
        
        if (document.querySelector('[data-testid="deadline-erimodik-filing-checkbox"]')?.checked) {
          selectedDeadlines.push('anakopi_erimodik_filing');
        }
        if (document.querySelector('[data-testid="deadline-erimodik-service-checkbox"]')?.checked) {
          selectedDeadlines.push('epidosi_anakopis_erimodik');
        }
        if (formData.case.hearing_date && document.querySelector('[data-testid="deadline-erimodik-proposals-checkbox"]')?.checked) {
          selectedDeadlines.push('protaseis_erimodik');
        }

        if (selectedDeadlines.length > 0) {
          const deadlineRequest = {
            template_id: 'anakopi_erimodik',
            base_date: formData.case.lawsuit_service_date,
            selected_deadline_ids: selectedDeadlines,
            options: {
              foreign_resident: formData.case.has_foreign_defendant === 'yes',
              is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true
            }
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: capitalizeFirst(deadline.name),
              date: deadline.deadline_date,
              priority: 'high',
              notes: `${deadline.article} - Επίδοση απόφασης: ${fmtDate(formData.case.lawsuit_service_date)}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // ΔΕΝ δημιουργούμε "γενική" εγγραφή - μόνο επιλεγμένες προθεσμίες
      }
      // ==================== ΔΙΟΙΚΗΤΙΚΕΣ ΔΙΑΦΟΡΕΣ ====================
      // Μη φορολογικές - Επίδοση/Γνώση πράξης
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'notice' && 
               formData.case.administrative_notice_type === 'non_tax' &&
               formData.case.administrative_notice_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_prosfygi_mh_forol',
          base_date: formData.case.administrative_notice_date,
          selected_deadline_ids: ['prosfygi_mh_forol'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Άσκηση προσφυγής/Αίτησης ακύρωσης',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία επίδοσης/γνώσης: ${fmtDate(formData.case.administrative_notice_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Φορολογικές - Επίδοση/Γνώση πράξης
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'notice' && 
               formData.case.administrative_notice_type === 'tax' &&
               formData.case.administrative_notice_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_prosfygi_forol',
          base_date: formData.case.administrative_notice_date,
          selected_deadline_ids: ['prosfygi_forol'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Άσκηση προσφυγής',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία κοινοποίησης: ${fmtDate(formData.case.administrative_notice_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Επίδοση ενδίκου μέσου ΣτΕ — πρόσθετοι λόγοι (ν. 5119/2024)
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'ste_appeal_service' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_ste_epidosi',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['prosthetoi_logoi_ste_trimino'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => ({
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Κατάθεση πρόσθετων λόγων ΣτΕ',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Επίδοση ενδίκου μέσου: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          }));
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Κατάθεση δικογράφου ΣτΕ (ν. 5119/2024)
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'ste_filing' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_ste_katathesi',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['epidosi_eisagogikou_ste'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => ({
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Επίδοση εισαγωγικού δικογράφου ΣτΕ',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία κατάθεσης: ${fmtDate(formData.case.administrative_service_date)} — επί ποινή θέσης στο αρχείο`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          }));
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Επίδοση απόφασης Πρωτοδικείου
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'first_instance_service' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_epidosi_protodikeiou_loipes',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['efesi_dioik_protod'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Προθεσμία έφεσης',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία επίδοσης: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Δημοσίευση απόφασης Πρωτοδικείου
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'first_instance_publication' &&
               formData.case.administrative_publication_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_dimosieysi_protodikeiou',
          base_date: formData.case.administrative_publication_date,
          selected_deadline_ids: ['kataxristiki_efesi_dioik_protod'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Καταχρηστική προθεσμία έφεσης',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία δημοσίευσης: ${fmtDate(formData.case.administrative_publication_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Επίδοση απόφασης Εφετείου
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'appeal_service' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_epidosi_efeteiou',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['anairesi_dioik_efet'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Προθεσμία αναίρεσης',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία επίδοσης: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Δημοσίευση απόφασης Εφετείου
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'appeal_publication' &&
               formData.case.administrative_publication_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_dimosieysi_efeteiou',
          base_date: formData.case.administrative_publication_date,
          selected_deadline_ids: ['kataxristiki_anairesi_dioik_efet'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Καταχρηστική προθεσμία αναίρεσης',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία δημοσίευσης: ${fmtDate(formData.case.administrative_publication_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Επίδοση Εφετείου επί αιτήσεως ακύρωσης
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'appeal_cancellation_service' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_epidosi_efeteiou_akyrosis',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['efesi_dioik_efet_akyrosis'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Προθεσμία έφεσης (58 παρ. 3 ΣτΕ)',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία επίδοσης: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Δημοσίευση Εφετείου επί αιτήσεως ακύρωσης
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'appeal_cancellation_publication' &&
               formData.case.administrative_publication_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_dimosieysi_efeteiou_akyrosis',
          base_date: formData.case.administrative_publication_date,
          selected_deadline_ids: ['kataxristiki_efesi_dioik_efet_akyrosis'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Καταχρηστική προθεσμία έφεσης (58 παρ. 3 ΣτΕ)',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία δημοσίευσης: ${fmtDate(formData.case.administrative_publication_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Τελεσίδικη απόρριψη
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'final_rejection' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_telesidiki_aporripsi',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['ek_neou_dioik'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Εκ νέου άσκηση ενδίκου βοηθήματος',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία επίδοσης τελεσίδικης: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Ένορκες βεβαιώσεις
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'sworn_statements' &&
               formData.case.administrative_service_date) {
        
        const deadlineRequest = {
          template_id: 'dioikitiki_enorkes_vevaiosis',
          base_date: formData.case.administrative_service_date,
          selected_deadline_ids: ['enorkes_dioik'],
          options: {}
        };

        const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
        
        {
          const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Λήψη ενόρκων Βεβαιώσεων',
            date: deadline.deadline_date,
            priority: 'high',
            notes: `${deadline.article} - Ημ/νία γνωστοποίησης: ${fmtDate(formData.case.administrative_service_date)}`,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
            return entryData;
          });
          if (__batchEntries.length === 1) {
            await axios.post(`${API}/entries`, __batchEntries[0]);
          } else if (__batchEntries.length > 1) {
            await axios.post(`${API}/entries/batch`, __batchEntries);
          }
        }
      }
      // Διοικητικές - Άλλη διαδικαστική πράξη - Αναβολή συζήτησης (ΠΕΔΙΟ 3)
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'other' && 
               formData.case.administrative_other_type === 'postponement' &&
               (formData.case.postponement_date || formData.case.postponement_alt_date)) {
        
        // Determine the final postponement date
        const finalPostponementDate = formData.case.postponement_accepted === 'yes' 
          ? formData.case.postponement_date 
          : formData.case.postponement_alt_date;
        
        // Determine the case_id based on user selection
        let postponementCaseId = caseId || '';
        if (formData.case.postponement_is_new_case === 'no' && formData.case.postponement_selected_case_id) {
          postponementCaseId = formData.case.postponement_selected_case_id;
        }
        
        // Create the postponement entry (new hearing date)
        const postponementEntryData = {
          entry_type: formData.entryType,
          client_id: clientId,
          case_id: postponementCaseId,
          title: 'Αναβολή - Νέα δικάσιμος',
          date: new Date(finalPostponementDate).toISOString(),
          priority: 'high',
          notes: `Αναβολή συζήτησης από ${fmtDate(formData.case.postponement_date)}` + 
                 (formData.case.postponement_accepted === 'no' ? ` σε ${formData.case.postponement_alt_date}` : ''),
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        
        await axios.post(`${API}/entries`, postponementEntryData);
      }
      // ==================== ΔΙΟΙΚΗΤΙΚΗ ΔΙΚΑΣΙΜΟΣ ====================
      // 3.1 - Προσφυγή, Αγωγή, Έφεση (Εφετείο)
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'document' && 
               ['appeal_lawsuit', 'appeal', 'lawsuit', 'appeal_admin'].includes(formData.case.document_type) &&
               formData.case.hearing_date) {
        
        // Collect selected deadlines for 3.1
        const selectedDeadlineIds = [];
        if (document.getElementById('deadline-prostheti-logoi-3-1')?.checked) {
          selectedDeadlineIds.push('prostheti_logoi_3_1');
        }
        if (document.getElementById('deadline-anakoinosi-3-1')?.checked) {
          selectedDeadlineIds.push('anakoinosi_dikis_3_1');
        }
        if (document.getElementById('deadline-prosepiklisi-3-1')?.checked) {
          selectedDeadlineIds.push('prosepiklisi_3_1');
        }
        if (document.getElementById('deadline-paremvasi-3-1')?.checked) {
          selectedDeadlineIds.push('paremvasi_3_1');
        }
        if (document.getElementById('deadline-eggrafa-3-1')?.checked) {
          selectedDeadlineIds.push('eggrafa_3_1');
        }
        if (document.getElementById('deadline-ypomimma-meta-3-1')?.checked) {
          selectedDeadlineIds.push('ypomimma_meta_3_1');
        }
        if (document.getElementById('deadline-antikrousi-3-1')?.checked) {
          selectedDeadlineIds.push('antikrousi_3_1');
        }

        if (selectedDeadlineIds.length > 0) {
          const deadlineRequest = {
            template_id: 'dioikitiki_dikasimos_3_1',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlineIds,
            options: {}
          };

          // First create hearing entry for 3.1
          const courtNames31 = {
            'single_admin_first': 'Μονομελές Διοικητικό Πρωτοδικείο',
            'triple_admin_first': 'Τριμελές Διοικητικό Πρωτοδικείο',
            'admin_appeal': 'Διοικητικό Εφετείο',
            'council_of_state': 'Συμβούλιο της Επικρατείας'
          };
          const documentTypeNames31 = {
            'appeal_lawsuit': 'Προσφυγή/Αγωγή',
            'appeal': 'Προσφυγή',
            'lawsuit': 'Αγωγή',
            'appeal_admin': 'Έφεση'
          };
          const courtName31 = courtNames31[formData.case.court_type] || '';
          const courtLocation31 = formData.case.court_location || '';
          const documentTypeName31 = documentTypeNames31[formData.case.document_type] || '';
          const opponentDetails31 = formData.case.opponent_details || '';
          
          let hearingNotes31 = `${courtName31} ${courtLocation31}`.trim();
          if (documentTypeName31) hearingNotes31 += ` | ${documentTypeName31}`;
          if (opponentDetails31) hearingNotes31 += ` | Αντίδικος: ${opponentDetails31}`;
          
          const hearingEntryData = {
            entry_type: 'hearing',
            client_id: clientId,
            case_id: caseId || '',
            title: 'Δικάσιμος',
            date: new Date(formData.case.hearing_date).toISOString(),
            priority: 'high',
            notes: hearingNotes31,
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
          const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
          const hearingId = hearingResponse.data.id;

          // Then calculate and create deadlines linked to the hearing
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: deadline.name,
              date: deadline.deadline_date,
              priority: 'high',
              notes: `Δικάσιμος: ${fmtDate(formData.case.hearing_date)} - ${deadline.article}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null,
              parent_hearing_id: hearingId
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
      }
      // 3.2 - Αίτηση ακύρωσης, Αίτηση αναίρεσης (ΣτΕ)
      else if (formData.case.procedure_type === 'administrative' && 
               formData.case.administrative_action_type === 'document' && 
               ['cancellation_request', 'cassation_admin'].includes(formData.case.document_type) &&
               formData.case.hearing_date) {
        
        // Collect selected deadlines for 3.2 — καθεστώς ν. 5119/2024 (δικόγραφα από 16.9.2024)
        const admin32New = formData.case.admin_32_new_regime === 'yes';
        const ids32 = admin32New
          ? { koin: 'koinopoiisi_anairesis_ste', prosth: 'prosthetoi_logoi_ste', parem: 'paremvasi_ste', ypom: 'ypomnima_prin_ste', eggr: 'eggrafa_ste' }
          : { koin: 'koinopoiisi_anairesi', prosth: 'prostheti_logoi_3_2', parem: 'paremvasi_3_2', ypom: 'ypomimma_prin_3_2', eggr: 'eggrafa_3_2' };
        const selectedDeadlineIds = [];
        if (document.getElementById('deadline-koinopoiisi-3-2')?.checked) {
          selectedDeadlineIds.push(ids32.koin);
        }
        if (document.getElementById('deadline-prostheti-logoi-3-2')?.checked) {
          selectedDeadlineIds.push(ids32.prosth);
        }
        if (document.getElementById('deadline-paremvasi-3-2')?.checked) {
          selectedDeadlineIds.push(ids32.parem);
        }
        if (document.getElementById('deadline-ypomimma-prin-3-2')?.checked) {
          selectedDeadlineIds.push(ids32.ypom);
        }
        if (document.getElementById('deadline-eggrafa-3-2')?.checked) {
          selectedDeadlineIds.push(ids32.eggr);
        }
        if (selectedDeadlineIds.length > 0 && !formData.case.admin_32_new_regime) {
          toast.error('Διευκρινίστε πότε κατατέθηκε το εισαγωγικό δικόγραφο (πριν ή από 16.9.2024)');
          return;
        }

        if (selectedDeadlineIds.length > 0) {
          const deadlineRequest = {
            template_id: admin32New ? 'dioikitiki_dikasimos_3_2_full' : 'dioikitiki_dikasimos_3_2',
            base_date: formData.case.hearing_date,
            selected_deadline_ids: selectedDeadlineIds,
            options: {}
          };

          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: 'deadline',
              client_id: clientId,
              case_id: caseId || '',
              title: deadline.name,
              date: deadline.deadline_date,
              priority: 'high',
              notes: `Δικάσιμος: ${fmtDate(formData.case.hearing_date)} - ${deadline.article}`,
              opponent_details: formData.case.opponent_details || null,
              case_description_type: formData.case.case_description_type || null,
              case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }

        // Create hearing entry for 3.2
        const courtNames32 = {
          'single_admin_first': 'Μονομελές Διοικητικό Πρωτοδικείο',
          'triple_admin_first': 'Τριμελές Διοικητικό Πρωτοδικείο',
          'admin_appeal': 'Διοικητικό Εφετείο',
          'council_of_state': 'Συμβούλιο της Επικρατείας'
        };
        const documentTypeNames32 = {
          'cancellation_request': 'Αίτηση ακύρωσης',
          'cassation_admin': 'Αίτηση αναίρεσης'
        };
        const courtName32 = courtNames32[formData.case.court_type] || '';
        const courtLocation32 = formData.case.court_location || '';
        const documentTypeName32 = documentTypeNames32[formData.case.document_type] || '';
        const opponentDetails32 = formData.case.opponent_details || '';
        
        let hearingNotes32 = `${courtName32} ${courtLocation32}`.trim();
        if (documentTypeName32) hearingNotes32 += ` | ${documentTypeName32}`;
        if (opponentDetails32) hearingNotes32 += ` | Αντίδικος: ${opponentDetails32}`;
        
        const hearingEntryData32 = {
          entry_type: 'hearing',
          client_id: clientId,
          case_id: caseId || '',
          title: 'Δικάσιμος',
          date: new Date(formData.case.hearing_date).toISOString(),
          priority: 'high',
          notes: hearingNotes32,
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        await axios.post(`${API}/entries`, hearingEntryData32);
      }
      // ===========================================
      // ΠΟΙΝΙΚΗ ΔΙΑΔΙΚΑΣΙΑ - Υπολογισμός προθεσμιών
      // ===========================================
      else if (formData.case.procedure_type === 'criminal') {
        const criminalCourtNames = {
          'single_criminal': 'Μονομελές Πλημμελειοδικείο',
          'three_criminal': 'Τριμελές Πλημμελειοδικείο',
          'single_criminal_appeal': 'Μονομελές Εφετείο Κακουργημάτων',
          'triple_criminal_appeal': 'Τριμελές Εφετείο Κακουργημάτων',
          'five_criminal_appeal': 'Πενταμελές Εφετείο',
          'mixed_court': 'Μικτό Ορκωτό Δικαστήριο',
          'mixed_jury': 'Μικτό Ορκωτό',
          'appeal_criminal': 'Εφετείο Κακουργημάτων',
          'supreme_criminal': 'Άρειος Πάγος (Ποινικό)'
        };

        // 1. Πράξη προδικασίας - Βούλευμα
        if (formData.case.is_preliminary === 'yes' && formData.case.preliminary_date) {
          const deadlineRequest = {
            template_id: 'poiniki_voulevma',
            base_date: formData.case.preliminary_date,
            selected_deadline_ids: ['efesi_voulevma'],
            options: {}
          };
          
          const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
          
          {
            const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: deadline.name,
              date: deadline.deadline_date,
              priority: 'high',
              notes: `Επίδοση βουλεύματος: ${fmtDate(formData.case.preliminary_date)} - ${deadline.article}`,
              opponent_details: null,
              case_description_type: null,
              case_description_custom: null
            };
              return entryData;
            });
            if (__batchEntries.length === 1) {
              await axios.post(`${API}/entries`, __batchEntries[0]);
            } else if (__batchEntries.length > 1) {
              await axios.post(`${API}/entries/batch`, __batchEntries);
            }
          }
        }
        // 2. Επίδοση κλήτευσης - Απευθείας κλήση
        else if (formData.case.is_preliminary === 'no' && formData.case.criminal_action_type === 'summons') {
          // Απευθείας κλήση - προσφυγή κατά κλητηρίου
          if (formData.case.direct_summons === 'yes') {
            // 1. Προσφυγή κατά κλητηρίου: 10 ημ. από επίδοση
            if (formData.case.criminal_service_date) {
              const deadlineRequest = {
                template_id: 'poiniki_apeftheias_klisi',
                base_date: formData.case.criminal_service_date,
                selected_deadline_ids: ['prosfygi_klitirio'],
                options: {}
              };
              const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
              for (const deadline of deadlinesResponse.data.deadlines) {
                await axios.post(`${API}/entries`, {
                  entry_type: 'deadline',
                  client_id: clientId,
                  case_id: caseId || '',
                  title: deadline.name,
                  date: deadline.deadline_date,
                  priority: 'high',
                  notes: `Επίδοση κλητηρίου: ${fmtDate(formData.case.criminal_service_date)} - ${deadline.article}`,
                  opponent_details: null,
                  case_description_type: null,
                  case_description_custom: null
                });
              }
            }
            // 2. Optional Δικάσιμος entry
            if (formData.case.hearing_date) {
              const hearingEntryData = {
                entry_type: 'hearing',
                client_id: clientId,
                case_id: caseId || '',
                title: 'Δικάσιμος',
                date: formData.case.hearing_date,
                priority: 'high',
                notes: `${criminalCourtNames[formData.case.court_type] || ''} ${formData.case.court_location || ''} - Απευθείας κλήση`,
                opponent_details: null,
                case_description_type: null,
                case_description_custom: null
              };
              await axios.post(`${API}/entries`, hearingEntryData);
            }
          }
          // Δικάσιμος με κανονική κλήτευση
          else if (formData.case.direct_summons === 'no' && formData.case.hearing_date) {
            // Create Δικάσιμος entry FIRST
            const hearingEntryData = {
              entry_type: 'hearing',
              client_id: clientId,
              case_id: caseId || '',
              title: 'Δικάσιμος',
              date: formData.case.hearing_date,
              priority: 'high',
              notes: `${criminalCourtNames[formData.case.court_type] || ''} ${formData.case.court_location || ''}`,
              opponent_details: null,
              case_description_type: null,
              case_description_custom: null
            };
            const hearingResponse = await axios.post(`${API}/entries`, hearingEntryData);
            const hearingId = hearingResponse.data.id;

            // Άρειος Πάγος - Πρόσθετοι λόγοι αναίρεσης
            if (formData.case.court_type === 'supreme_criminal') {
              const deadlineRequest = {
                template_id: 'poiniki_areios_pagos',
                base_date: formData.case.hearing_date,
                selected_deadline_ids: ['prostheti_logoi_anairesis'],
                options: {}
              };
              
              const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
              
              {
                const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
                const entryData = {
                  entry_type: 'deadline',
                  client_id: clientId,
                  case_id: caseId || '',
                  title: deadline.name,
                  date: deadline.deadline_date,
                  priority: 'high',
                  notes: `Δικάσιμος Αρείου Πάγου: ${fmtDate(formData.case.hearing_date)} - ${deadline.article}`,
                  parent_hearing_id: hearingId,
                  opponent_details: null,
                  case_description_type: null,
                  case_description_custom: null
                };
                  return entryData;
                });
                if (__batchEntries.length === 1) {
                  await axios.post(`${API}/entries`, __batchEntries[0]);
                } else if (__batchEntries.length > 1) {
                  await axios.post(`${API}/entries/batch`, __batchEntries);
                }
              }
            }
          }
        }
        // 3. Άλλη διαδικαστική πράξη - Απόφαση
        else if (formData.case.is_preliminary === 'no' && formData.case.criminal_action_type === 'other') {
          // Αναβολή συζήτησης
          if (formData.case.criminal_other_type === 'postponement') {
            if (!formData.case.criminal_other_date) {
              toast.error('Συμπληρώστε τη νέα ημερομηνία δικασίμου');
              return;
            }
            const entryData = {
              entry_type: formData.entryType,
              client_id: clientId,
              case_id: caseId || '',
              title: 'Δικάσιμος',
              date: new Date(formData.case.criminal_other_date).toISOString(),
              priority: 'high',
              status: 'ΣΕ ΑΝΑΜΟΝΗ',
              notes: 'Αναβολή συζήτησης — νέα δικάσιμος ποινικής υπόθεσης',
              opponent_details: null,
              case_description_type: null,
              case_description_custom: null
            };
            await axios.post(`${API}/entries`, entryData);
          }
          // Έκδοση/Επίδοση απόφασης ποινικού δικαστηρίου
          else if (formData.case.criminal_other_type === 'decision') {
            // Πρωτοβάθμιο δικαστήριο
            if (formData.case.criminal_decision_level === 'first') {
              // Παρών κατηγορούμενος
              if (formData.case.criminal_was_present === 'yes' && formData.case.criminal_hearing_date) {
                const deadlineRequest = {
                  template_id: 'poiniki_apofasi_protov_paron',
                  base_date: formData.case.criminal_hearing_date,
                  selected_deadline_ids: ['efesi_poiniki_paron', 'aitisi_akyrosis_poiniki_paron'],
                  options: {}
                };
                
                const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
                
                {
                  const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
                  const entryData = {
                    entry_type: formData.entryType,
                    client_id: clientId,
                    case_id: caseId || '',
                    title: deadline.name,
                    date: deadline.deadline_date,
                    priority: 'high',
                    notes: `Συζήτηση: ${fmtDate(formData.case.criminal_hearing_date)} - ${deadline.article}`,
                    opponent_details: null,
                    case_description_type: null,
                    case_description_custom: null
                  };
                    return entryData;
                  });
                  if (__batchEntries.length === 1) {
                    await axios.post(`${API}/entries`, __batchEntries[0]);
                  } else if (__batchEntries.length > 1) {
                    await axios.post(`${API}/entries/batch`, __batchEntries);
                  }
                }
              }
              // Απών κατηγορούμενος
              else if (formData.case.criminal_was_present === 'no' && formData.case.criminal_service_date) {
                // Αίτηση ακύρωσης 15 ημέρες από δημοσίευση
                const akyroshRequest = {
                  template_id: 'poiniki_apofasi_protov_apon',
                  base_date: formData.case.criminal_decision_date || formData.case.criminal_service_date,
                  selected_deadline_ids: ['aitisi_akyrosis_poiniki_apon'],
                  options: {}
                };
                
                const akyroshResponse = await axios.post(`${API}/calculate-from-template`, akyroshRequest);
                
                {
                  const __batchEntries = akyroshResponse.data.deadlines.map(deadline => {
                  const entryData = {
                    entry_type: formData.entryType,
                    client_id: clientId,
                    case_id: caseId || '',
                    title: deadline.name,
                    date: deadline.deadline_date,
                    priority: 'high',
                    notes: `Δημοσίευση απόφασης - ${deadline.article}`,
                    opponent_details: null,
                    case_description_type: null,
                    case_description_custom: null
                  };
                    return entryData;
                  });
                  if (__batchEntries.length === 1) {
                    await axios.post(`${API}/entries`, __batchEntries[0]);
                  } else if (__batchEntries.length > 1) {
                    await axios.post(`${API}/entries/batch`, __batchEntries);
                  }
                }
                
                // Έφεση 10 ημέρες από επίδοση
                if (formData.case.criminal_service_date) {
                  const efesiRequest = {
                    template_id: 'poiniki_apofasi_protov_apon',
                    base_date: formData.case.criminal_service_date,
                    selected_deadline_ids: ['efesi_poiniki_apon'],
                    options: {}
                  };
                  
                  const efesiResponse = await axios.post(`${API}/calculate-from-template`, efesiRequest);
                  
                  {
                    const __batchEntries = efesiResponse.data.deadlines.map(deadline => {
                    const entryData = {
                      entry_type: formData.entryType,
                      client_id: clientId,
                      case_id: caseId || '',
                      title: deadline.name,
                      date: deadline.deadline_date,
                      priority: 'high',
                      notes: `Επίδοση: ${fmtDate(formData.case.criminal_service_date)} - ${deadline.article}`,
                      opponent_details: null,
                      case_description_type: null,
                      case_description_custom: null
                    };
                      return entryData;
                    });
                    if (__batchEntries.length === 1) {
                      await axios.post(`${API}/entries`, __batchEntries[0]);
                    } else if (__batchEntries.length > 1) {
                      await axios.post(`${API}/entries/batch`, __batchEntries);
                    }
                  }
                }
              }
            }
            // Δευτεροβάθμιο δικαστήριο
            else if (formData.case.criminal_decision_level === 'second') {
              // Παρών κατηγορούμενος - Αναίρεση
              if (formData.case.criminal_was_present === 'yes' && formData.case.criminal_clean_copy_date) {
                const deadlineRequest = {
                  template_id: 'poiniki_apofasi_deuterov_paron',
                  base_date: formData.case.criminal_clean_copy_date,
                  selected_deadline_ids: ['anairesi_poiniki_paron'],
                  options: {}
                };
                
                const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
                
                {
                  const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
                  const entryData = {
                    entry_type: formData.entryType,
                    client_id: clientId,
                    case_id: caseId || '',
                    title: deadline.name,
                    date: deadline.deadline_date,
                    priority: 'high',
                    notes: `Καθαρογραφή: ${fmtDate(formData.case.criminal_clean_copy_date)} - ${deadline.article}`,
                    opponent_details: null,
                    case_description_type: null,
                    case_description_custom: null
                  };
                    return entryData;
                  });
                  if (__batchEntries.length === 1) {
                    await axios.post(`${API}/entries`, __batchEntries[0]);
                  } else if (__batchEntries.length > 1) {
                    await axios.post(`${API}/entries/batch`, __batchEntries);
                  }
                }
              }
              // Απών κατηγορούμενος
              else if (formData.case.criminal_was_present === 'no' && formData.case.criminal_decision_date) {
                // Αίτηση ακύρωσης 15 ημέρες από έκδοση
                const deadlineRequest = {
                  template_id: 'poiniki_apofasi_deuterov_apon',
                  base_date: formData.case.criminal_decision_date,
                  selected_deadline_ids: ['aitisi_akyrosis_deuterov_apon'],
                  options: {}
                };
                
                const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
                
                {
                  const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
                  const entryData = {
                    entry_type: formData.entryType,
                    client_id: clientId,
                    case_id: caseId || '',
                    title: deadline.name,
                    date: deadline.deadline_date,
                    priority: 'high',
                    notes: `Έκδοση απόφασης: ${fmtDate(formData.case.criminal_decision_date)} - ${deadline.article}`,
                    opponent_details: null,
                    case_description_type: null,
                    case_description_custom: null
                  };
                    return entryData;
                  });
                  if (__batchEntries.length === 1) {
                    await axios.post(`${API}/entries`, __batchEntries[0]);
                  } else if (__batchEntries.length > 1) {
                    await axios.post(`${API}/entries/batch`, __batchEntries);
                  }
                }

                // Άσκηση Αναίρεσης 20 ημέρες από επίδοση (αν υπάρχει ημερομηνία επίδοσης)
                if (formData.case.criminal_service_date) {
                  const anairesiReq = {
                    template_id: 'poiniki_apofasi_deuterov_apon',
                    base_date: formData.case.criminal_service_date,
                    selected_deadline_ids: ['anairesi_poiniki_deuterov_apon'],
                    options: {}
                  };
                  const anairesiResp = await axios.post(`${API}/calculate-from-template`, anairesiReq);
                  {
                    const __batchEntries = anairesiResp.data.deadlines.map(deadline => {
                    const entryData = {
                      entry_type: formData.entryType,
                      client_id: clientId,
                      case_id: caseId || '',
                      title: deadline.name,
                      date: deadline.deadline_date,
                      priority: 'high',
                      notes: `Επίδοση: ${fmtDate(formData.case.criminal_service_date)} - ${deadline.article}`,
                      opponent_details: null,
                      case_description_type: null,
                      case_description_custom: null
                    };
                      return entryData;
                    });
                    if (__batchEntries.length === 1) {
                      await axios.post(`${API}/entries`, __batchEntries[0]);
                    } else if (__batchEntries.length > 1) {
                      await axios.post(`${API}/entries/batch`, __batchEntries);
                    }
                  }
                }
              }
            }
          }
        }
        // Καμία συγκεκριμένη ποινική ροή — μην δημιουργείς κενή εγγραφή
        else {
          toast.error('Συμπληρώστε τα στοιχεία της ποινικής διαδικασίας για να γίνει καταχώρηση');
          return;
        }
      }
      // ===========================================
      // ΠΕΔΙΟ 4 - ΡΑΝΤΕΒΟΥ / ΕΞΩΔΙΚΑΣΤΙΚΗ ΑΠΑΣΧΟΛΗΣΗ
      // ===========================================
      else if (formData.entryType === 'other_activity') {
        let entryDate = new Date().toISOString();
        let entryTitle = 'Εξωδικαστική απασχόληση';
        let entryPriority = 'medium';
        let entryNotes = formData.case.appointment_description || '';

        // Ραντεβού
        if (formData.case.appointment_type === 'appointment') {
          if (formData.case.appointment_date) {
            // Combine date and time
            let dateStr = formData.case.appointment_date;
            if (formData.case.appointment_time) {
              dateStr = `${formData.case.appointment_date}T${formData.case.appointment_time}:00`;
            }
            entryDate = new Date(dateStr).toISOString();
          }
          entryTitle = 'Ραντεβού';
          if (formData.case.appointment_time) {
            entryNotes = `Ώρα: ${formData.case.appointment_time}` + (entryNotes ? ` - ${entryNotes}` : '');
          }
        }
        // Άλλη εργασία
        else if (formData.case.appointment_type === 'task') {
          if (formData.case.task_has_deadline === 'specific_date' && formData.case.task_date) {
            entryDate = new Date(formData.case.task_date).toISOString();
            entryTitle = 'Εργασία';
          } else if (formData.case.task_has_deadline === 'deadline' && formData.case.task_deadline) {
            entryDate = new Date(formData.case.task_deadline).toISOString();
            entryTitle = 'Προθεσμία εργασίας';
            entryPriority = 'high';
          }
        }

        // Για "Άλλη εργασία" με περιγραφή: δημιουργία αντίστοιχης υπόθεσης
        // ώστε η εγγραφή να εμφανίζεται ως υπόθεση του πελάτη
        let extrajudicialCaseId = '';
        if (
          formData.case.appointment_type === 'task' &&
          formData.case.appointment_description
        ) {
          try {
            const caseResp = await axios.post(`${API}/cases`, {
              client_id: clientId,
              procedure_type: 'extrajudicial',
              case_description_type: 'other',
              case_description_custom: capitalizeFirst(formData.case.appointment_description),
            });
            extrajudicialCaseId = caseResp.data.id;
          } catch (e) {
            console.error('Failed to create extrajudicial case:', e);
          }
        }

        // Δημιουργία εγγραφής
        const entryData = {
          entry_type: 'other_activity',
          client_id: clientId,
          case_id: extrajudicialCaseId, // Συνδέεται με την εξωδικαστική υπόθεση (αν δημιουργήθηκε)
          title: entryTitle,
          date: entryDate,
          priority: entryPriority,
          notes: entryNotes,
          opponent_details: null,
          // Σημείωση: case_description_type='other' ώστε η περιγραφή να εμφανίζεται στην καρτέλα
          case_description_type: formData.case.appointment_description ? 'other' : null,
          case_description_custom: formData.case.appointment_description
            ? capitalizeFirst(formData.case.appointment_description)
            : null,
          duration_minutes:
            formData.case.appointment_type === 'appointment'
              ? parseInt(formData.case.appointment_duration || '60', 10)
              : null,
        };
        await axios.post(`${API}/entries`, entryData);

        // Προσθήκη στην οικονομική διαχείριση
        if (formData.case.appointment_add_to_financial && formData.case.appointment_fee) {
          const feeAmount = parseFloat(formData.case.appointment_fee);
          if (feeAmount > 0) {
            const financialEntry = {
              entry_type: 'fee',
              amount: feeAmount,
              description: formData.case.appointment_description || entryTitle,
              date: formData.case.appointment_date || formData.case.task_date || formData.case.task_deadline || new Date().toISOString().split('T')[0],
              client_id: clientId,
              case_id: extrajudicialCaseId || null,
              fee_type: 'flat',
              is_paid: false,
              notes: `Από εγγραφή: ${entryTitle}`
            };
            await axios.post(`${API}/financial/entries`, financialEntry);
            toast.success('Η αμοιβή καταχωρήθηκε στην οικονομική διαχείριση');
          }
        }
      }
      // =====================================================
      // ΑΣΤΙΚΗ ΔΙΑΔΙΚΑΣΙΑ - ΑΛΛΕΣ ΔΙΑΔΙΚΑΣΤΙΚΕΣ ΠΡΑΞΕΙΣ
      // =====================================================
      else if (formData.case.procedure_type === 'civil' && formData.case.action_type === 'other' && formData.case.civil_other_type && formData.case.civil_other_date) {
        
        if (formData.case.civil_other_type === 'sworn_statement') {
          // ΚΠολΔ 421-422: γνωστοποίηση/κλήτευση αντιδίκου τουλάχιστον 2 εργάσιμες ημέρες πριν τη λήψη
          const lipsiDate = formData.case.civil_other_date;
          const gnostDate = shiftWorkingDays(lipsiDate, -3);
          const swornCommon = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            priority: 'high',
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
          await axios.post(`${API}/entries/batch`, [
            { ...swornCommon, title: 'Γνωστοποίηση λήψης ένορκης βεβαίωσης', date: gnostDate,
              notes: `άρθρα 421-422 ΚΠολΔ — Κλήτευση αντιδίκου τουλάχιστον 2 εργάσιμες ημέρες πριν τη λήψη (${fmtDate(lipsiDate)})` },
            { ...swornCommon, title: 'Λήψη ένορκης βεβαίωσης', date: lipsiDate,
              notes: 'άρθρο 421 ΚΠολΔ' },
          ]);
        } else if (formData.case.civil_other_type === 'mediation_session') {
          await axios.post(`${API}/entries`, {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: 'Συνεδρία διαμεσολάβησης',
            date: formData.case.civil_other_date,
            priority: 'high',
            notes: 'Ημερομηνία που ορίστηκε από τον διαμεσολαβητή',
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          });
        } else {
        // Mapping of civil_other_type to template_id
        const templateMapping = {
          'cancellation': 'astiki_mataiosi',
          'withdrawal': 'astiki_paraitisi',
          'final_decision_service': null, // Complex logic - handled separately
          'final_decision_publication': 'astiki_dimosieysi_oristikis',
          'final_rejection': 'astiki_telesidiki_aporripsi',
          'payment_order_issue': 'astiki_ekdosi_dp',
          'katataxi_prosklisi': 'astiki_pinakas_katataxis',
          'diataxi_prodikastiki': 'astiki_ekdosi_diataxis',
          'payment_order_service': null, // Complex logic - handled separately
          'enforcement_action': null, // Complex logic - handled separately
        };

        let templateId = templateMapping[formData.case.civil_other_type];
        let deadlineIds = [];

        // Handle complex cases with sub-options
        if (formData.case.civil_other_type === 'final_decision_service') {
          if (formData.case.is_first_instance === 'yes') {
            if (formData.case.is_small_claims === 'yes') {
              // Μικροδιαφορά - δεν υπάρχει έφεση
              templateId = null;
            } else if (formData.case.is_default_judgment === 'yes') {
              templateId = 'astiki_epidosi_oristikis_erimin';
              deadlineIds = ['efesi_erimin', 'anakopi_erimodik_protov'];
            } else {
              templateId = 'astiki_epidosi_oristikis';
              deadlineIds = ['efesi_30'];
            }
          } else if (formData.case.is_final === 'yes') {
            templateId = 'astiki_epidosi_telesidikis';
            deadlineIds = ['anairesi_60'];
          }
        } else if (formData.case.civil_other_type === 'payment_order_service') {
          if (formData.case.payment_order_service_type === 'first') {
            templateId = 'astiki_proti_epidosi_dp';
            deadlineIds = ['anakopi_dp_proti'];
          } else if (formData.case.payment_order_service_type === 'second') {
            templateId = 'astiki_defteri_epidosi_dp';
            deadlineIds = ['anakopi_dp_defteri'];
          } else if (formData.case.payment_order_service_type === 'rent') {
            templateId = 'astiki_epidosi_dapm';
            deadlineIds = ['anakopi_dapm'];
          }
        } else if (formData.case.civil_other_type === 'enforcement_action') {
          if (formData.case.enforcement_remedy === 'article_954') {
            templateId = 'astiki_anakopi_954';
            deadlineIds = ['anakopi_954'];
          } else if (formData.case.enforcement_article === '934a_seizure') {
            templateId = 'astiki_anakopi_933_katasxesi';
            deadlineIds = ['anakopi_933_katasxesi'];
          } else if (formData.case.enforcement_article === '934a_immediate') {
            templateId = 'astiki_anakopi_933_amesi';
            deadlineIds = ['anakopi_933_amesi'];
          } else if (formData.case.enforcement_article === '934b_last_action') {
            templateId = 'astiki_anakopi_933_teleytaia';
            deadlineIds = ['anakopi_933_teleytaia'];
          } else if (formData.case.enforcement_article === '934b_movables') {
            templateId = 'astiki_anakopi_933_plist_kiniton';
            deadlineIds = ['anakopi_933_plist_kin'];
          } else if (formData.case.enforcement_article === '934b_immovables') {
            templateId = 'astiki_anakopi_933_plist_akiniton';
            deadlineIds = ['anakopi_933_plist_akin'];
          }
        } else if (templateId) {
          // For simple templates, select all deadlines
          const simpleDeadlineIds = {
            'astiki_mataiosi': ['epanafora_klisi'],
            'astiki_paraitisi': ['ek_neou_agogi'],
            'astiki_dimosieysi_oristikis': ['kataxristiki_efesis'],
            'astiki_telesidiki_aporripsi': ['ek_neou_agogi_aporripsi'],
            'astiki_ekdosi_dp': ['epidosi_dp'],
            'astiki_pinakas_katataxis': ['anakopi_979'],
            'astiki_ekdosi_diataxis': ['antilexi_diataxis'],
          };
          deadlineIds = simpleDeadlineIds[templateId] || [];
        }

        // Calculate deadlines using the API
        if (templateId && deadlineIds.length > 0) {
          const deadlineRequest = {
            template_id: templateId,
            base_date: formData.case.civil_other_date,
            selected_deadline_ids: deadlineIds,
            options: templateId === 'astiki_anakopi_954' && formData.case.enf_954_old_regime === 'yes'
              ? { pre_2026_epitagi: true }
              : {}
          };

          try {
            const deadlinesResponse = await axios.post(`${API}/calculate-from-template`, deadlineRequest);
            
            // Create one entry for each calculated deadline
            {
              const __batchEntries = deadlinesResponse.data.deadlines.map(deadline => {
              const entryData = {
                entry_type: formData.entryType,
                client_id: clientId,
                case_id: caseId || '',
                title: capitalizeFirst(deadline.name),
                date: deadline.deadline_date,
                priority: 'high',
                notes: `${deadline.article} - Από ${fmtDate(formData.case.civil_other_date)}`,
                opponent_details: formData.case.opponent_details || null,
                case_description_type: formData.case.case_description_type || null,
                case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
              };
                return entryData;
              });
              if (__batchEntries.length === 1) {
                await axios.post(`${API}/entries`, __batchEntries[0]);
              } else if (__batchEntries.length > 1) {
                await axios.post(`${API}/entries/batch`, __batchEntries);
              }
            }
          } catch (error) {
            console.error('Error calculating deadlines:', error);
            toast.error('Σφάλμα κατά τον υπολογισμό προθεσμιών');
          }
        } else {
          // No template available (e.g., μικροδιαφορά) - create simple entry
          const entryData = {
            entry_type: formData.entryType,
            client_id: clientId,
            case_id: caseId || '',
            title: capitalizeFirst(autoTitle),
            date: new Date(formData.case.civil_other_date).toISOString(),
            priority: 'medium',
            notes: formData.case.description || '',
            opponent_details: formData.case.opponent_details || null,
            case_description_type: formData.case.case_description_type || null,
            case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
          };
          await axios.post(`${API}/entries`, entryData);
        }
        }
      }
      else {
        // Simple entry without deadline calculation (Άλλη απασχόληση or other)
        let entryDate = new Date().toISOString();
        if (formData.case.civil_other_date) {
          entryDate = new Date(formData.case.civil_other_date).toISOString();
        } else if (formData.case.preliminary_date) {
          entryDate = new Date(formData.case.preliminary_date).toISOString();
        } else if (formData.case.administrative_notice_date) {
          entryDate = new Date(formData.case.administrative_notice_date).toISOString();
        }

        const entryData = {
          entry_type: formData.entryType,
          client_id: clientId,
          case_id: caseId || '',
          title: capitalizeFirst(autoTitle),
          date: entryDate,
          priority: 'medium',
          notes: formData.case.description || '',
          opponent_details: formData.case.opponent_details || null,
          case_description_type: formData.case.case_description_type || null,
          case_description_custom: capitalizeFirst(formData.case.case_description_custom) || null
        };
        
        await axios.post(`${API}/entries`, entryData);
      }

      // ΣΗΜΑΝΤΙΚΟ: ΔΕΝ δημιουργούμε επιπλέον "Δικάσιμος" entry εδώ
      // Η εγγραφή δημιουργείται ήδη στις διαδικασίες 3.1 και 3.2 με entry_type: 'hearing'
      // Προηγούμενη λογική που δημιουργούσε διπλές εγγραφές αφαιρέθηκε

      // Διαμεσολάβηση: δημιουργία εργασιών προετοιμασίας
      if (formData.case.procedure_type === 'civil' && formData.case.mediation_required === true) {
        try {
          const reqDay = shiftWorkingDays(new Date().toISOString().slice(0, 10), 1);
          const sendDay = shiftWorkingDays(reqDay, 2);
          await axios.post(`${API}/day-tasks`, { date: reqDay, title: 'Αίτημα για ορισμό διαμεσολαβητή', duration_minutes: 30, notes: 'Διαμεσολάβηση', client_id: clientId || '', case_id: caseId || '' });
          await axios.post(`${API}/day-tasks`, { date: sendDay, title: 'Αποστολή στοιχείων σε διαμεσολαβητή', duration_minutes: 60, notes: 'Διαμεσολάβηση', client_id: clientId || '', case_id: caseId || '' });
        } catch (mediationErr) {
          console.error('mediation tasks failed', mediationErr);
        }
      }

      // Auto-sync to Google Calendar if enabled
      try {
        // Get all created entry IDs (we need to track them)
        const allEntries = await axios.get(`${API}/entries`);
        const latestEntries = allEntries.data.slice(-10); // Get last 10 entries (newly created)
        
        // Try to sync each entry
        for (const entry of latestEntries) {
          if (!entry.calendar_event_id) {
            try {
              await axios.post(`${API}/sync-to-calendar?entry_id=${entry.id}`, {}, {
                withCredentials: true
              });
            } catch (syncError) {
              console.log('Calendar sync skipped or failed:', syncError.response?.data?.message);
            }
          }
        }
      } catch (error) {
        console.log('Auto-sync to calendar failed (may not be enabled):', error);
      }

      toast.success('Η εγγραφή δημιουργήθηκε επιτυχώς');
      onComplete();
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Σφάλμα κατά τη δημιουργία εγγραφής');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderEntryTypeSelection = () => (
    <div className="space-y-4">
      <h3 className="hidden sm:flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Η νέα εγγραφή αφορά</h3>
      <div className="flex items-center gap-2 sm:gap-3" style={{ width: '100%' }}>
        <button
          data-testid="court-action-btn"
          onClick={() => setField('entryType', 'court_action')}
          className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
            formData.entryType === 'court_action' 
              ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
              : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
          }`}
        >
          <Scale className="w-4 h-4 hidden sm:block opacity-90" />
          Δικαστική πράξη
        </button>
        <button
          data-testid="other-activity-btn"
          onClick={() => setField('entryType', 'other_activity')}
          className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
            formData.entryType === 'other_activity' 
              ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
              : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
          }`}
        >
          <FileText className="w-4 h-4 hidden sm:block opacity-90" />
          Άλλη απασχόληση
        </button>
      </div>
    </div>
  );

  // ΠΕΔΙΟ 4 - Ραντεβού / Εξωδικαστική απασχόληση
  const renderAppointmentForm = () => (
    <div className="space-y-6">
      <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Εξωδικαστική απασχόληση</h3>
      
      {/* Τύπος εγγραφής: Ραντεβού ή Άλλη εργασία */}
      <div>
        <Label>Η εγγραφή αφορά *</Label>
        <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
          <button
            data-testid="appointment-type-appointment-btn"
            onClick={() => updateFormData('case', 'appointment_type', 'appointment')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              formData.case.appointment_type === 'appointment' 
                ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
            }`}
          >
            <Calendar className="w-4 h-4 hidden sm:block opacity-90" />
            Ραντεβού
          </button>
          <button
            data-testid="appointment-type-task-btn"
            onClick={() => updateFormData('case', 'appointment_type', 'task')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              formData.case.appointment_type === 'task' 
                ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
            }`}
          >
            <FileText className="w-4 h-4 hidden sm:block opacity-90" />
            Άλλη εργασία
          </button>
        </div>
      </div>

      {/* Ραντεβού - Ημερομηνία και Ώρα */}
      {formData.case.appointment_type === 'appointment' && (
        <>
          <div>
            <Label htmlFor="appointment-date">Ημερομηνία ραντεβού *</Label>
            <LargeDatePicker
              id="appointment-date"
              data-testid="appointment-date-input"
              value={formData.case.appointment_date}
              onChange={(e) => updateFormData('case', 'appointment_date', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="appointment-time">Ώρα *</Label>
            <Input
              id="appointment-time"
              type="time"
              data-testid="appointment-time-input"
              value={formData.case.appointment_time}
              onChange={(e) => updateFormData('case', 'appointment_time', e.target.value)}
              className="w-full h-8 px-3 py-1 border border-stone-300 rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-0 focus:border-stone-400"
            />
          </div>
          <div>
            <Label htmlFor="appointment-duration">Διάρκεια</Label>
            <Select
              value={String(formData.case.appointment_duration || '60')}
              onValueChange={(v) => updateFormData('case', 'appointment_duration', v)}
            >
              <SelectTrigger data-testid="appointment-duration-select" className="w-full h-11 border-stone-300 rounded-lg bg-white text-slate-900 text-sm focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30" className="text-sm">30 λεπτά</SelectItem>
                <SelectItem value="60" className="text-sm">1 ώρα</SelectItem>
                <SelectItem value="90" className="text-sm">1,5 ώρα</SelectItem>
                <SelectItem value="120" className="text-sm">2 ώρες</SelectItem>
                <SelectItem value="150" className="text-sm">2,5 ώρες</SelectItem>
                <SelectItem value="180" className="text-sm">3 ώρες</SelectItem>
                <SelectItem value="210" className="text-sm">3,5 ώρες</SelectItem>
                <SelectItem value="240" className="text-sm">4 ώρες</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="appointment-description">Περιγραφή ραντεβού</Label>
            <Input
              id="appointment-description"
              data-testid="appointment-description-input"
              value={formData.case.appointment_description}
              onChange={(e) => updateFormData('case', 'appointment_description', e.target.value)}
              placeholder="π.χ. Συνάντηση με πελάτη, Επίσκεψη σε γραφείο..."
              className="h-11 bg-white border-stone-300 rounded-lg items-center"
            />
          </div>
        </>
      )}

      {/* Άλλη εργασία - Ημερομηνία ή Προθεσμία */}
      {formData.case.appointment_type === 'task' && (
        <>
          <div>
            <Label>Τύπος προθεσμίας *</Label>
            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
              <button
                data-testid="task-specific-date-btn"
                onClick={() => updateFormData('case', 'task_has_deadline', 'specific_date')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.case.task_has_deadline === 'specific_date' 
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Συγκεκριμένη ημερομηνία
              </button>
              <button
                data-testid="task-deadline-btn"
                onClick={() => updateFormData('case', 'task_has_deadline', 'deadline')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.case.task_has_deadline === 'deadline' 
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Προθεσμία διεκπεραίωσης
              </button>
            </div>
          </div>

          {formData.case.task_has_deadline === 'specific_date' && (
            <div>
              <Label htmlFor="task-date">Ημερομηνία εκτέλεσης *</Label>
              <LargeDatePicker 
                id="task-date" 
                data-testid="task-date-input" 
                value={formData.case.task_date} 
                onChange={(e) => updateFormData('case', 'task_date', e.target.value)} 
              />
            </div>
          )}

          {formData.case.task_has_deadline === 'deadline' && (
            <div>
              <Label htmlFor="task-deadline">Προθεσμία μέχρι *</Label>
              <LargeDatePicker 
                id="task-deadline" 
                data-testid="task-deadline-input" 
                value={formData.case.task_deadline} 
                onChange={(e) => updateFormData('case', 'task_deadline', e.target.value)} 
              />
            </div>
          )}

          <div>
            <Label htmlFor="task-description">Περιγραφή εργασίας *</Label>
            <Input
              id="task-description"
              data-testid="task-description-input"
              value={formData.case.appointment_description}
              onChange={(e) => updateFormData('case', 'appointment_description', e.target.value)}
              placeholder="π.χ. Σύνταξη γνωμοδότησης, Έρευνα νομολογίας..."
              className="h-11 bg-white border-stone-300 rounded-lg items-center"
            />
          </div>
        </>
      )}

      {/* Αμοιβή - Κοινό για ραντεβού και εργασία */}
      {formData.case.appointment_type && (
        <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add-fee-checkbox"
              data-testid="add-fee-checkbox"
              checked={formData.case.appointment_add_to_financial}
              onChange={(e) => updateFormData('case', 'appointment_add_to_financial', e.target.checked)}
              className="h-4 w-4 rounded border-stone-300 accent-[#c2410c]"
              style={{ accentColor: '#c2410c' }}
            />
            <Label htmlFor="add-fee-checkbox" className="cursor-pointer font-medium">
              Προσθήκη αμοιβής στην οικονομική διαχείριση
            </Label>
          </div>
          
          {formData.case.appointment_add_to_financial && (
            <div>
              <Label htmlFor="appointment-fee">Ποσό αμοιβής (€) *</Label>
              <Input
                id="appointment-fee"
                type="number"
                step="0.01"
                data-testid="appointment-fee-input"
                value={formData.case.appointment_fee}
                onChange={(e) => updateFormData('case', 'appointment_fee', e.target.value)}
                placeholder="0.00"
                className="h-11 bg-white border-stone-300 rounded-lg items-center"
              />
              <p className="text-xs text-slate-500 mt-1">
                Η αμοιβή θα καταχωρηθεί αυτόματα στην οικονομική διαχείριση
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderClientSelection = () => (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Επιλογή πελάτη</h3>
      <div className="flex items-center gap-2 sm:gap-3" style={{ width: '100%' }}>
        <button
          data-testid="new-client-btn"
          onClick={() => setField('clientOption', 'new')}
          className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
            formData.clientOption === 'new' 
              ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
              : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
          }`}
        >
          <User className="w-4 h-4 hidden sm:block opacity-90" />
          Νέος πελάτης
        </button>
        <button
          data-testid="existing-client-btn"
          onClick={() => setField('clientOption', 'existing')}
          className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
            formData.clientOption === 'existing' 
              ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
              : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
          }`}
        >
          <User className="w-4 h-4 hidden sm:block opacity-90" />
          Καταχωρημένος πελάτης
        </button>
      </div>
    </div>
  );

  const renderClientForm = () => {
    if (formData.clientOption === 'new') {
      return (
        <div className="space-y-6" data-testid="new-client-form">
          <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Στοιχεία νέου πελάτη</h3>
          
          <div className="flex items-center gap-2 sm:gap-3 mb-6" style={{ width: '100%' }}>
            <button
              data-testid="natural-person-btn"
              onClick={() => setFormData(prev => ({ ...prev, clientType: 'natural' }))}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                formData.clientType === 'natural' 
                  ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                  : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
              }`}
            >
              <User className="w-4 h-4 hidden sm:block opacity-90" />
              Φυσικό πρόσωπο
            </button>
            <button
              data-testid="legal-person-btn"
              onClick={() => setFormData(prev => ({ ...prev, clientType: 'legal' }))}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                formData.clientType === 'legal' 
                  ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                  : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
              }`}
            >
              <Building2 className="w-4 h-4 hidden sm:block opacity-90" />
              Νομικό πρόσωπο
            </button>
          </div>

          {formData.clientType && (
            <div className="space-y-4">
              {formData.clientType === 'natural' ? (
                <>
                  <div>
                    <Label htmlFor="surname">Επώνυμο *</Label>
                    <Input
                      id="surname"
                      data-testid="surname-input"
                      value={formData.client.surname}
                      onChange={(e) => updateFormData('client', 'surname', toUpperCaseNoAccents(e.target.value))}
                      placeholder="Υποχρεωτικό πεδίο"
                      className="h-11 bg-white border-stone-300 rounded-lg items-center"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Όνομα *</Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={formData.client.name}
                      onChange={(e) => updateFormData('client', 'name', toUpperCaseNoAccents(e.target.value))}
                      placeholder="Υποχρεωτικό πεδίο"
                      className="h-11 bg-white border-stone-300 rounded-lg items-center"
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <Label htmlFor="company_name">Επωνυμία *</Label>
                  <Input
                    id="company_name"
                    data-testid="company-name-input"
                    value={formData.client.company_name}
                    onChange={(e) => updateFormData('client', 'company_name', toUpperCaseNoAccents(e.target.value))}
                    placeholder="Υποχρεωτικό πεδίο"
                    className="h-11 bg-white border-stone-300 rounded-lg items-center"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="vat">ΑΦΜ</Label>
                <Input
                  id="vat"
                  data-testid="vat-input"
                  value={formData.client.vat}
                  onChange={(e) => updateFormData('client', 'vat', e.target.value)}
                  className="h-11 bg-white border-stone-300 rounded-lg items-center"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
              <div>
                <Label htmlFor="phone">Κινητό επικοινωνίας</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  value={formData.client.phone}
                  onChange={(e) => updateFormData('client', 'phone', e.target.value)}
                  className="h-11 bg-white border-stone-300 rounded-lg items-center"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="text"
                  inputMode="email"
                  value={formData.client.email}
                  onChange={(e) => updateFormData('client', 'email', e.target.value)}
                  className="h-11 bg-white border-stone-300 rounded-lg items-center"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-form-type="other"
                />
              </div>
            </div>
          )}
        </div>
      );
    } else if (formData.clientOption === 'existing') {
      return (
        <div className="space-y-6" data-testid="existing-client-select">
          <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Επιλογή πελάτη</h3>
          <div>
            <Label htmlFor="client-select">Επιλέξτε πελάτη *</Label>
            <div className="mt-1">
              <SearchableClientSelect
                value={formData.selectedClientId || ''}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, selectedClientId: value }));
                  fetchClientCases(value);
                }}
                clients={clients}
                placeholder="Επιλέξτε πελάτη"
                className="h-11"
                testId="client-select"
                disableSearch
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCaseSelection = () => {
    if (formData.entryType !== 'court_action') return null;

    return (
      <div className="space-y-4">
        <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Επιλογή υπόθεσης</h3>
        <div className="flex items-center gap-2 sm:gap-3" style={{ width: '100%' }}>
          <button
            data-testid="new-case-btn"
            onClick={() => setField('caseOption', 'new')}
            className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              formData.caseOption === 'new' 
                ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
            }`}
          >
            <FileText className="w-4 h-4 hidden sm:block opacity-90" />
            Νέα υπόθεση
          </button>
          <button
            data-testid="existing-case-btn"
            onClick={() => setField('caseOption', 'existing')}
            className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              formData.caseOption === 'existing' 
                ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
            }`}
          >
            <FileText className="w-4 h-4 hidden sm:block opacity-90" />
            Υπάρχουσα υπόθεση
          </button>
        </div>
      </div>
    );
  };

  const renderCaseForm = () => {
    if (formData.caseOption === 'new') {
      return (
        <div className="space-y-6" data-testid="new-case-form">
          <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Στοιχεία νέας υπόθεσης</h3>
          
          <div>
            <Label>Είδος διαδικασίας *</Label>
            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
              <button
                data-testid="civil-btn"
                onClick={() => updateFormData('case', 'procedure_type', 'civil')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.case.procedure_type === 'civil' 
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Αστική
              </button>
              <button
                data-testid="criminal-btn"
                onClick={() => updateFormData('case', 'procedure_type', 'criminal')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.case.procedure_type === 'criminal' 
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Ποινική
              </button>
              <button
                data-testid="administrative-btn"
                onClick={() => updateFormData('case', 'procedure_type', 'administrative')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.case.procedure_type === 'administrative' 
                    ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                }`}
              >
                Διοικητική
              </button>
            </div>
          </div>

          {formData.case.procedure_type === 'administrative' && (
            <>
              <div>
                <Label>Η εγγραφή αφορά *</Label>
                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                  <button
                    data-testid="admin-notice-btn"
                    onClick={() => updateFormData('case', 'administrative_action_type', 'notice')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.administrative_action_type === 'notice' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Επίδοση/Γνώση πράξης
                  </button>
                  <button
                    data-testid="admin-document-btn"
                    onClick={() => updateFormData('case', 'administrative_action_type', 'document')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.administrative_action_type === 'document' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Δικόγραφο
                  </button>
                  <button
                    data-testid="admin-other-btn"
                    onClick={() => updateFormData('case', 'administrative_action_type', 'other')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.administrative_action_type === 'other' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Άλλη διαδικαστική πράξη
                  </button>
                </div>
              </div>

              {formData.case.administrative_action_type === 'notice' && (
                <>
                  <div>
                    <Label>Η εγγραφή αφορά *</Label>
                    <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                      <button
                        data-testid="admin-notice-non-tax-btn"
                        onClick={() => updateFormData('case', 'administrative_notice_type', 'non_tax')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.case.administrative_notice_type === 'non_tax' 
                            ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                            : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                        }`}
                      >
                        Μη φορολογική
                      </button>
                      <button
                        data-testid="admin-notice-tax-btn"
                        onClick={() => updateFormData('case', 'administrative_notice_type', 'tax')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.case.administrative_notice_type === 'tax' 
                            ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                            : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                        }`}
                      >
                        Φορολογική
                      </button>
                    </div>
                  </div>

                  {formData.case.administrative_notice_type === 'non_tax' && (
                    <div className="date-field-wrapper">
                      <Label htmlFor="admin-notice-date">Ημερομηνία επίδοσης ή λήψης γνώσης *</Label>
                      <LargeDatePicker id="admin-notice-date" data-testid="admin-notice-date-input" value={formData.case.administrative_notice_date} onChange={(e) => updateFormData('case', 'administrative_notice_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία άσκησης προσφυγής ή αίτησης ακύρωσης: 60 ημέρες
                      </p>
                    </div>
                  )}

                  {formData.case.administrative_notice_type === 'tax' && (
                    <div className="date-field-wrapper">
                      <Label htmlFor="admin-tax-notice-date">Ημερομηνία επίδοσης ή λήψης γνώσης *</Label>
                      <LargeDatePicker id="admin-tax-notice-date" data-testid="admin-tax-notice-date-input" value={formData.case.administrative_notice_date} onChange={(e) => updateFormData('case', 'administrative_notice_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία άσκησης προσφυγής: 30 ημέρες
                      </p>
                    </div>
                  )}
                </>
              )}

              {formData.case.administrative_action_type === 'document' && (
                <>
                  <div>
                    <Label htmlFor="admin-court-type">Αρμόδιο δικαστήριο *</Label>
                    <Select
                      value={formData.case.court_type}
                      onValueChange={(value) => updateFormData('case', 'court_type', value)}
                    >
                      <SelectTrigger data-testid="admin-court-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                        <SelectValue placeholder="Επιλέξτε δικαστήριο" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_admin_first">Μονομελές Διοικητικό Πρωτοδικείο</SelectItem>
                        <SelectItem value="triple_admin_first">Τριμελές Διοικητικό Πρωτοδικείο</SelectItem>
                        <SelectItem value="admin_appeal">Διοικητικό Εφετείο</SelectItem>
                        <SelectItem value="council_of_state">Συμβούλιο της Επικρατείας</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.case.court_type && (
                    <>
                      {!['council_of_state'].includes(formData.case.court_type) && (
                      <div>
                        <Label htmlFor="admin-court-location">Έδρα δικαστηρίου *</Label>
                        <Select
                          value={formData.case.court_location}
                          onValueChange={(value) => updateFormData('case', 'court_location', value)}
                        >
                          <SelectTrigger data-testid="admin-court-location-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε έδρα" />
                          </SelectTrigger>
                          <SelectContent>
                            {getSortedLocations('administrative', formData.case.court_type).map(location => (
                              <SelectItem key={location} value={location}>{location}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      <div>
                        <Label htmlFor="admin-document-type">Είδος δικογράφου *</Label>
                        <Select
                          value={formData.case.document_type}
                          onValueChange={(value) => updateFormData('case', 'document_type', value)}
                        >
                          <SelectTrigger data-testid="admin-document-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε είδος δικογράφου" />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.case.court_type === 'single_admin_first' && (
                              <>
                                <SelectItem value="appeal_lawsuit">Προσφυγή, αγωγή</SelectItem>
                                <SelectItem value="cancellation_request">Αίτηση ακύρωσης</SelectItem>
                              </>
                            )}
                            {formData.case.court_type === 'triple_admin_first' && (
                              <>
                                <SelectItem value="appeal">Προσφυγή</SelectItem>
                                <SelectItem value="lawsuit">Αγωγή</SelectItem>
                                <SelectItem value="cancellation_request">Αίτηση ακύρωσης</SelectItem>
                              </>
                            )}
                            {formData.case.court_type === 'admin_appeal' && (
                              <>
                                <SelectItem value="appeal_admin">Έφεση</SelectItem>
                                <SelectItem value="lawsuit">Αγωγή</SelectItem>
                                <SelectItem value="cancellation_request">Αίτηση ακύρωσης</SelectItem>
                              </>
                            )}
                            {formData.case.court_type === 'council_of_state' && (
                              <>
                                <SelectItem value="cassation_admin">Αίτηση αναίρεσης</SelectItem>
                                <SelectItem value="cancellation_request">Αίτηση ακύρωσης</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Πεδίο δικασίμου */}
                      {formData.case.document_type && (
                        <>
                          <ClientRoleSelect
                            value={formData.case.client_role}
                            options={getClientRoleOptions(formData.case.procedure_type, formData.case.document_type)}
                            onChange={(v) => updateFormData('case', 'client_role', v)}
                            testId="admin-client-role-select"
                          />
                          <div>
                            <Label htmlFor="admin-hearing-date">Ημερομηνία δικασίμου *</Label>
                            <LargeDatePicker 
                              id="admin-hearing-date" 
                              data-testid="admin-hearing-date-input" 
                              value={formData.case.hearing_date} 
                              onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} 
                            />
                          </div>

                          {/* Προθεσμίες - εμφανίζονται αμέσως μετά την επιλογή ημερομηνίας */}
                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>
                              
                              {/* 3.1 - Προσφυγή, Αγωγή, Έφεση (Εφετείο) */}
                              {['appeal_lawsuit', 'appeal', 'lawsuit', 'appeal_admin'].includes(formData.case.document_type) && (
                                <>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-prostheti-logoi-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση & Επίδοση πρόσθετων Λόγων</p>
                                      <p className="text-xs text-slate-600 mt-1">15 πλήρεις ημέρες πριν (άρθρο 131 παρ. 1 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-anakoinosi-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Ανακοίνωση δίκης</p>
                                      <p className="text-xs text-slate-600 mt-1">20 πλήρεις ημέρες πριν (άρθρο 114 παρ. 1 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-prosepiklisi-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση & Επίδοση προσεπίκλησης Αναγκαίων Ομοδίκων</p>
                                      <p className="text-xs text-slate-600 mt-1">15 πλήρεις ημέρες πριν (άρθρο 117 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-paremvasi-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση & Επίδοση παρέμβασης</p>
                                      <p className="text-xs text-slate-600 mt-1">6 πλήρεις ημέρες πριν (άρθρο 114 παρ. 2 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-eggrafa-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση εγγράφων</p>
                                      <p className="text-xs text-slate-600 mt-1">Έως την προηγούμενη ημέρα (άρθρο 150 παρ. 1 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-ypomimma-meta-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Υπόμνημα μετά τη Συζήτηση</p>
                                      <p className="text-xs text-slate-600 mt-1">3 εργάσιμες ημέρες μετά (άρθρο 138 παρ. 1 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-antikrousi-3-1"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Αντίκρουση ισχυρισμών</p>
                                      <p className="text-xs text-slate-600 mt-1">6 εργάσιμες ημέρες μετά (άρθρο 138 παρ. 1 ΚΔΔ)</p>
                                    </div>
                                  </label>
                                </>
                              )}

                              {/* 3.2 - Αίτηση ακύρωσης, Αίτηση αναίρεσης (ΣτΕ) */}
                              {['cancellation_request', 'cassation_admin'].includes(formData.case.document_type) && (
                                <>
                                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <p className="text-sm font-medium text-slate-900">Το εισαγωγικό δικόγραφο κατατέθηκε από 16.9.2024 και μετά; (ν. 5119/2024) *</p>
                                    <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                      <button
                                        type="button"
                                        data-testid="admin-32-regime-yes-btn"
                                        onClick={() => updateFormData('case', 'admin_32_new_regime', 'yes')}
                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                          formData.case.admin_32_new_regime === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                        }`}
                                      >
                                        Ναι (νέο καθεστώς)
                                      </button>
                                      <button
                                        type="button"
                                        data-testid="admin-32-regime-no-btn"
                                        onClick={() => updateFormData('case', 'admin_32_new_regime', 'no')}
                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                          formData.case.admin_32_new_regime === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                        }`}
                                      >
                                        Όχι (παλαιό καθεστώς)
                                      </button>
                                    </div>
                                  </div>
                                  {formData.case.document_type === 'cassation_admin' && (
                                    <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                      <input 
                                        type="checkbox" 
                                        id="deadline-koinopoiisi-3-2"
                                        className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                      />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-slate-900">Κοινοποίηση αίτησης Αναίρεσης σε Αναιρεσίβλητο</p>
                                        <p className="text-xs text-slate-600 mt-1">20 πλήρεις ημέρες πριν (άρθρο 21 παρ. 4 ΠΔ 18/1989)</p>
                                      </div>
                                    </label>
                                  )}
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-prostheti-logoi-3-2"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση & Επίδοση πρόσθετων Λόγων</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.admin_32_new_regime === 'yes' ? '20 πλήρεις ημέρες πριν (άρθρο 25 παρ. 1 ΠΔ 18/1989, ν. 5119/2024)' : '15 πλήρεις ημέρες πριν (άρθρο 25 παρ. 1 ΠΔ 18/1989)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-paremvasi-3-2"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση & Επίδοση παρέμβασης</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.admin_32_new_regime === 'yes' ? '15 πλήρεις ημέρες πριν (άρθρο 49 ΠΔ 18/1989, ν. 5119/2024)' : '6 πλήρεις ημέρες πριν (άρθρο 49 παρ. 2 ΠΔ 18/1989)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-ypomimma-prin-3-2"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Υπόμνημα πριν τη Συζήτηση</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.admin_32_new_regime === 'yes' ? '15 πλήρεις ημέρες πριν (άρθρο 25 παρ. 2 ΠΔ 18/1989, ν. 5119/2024)' : '6 πλήρεις ημέρες πριν (άρθρο 25 παρ. 2 ΠΔ 18/1989)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input 
                                      type="checkbox" 
                                      id="deadline-eggrafa-3-2"
                                      className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση εγγράφων</p>
                                      <p className="text-xs text-slate-600 mt-1">Έως την προηγούμενη ημέρα (ΠΔ 18/1989)</p>
                                    </div>
                                  </label>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              {formData.case.administrative_action_type === 'other' && (
                <>
                  <div>
                    <Label>Η εγγραφή αφορά *</Label>
                    <Select
                      value={formData.case.administrative_other_type}
                      onValueChange={(value) => updateFormData('case', 'administrative_other_type', value)}
                    >
                      <SelectTrigger data-testid="admin-other-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                        <SelectValue placeholder="Επιλέξτε τύπο πράξης" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="postponement">Αναβολή συζήτησης</SelectItem>
                        <SelectItem value="first_instance_service">Επίδοση οριστικής απόφασης Διοικητικού Πρωτοδικείου</SelectItem>
                        <SelectItem value="first_instance_publication">Δημοσίευση οριστικής απόφασης Διοικητικού Πρωτοδικείου</SelectItem>
                        <SelectItem value="appeal_service">Επίδοση απόφασης Διοικητικού Εφετείου</SelectItem>
                        <SelectItem value="appeal_publication">Δημοσίευση οριστικής απόφασης Διοικητικού Εφετείου</SelectItem>
                        <SelectItem value="appeal_cancellation_service">Επίδοση απόφασης Διοικητικού Εφετείου επί αιτήσεως ακύρωσης</SelectItem>
                        <SelectItem value="appeal_cancellation_publication">Δημοσίευση οριστικής απόφασης Διοικητικού Εφετείου επί αιτήσεως ακύρωσης</SelectItem>
                        <SelectItem value="final_rejection">Τελεσίδικη απόρριψη αγωγής για τυπικούς λόγους</SelectItem>
                        <SelectItem value="non_final_service">Επίδοση μη οριστικής απόφασης</SelectItem>
                        <SelectItem value="non_final_publication">Δημοσίευση μη οριστικής απόφασης</SelectItem>
                        <SelectItem value="sworn_statements">Κλήση για λήψη ενόρκων βεβαιώσεων</SelectItem>
                        <SelectItem value="ste_filing">Κατάθεση δικογράφου ΣτΕ (επίδοση εντός 2 μηνών)</SelectItem>
                        <SelectItem value="ste_appeal_service">Επίδοση ενδίκου μέσου ΣτΕ (πρόσθετοι λόγοι)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Επίδοση ενδίκου μέσου ΣτΕ — πρόσθετοι λόγοι (ν. 5119/2024) */}
                  {formData.case.administrative_other_type === 'ste_appeal_service' && (
                    <div>
                      <Label htmlFor="ste-appeal-service-date">Ημερομηνία επίδοσης ενδίκου μέσου / αποστολής φακέλου *</Label>
                      <LargeDatePicker id="ste-appeal-service-date" data-testid="ste-appeal-service-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Πρόσθετοι λόγοι: 20 ημέρες από την πάροδο τριμήνου (ν. 5119/2024) — τουλάχιστον 20 πλήρεις ημέρες πριν τη δικάσιμο
                      </p>
                    </div>
                  )}

                  {/* Κατάθεση δικογράφου ΣτΕ (ν. 5119/2024) */}
                  {formData.case.administrative_other_type === 'ste_filing' && (
                    <div>
                      <Label htmlFor="ste-filing-date">Ημερομηνία κατάθεσης *</Label>
                      <LargeDatePicker id="ste-filing-date" data-testid="ste-filing-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Επίδοση εισαγωγικού δικογράφου: 2 μήνες από την κατάθεση — επί ποινή θέσης στο αρχείο (ν. 5119/2024)
                      </p>
                    </div>
                  )}

                  {/* Επίδοση οριστικής απόφασης Πρωτοδικείου */}
                  {formData.case.administrative_other_type === 'first_instance_service' && (
                    <>
                      <div>
                        <Label htmlFor="admin-first-service-date">Ημερομηνία επίδοσης *</Label>
                        <LargeDatePicker id="admin-first-service-date" data-testid="admin-first-service-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Προθεσμία έφεσης: 60 ημέρες από επίδοση
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-first-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-first-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Δημοσίευση οριστικής απόφασης Πρωτοδικείου */}
                  {formData.case.administrative_other_type === 'first_instance_publication' && (
                    <>
                      <div>
                        <Label htmlFor="admin-first-pub-number">Αριθμός απόφασης</Label>
                        <Input
                          id="admin-first-pub-number"
                          data-testid="admin-first-pub-number-input"
                          value={formData.case.administrative_decision_number}
                          onChange={(e) => updateFormData('case', 'administrative_decision_number', e.target.value)}
                          className="h-11 bg-white border-stone-300 rounded-lg items-center"
                        />
                      </div>
                      <div>
                        <Label htmlFor="admin-first-pub-date">Ημερομηνία δημοσίευσης *</Label>
                        <LargeDatePicker id="admin-first-pub-date" data-testid="admin-first-pub-date-input" value={formData.case.administrative_publication_date} onChange={(e) => updateFormData('case', 'administrative_publication_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Καταχρηστική προθεσμία έφεσης: 3 έτη από δημοσίευση
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-first-pub-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-first-pub-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Επίδοση απόφασης Εφετείου */}
                  {formData.case.administrative_other_type === 'appeal_service' && (
                    <>
                      <div>
                        <Label htmlFor="admin-appeal-service-date">Ημερομηνία επίδοσης *</Label>
                        <LargeDatePicker id="admin-appeal-service-date" data-testid="admin-appeal-service-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Προθεσμία αναίρεσης: 60 ημέρες από επίδοση
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-appeal-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-appeal-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Δημοσίευση οριστικής απόφασης Εφετείου */}
                  {formData.case.administrative_other_type === 'appeal_publication' && (
                    <>
                      <div>
                        <Label htmlFor="admin-appeal-pub-number">Αριθμός απόφασης</Label>
                        <Input
                          id="admin-appeal-pub-number"
                          data-testid="admin-appeal-pub-number-input"
                          value={formData.case.administrative_decision_number}
                          onChange={(e) => updateFormData('case', 'administrative_decision_number', e.target.value)}
                          className="h-11 bg-white border-stone-300 rounded-lg items-center"
                        />
                      </div>
                      <div>
                        <Label htmlFor="admin-appeal-pub-date">Ημερομηνία δημοσίευσης *</Label>
                        <LargeDatePicker id="admin-appeal-pub-date" data-testid="admin-appeal-pub-date-input" value={formData.case.administrative_publication_date} onChange={(e) => updateFormData('case', 'administrative_publication_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Καταχρηστική προθεσμία αναίρεσης: 3 έτη από δημοσίευση
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-appeal-pub-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-appeal-pub-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Επίδοση Εφετείου επί αιτήσεως ακύρωσης */}
                  {formData.case.administrative_other_type === 'appeal_cancellation_service' && (
                    <>
                      <div>
                        <Label htmlFor="admin-cancel-service-date">Ημερομηνία επίδοσης *</Label>
                        <LargeDatePicker id="admin-cancel-service-date" data-testid="admin-cancel-service-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Προθεσμία έφεσης: 60 ημέρες από επίδοση (58 παρ. 3 ΣτΕ)
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-cancel-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-cancel-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Δημοσίευση Εφετείου επί αιτήσεως ακύρωσης */}
                  {formData.case.administrative_other_type === 'appeal_cancellation_publication' && (
                    <>
                      <div>
                        <Label htmlFor="admin-cancel-pub-number">Αριθμός απόφασης</Label>
                        <Input
                          id="admin-cancel-pub-number"
                          data-testid="admin-cancel-pub-number-input"
                          value={formData.case.administrative_decision_number}
                          onChange={(e) => updateFormData('case', 'administrative_decision_number', e.target.value)}
                          className="h-11 bg-white border-stone-300 rounded-lg items-center"
                        />
                      </div>
                      <div>
                        <Label htmlFor="admin-cancel-pub-date">Ημερομηνία δημοσίευσης *</Label>
                        <LargeDatePicker id="admin-cancel-pub-date" data-testid="admin-cancel-pub-date-input" value={formData.case.administrative_publication_date} onChange={(e) => updateFormData('case', 'administrative_publication_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Προθεσμία έφεσης: 1 έτος από δημοσίευση (58 παρ. 3 ΣτΕ)
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="admin-cancel-pub-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-cancel-pub-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Τελεσίδικη απόρριψη */}
                  {formData.case.administrative_other_type === 'final_rejection' && (
                    <div>
                      <Label htmlFor="admin-rejection-date">Ημερομηνία επίδοσης ή δημοσίευσης *</Label>
                      <LargeDatePicker id="admin-rejection-date" data-testid="admin-rejection-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία εκ νέου άσκησης ενδίκου βοηθήματος: 60 ημέρες από επίδοση τελεσίδικης απόφασης
                      </p>
                    </div>
                  )}

                  {/* Επίδοση μη οριστικής */}
                  {formData.case.administrative_other_type === 'non_final_service' && (
                    <>
                      <div>
                        <Label htmlFor="admin-non-final-service-date">Ημερομηνία επίδοσης *</Label>
                        <LargeDatePicker id="admin-non-final-service-date" data-testid="admin-non-final-service-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="admin-non-final-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-non-final-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Δημοσίευση μη οριστικής */}
                  {formData.case.administrative_other_type === 'non_final_publication' && (
                    <>
                      <div>
                        <Label htmlFor="admin-non-final-pub-date">Ημερομηνία δημοσίευσης *</Label>
                        <LargeDatePicker id="admin-non-final-pub-date" data-testid="admin-non-final-pub-date-input" value={formData.case.administrative_publication_date} onChange={(e) => updateFormData('case', 'administrative_publication_date', e.target.value)} />
                      </div>
                      <div>
                        <Label htmlFor="admin-non-final-pub-result">Αποτέλεσμα</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="admin-non-final-pub-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απορρίπτεται</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Κλήση για ένορκες βεβαιώσεις */}
                  {formData.case.administrative_other_type === 'sworn_statements' && (
                    <div>
                      <Label htmlFor="admin-sworn-date">Ημερομηνία γνωστοποίησης *</Label>
                      <LargeDatePicker id="admin-sworn-date" data-testid="admin-sworn-date-input" value={formData.case.administrative_service_date} onChange={(e) => updateFormData('case', 'administrative_service_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Ένορκες βεβαιώσεις: 10 ημέρες από γνωστοποίηση και το αργότερο έως την προηγούμενη της συζήτησης
                      </p>
                    </div>
                  )}

                  {/* ΠΕΔΙΟ 3 - Αναβολή συζήτησης */}
                  {formData.case.administrative_other_type === 'postponement' && (
                    <>
                      <div>
                        <Label htmlFor="admin-postponement-date">Προτεινόμενη ημερομηνία αναβολής *</Label>
                        <LargeDatePicker 
                          id="admin-postponement-date" 
                          data-testid="admin-postponement-date-input" 
                          value={formData.case.postponement_date} 
                          onChange={(e) => updateFormData('case', 'postponement_date', e.target.value)} 
                        />
                      </div>

                      {formData.case.postponement_date && (
                        <>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <Label className="text-amber-800 font-semibold">Εκκρεμότητες στην προτεινόμενη ημερομηνία</Label>
                            {loadingConflicts ? (
                              <p className="text-sm text-amber-700 mt-2">Φόρτωση εκκρεμοτήτων...</p>
                            ) : postponementConflicts.length === 0 ? (
                              <p className="text-sm text-green-700 mt-2 font-medium">✓ Δεν υπάρχουν άλλες εκκρεμότητες για αυτή την ημερομηνία</p>
                            ) : (
                              <div className="mt-2 space-y-2">
                                <p className="text-sm text-amber-700 font-medium">
                                  {postponementConflicts.length} εκκρεμότητ{postponementConflicts.length === 1 ? 'α' : 'ες'} για {formData.case.postponement_date}:
                                </p>
                                <ul className="space-y-1 max-h-32 overflow-y-auto">
                                  {postponementConflicts.map((conflict, index) => (
                                    <li key={conflict._id || index} className="text-sm text-amber-800 bg-amber-100 px-2 py-1 rounded">
                                      • {conflict.title}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          <div>
                            <Label>Η ημερομηνία είναι αποδεκτή; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="postponement-accepted-btn"
                                onClick={() => updateFormData('case', 'postponement_accepted', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.postponement_accepted === 'yes' 
                                    ? 'bg-green-600 text-white' 
                                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Αποδεκτή
                              </button>
                              <button
                                data-testid="postponement-rejected-btn"
                                onClick={() => updateFormData('case', 'postponement_accepted', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.postponement_accepted === 'no' 
                                    ? 'bg-red-600 text-white' 
                                    : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Άλλη ημερομηνία
                              </button>
                            </div>
                          </div>

                          {formData.case.postponement_accepted === 'no' && (
                            <>
                              <div>
                                <Label htmlFor="admin-postponement-alt-date">Νέα προτεινόμενη ημερομηνία *</Label>
                                <LargeDatePicker 
                                  id="admin-postponement-alt-date" 
                                  data-testid="admin-postponement-alt-date-input" 
                                  value={formData.case.postponement_alt_date} 
                                  onChange={(e) => updateFormData('case', 'postponement_alt_date', e.target.value)} 
                                />
                              </div>
                              
                              {formData.case.postponement_alt_date && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                  <Label className="text-amber-800 font-semibold">Εκκρεμότητες στη νέα ημερομηνία</Label>
                                  {loadingConflicts ? (
                                    <p className="text-sm text-amber-700 mt-2">Φόρτωση εκκρεμοτήτων...</p>
                                  ) : postponementConflicts.length === 0 ? (
                                    <p className="text-sm text-green-700 mt-2 font-medium">✓ Δεν υπάρχουν άλλες εκκρεμότητες για αυτή την ημερομηνία</p>
                                  ) : (
                                    <div className="mt-2 space-y-2">
                                      <p className="text-sm text-amber-700 font-medium">
                                        {postponementConflicts.length} εκκρεμότητ{postponementConflicts.length === 1 ? 'α' : 'ες'} για {formData.case.postponement_alt_date}:
                                      </p>
                                      <ul className="space-y-1 max-h-32 overflow-y-auto">
                                        {postponementConflicts.map((conflict, index) => (
                                          <li key={conflict._id || index} className="text-sm text-amber-800 bg-amber-100 px-2 py-1 rounded">
                                            • {conflict.title}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}

                          {(formData.case.postponement_accepted === 'yes' || formData.case.postponement_alt_date) && (
                            <div>
                              <Label>Η αναβολή αφορά νέα υπόθεση; *</Label>
                              <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                <button
                                  data-testid="postponement-new-case-btn"
                                  onClick={() => updateFormData('case', 'postponement_is_new_case', 'yes')}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.case.postponement_is_new_case === 'yes' 
                                      ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                                      : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                  }`}
                                >
                                  Ναι - Νέα υπόθεση
                                </button>
                                <button
                                  data-testid="postponement-existing-case-btn"
                                  onClick={() => updateFormData('case', 'postponement_is_new_case', 'no')}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.case.postponement_is_new_case === 'no' 
                                      ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                                      : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                  }`}
                                >
                                  Όχι - Υπάρχουσα υπόθεση
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Επιλογή υπάρχουσας υπόθεσης */}
                          {formData.case.postponement_is_new_case === 'no' && (
                            <div>
                              <Label>Επιλέξτε υπόθεση *</Label>
                              <Select
                                value={formData.case.postponement_selected_case_id}
                                onValueChange={(value) => updateFormData('case', 'postponement_selected_case_id', value)}
                              >
                                <SelectTrigger className="mt-2" data-testid="postponement-case-select">
                                  <SelectValue placeholder="Επιλέξτε υπόθεση..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {cases.map(caseItem => (
                                    <SelectItem key={caseItem._id} value={caseItem._id}>
                                      {getCourtTypeLabel(caseItem.court_type)} {caseItem.court_location} - {caseItem.document_type_display || caseItem.document_type}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {formData.case.procedure_type === 'criminal' && (
            <>
              <ClientRoleSelect
                value={formData.case.client_role}
                options={getClientRoleOptions('criminal', formData.case.document_type)}
                onChange={(v) => updateFormData('case', 'client_role', v)}
                testId="criminal-client-role-select"
              />
              <div>
                <Label>Η νέα εγγραφή αφορά πράξη της προδικασίας *</Label>
                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                  <button
                    data-testid="preliminary-yes-btn"
                    onClick={() => updateFormData('case', 'is_preliminary', 'yes')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.is_preliminary === 'yes' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Ναι
                  </button>
                  <button
                    data-testid="preliminary-no-btn"
                    onClick={() => updateFormData('case', 'is_preliminary', 'no')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.is_preliminary === 'no' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Όχι
                  </button>
                </div>
              </div>

              {formData.case.is_preliminary === 'yes' && (
                <div>
                  <Label htmlFor="preliminary-date">Ημερομηνία επίδοσης βουλεύματος *</Label>
                  <LargeDatePicker id="preliminary-date" data-testid="preliminary-date-input" value={formData.case.preliminary_date} onChange={(e) => updateFormData('case', 'preliminary_date', e.target.value)} />
                  <p className="text-sm text-slate-600 mt-1">
                    Προθεσμία άσκησης έφεσης: 10 ημέρες από την επίδοση
                  </p>
                </div>
              )}

              {formData.case.is_preliminary === 'no' && (
                <>
                  <div>
                    <Label>Είδος πράξης *</Label>
                    <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                      <button
                        data-testid="criminal-summons-btn"
                        onClick={() => updateFormData('case', 'criminal_action_type', 'summons')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.case.criminal_action_type === 'summons' 
                            ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                            : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                        }`}
                      >
                        Επίδοση κλήτευσης
                      </button>
                      <button
                        data-testid="criminal-other-btn"
                        onClick={() => updateFormData('case', 'criminal_action_type', 'other')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          formData.case.criminal_action_type === 'other' 
                            ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                            : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                        }`}
                      >
                        Άλλη διαδικαστική πράξη
                      </button>
                    </div>
                  </div>

                  {formData.case.criminal_action_type === 'summons' && (
                    <>
                      <div>
                        <Label htmlFor="criminal-court-type">Αρμόδιο δικαστήριο *</Label>
                        <Select
                          value={formData.case.court_type}
                          onValueChange={(value) => updateFormData('case', 'court_type', value)}
                        >
                          <SelectTrigger data-testid="criminal-court-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε δικαστήριο" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single_criminal">Μονομελές Πλημμελειοδικείο</SelectItem>
                            <SelectItem value="three_criminal">Τριμελές Πλημμελειοδικείο</SelectItem>
                            <SelectItem value="single_criminal_appeal">Μονομελές Εφετείο Κακουργημάτων</SelectItem>
                            <SelectItem value="triple_criminal_appeal">Τριμελές Εφετείο Κακουργημάτων</SelectItem>
                            <SelectItem value="five_criminal_appeal">Πενταμελές Εφετείο</SelectItem>
                            <SelectItem value="mixed_court">Μικτό Ορκωτό Δικαστήριο</SelectItem>
                            <SelectItem value="mixed_jury">Μικτό Ορκωτό</SelectItem>
                            <SelectItem value="appeal_criminal">Εφετείο Κακουργημάτων</SelectItem>
                            <SelectItem value="supreme_criminal">Άρειος Πάγος (Ποινικό)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.case.court_type && (
                        <>
                          {formData.case.court_type !== 'supreme_criminal' && (
                          <div>
                            <Label htmlFor="criminal-court-location">Έδρα δικαστηρίου *</Label>
                            <Select
                              value={formData.case.court_location}
                              onValueChange={(value) => updateFormData('case', 'court_location', value)}
                            >
                              <SelectTrigger data-testid="criminal-court-location-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                                <SelectValue placeholder="Επιλέξτε έδρα" />
                              </SelectTrigger>
                              <SelectContent>
                                {getSortedLocations('criminal', formData.case.court_type).map(location => (
                                  <SelectItem key={location} value={location}>{location}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          )}

                          <div>
                            <Label>Τύπος κλήτευσης *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="direct-summons-yes-btn"
                                onClick={() => updateFormData('case', 'direct_summons', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.direct_summons === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                <p className="font-medium text-slate-900">Απευθείας κλήση</p>
                                <p className="text-xs text-slate-600 mt-1">Προθεσμία προσφυγής: 10 ημέρες (322 ΚΠΔ)</p>
                              </button>
                              <button
                                data-testid="direct-summons-no-btn"
                                onClick={() => updateFormData('case', 'direct_summons', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.direct_summons === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                <p className="font-medium text-slate-900">Δικάσιμος</p>
                              </button>
                            </div>
                          </div>

                          {formData.case.direct_summons === 'yes' && (
                            <>
                              <div className="space-y-4">
                                <Label>Ημερομηνία επίδοσης κλητηρίου θεσπίσματος *</Label>
                                <InlineDatePicker
                                  id="criminal-direct-summons-service-date"
                                  data-testid="criminal-direct-summons-service-date-input"
                                  value={formData.case.criminal_service_date}
                                  onChange={(e) => updateFormData('case', 'criminal_service_date', e.target.value)}
                                />
                                <p className="text-xs text-slate-600">Προσφυγή κατά κλητηρίου: 10 ημέρες από επίδοση (άρθρο 322 ΚΠΔ)</p>
                              </div>

                              <div className="space-y-4">
                                <Label>Ημερομηνία δικασίμου (προαιρετικό)</Label>
                                <InlineDatePicker
                                  id="criminal-direct-summons-hearing-date"
                                  data-testid="criminal-direct-summons-hearing-date-input"
                                  value={formData.case.hearing_date}
                                  onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)}
                                />
                              </div>
                            </>
                          )}

                          {formData.case.direct_summons === 'no' && (
                            <>
                              <div className="space-y-4">
                                <Label>Ημερομηνία δικασίμου *</Label>
                                <InlineDatePicker 
                                  id="criminal-trial-date" 
                                  data-testid="criminal-trial-date-input" 
                                  value={formData.case.hearing_date} 
                                  onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} 
                                />
                              </div>

                              {formData.case.hearing_date && (
                                <div>
                                  <Label htmlFor="hearing-status-criminal">Κατάσταση δικασίμου *</Label>
                                  <Select
                                    value={formData.case.hearing_status}
                                    onValueChange={(value) => updateFormData('case', 'hearing_status', value)}
                                  >
                                    <SelectTrigger data-testid="hearing-status-criminal-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                                      <SelectValue placeholder="Επιλέξτε κατάσταση" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="ekdikasi">Εκδίκαση</SelectItem>
                                      <SelectItem value="anavoli">Αναβολή</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}

                              {/* Προθεσμίες για Άρειο Πάγο */}
                              {formData.case.court_type === 'supreme_criminal' && formData.case.hearing_date && formData.case.hearing_status && (
                                <div className="bg-stone-50/80 border border-stone-200 rounded-xl p-4 space-y-3">
                                  <Label className="text-base font-semibold">Προθεσμίες</Label>
                                  <div className="flex items-center space-x-2">
                                    <input 
                                      type="checkbox" 
                                      id="criminal-prostheti-logoi"
                                      className="h-4 w-4 rounded border-stone-300"
                                    />
                                    <label htmlFor="criminal-prostheti-logoi" className="text-sm">
                                      Κατάθεση πρόσθετων λόγων αναίρεσης (6 πλήρεις ημέρες πριν - 509 ΚΠΔ)
                                    </label>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {formData.case.criminal_action_type === 'other' && (
                    <>
                      <div>
                        <Label>Η εγγραφή αφορά *</Label>
                        <Select
                          value={formData.case.criminal_other_type}
                          onValueChange={(value) => updateFormData('case', 'criminal_other_type', value)}
                        >
                          <SelectTrigger data-testid="criminal-other-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε τύπο πράξης" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="postponement">Αναβολή συζήτησης</SelectItem>
                            <SelectItem value="decision">Έκδοση ή επίδοση απόφασης ποινικού δικαστηρίου</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Αναβολή συζήτησης */}
                      {formData.case.criminal_other_type === 'postponement' && (
                        <div>
                          <Label htmlFor="criminal-postponement-date">Νέα ημερομηνία δικασίμου *</Label>
                          <LargeDatePicker id="criminal-postponement-date" data-testid="criminal-postponement-date-input" value={formData.case.criminal_other_date} onChange={(e) => updateFormData('case', 'criminal_other_date', e.target.value)} />
                          <p className="text-sm text-slate-600 mt-1">
                            Οι προθεσμίες επαναϋπολογίζονται με βάση τη νέα δικάσιμο
                          </p>
                        </div>
                      )}

                      {/* Έκδοση ή επίδοση απόφασης */}
                      {formData.case.criminal_other_type === 'decision' && (
                        <>
                          <div>
                            <Label>Επίπεδο απόφασης *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="criminal-first-instance-btn"
                                onClick={() => updateFormData('case', 'criminal_decision_level', 'first')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.criminal_decision_level === 'first' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                <p className="font-medium text-slate-900">Απόφαση πρωτόδικου δικαστηρίου</p>
                              </button>
                              <button
                                data-testid="criminal-second-instance-btn"
                                onClick={() => updateFormData('case', 'criminal_decision_level', 'second')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.criminal_decision_level === 'second' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                <p className="font-medium text-slate-900">Απόφαση δευτεροβάθμιου δικαστηρίου</p>
                              </button>
                            </div>
                          </div>

                          {/* Πρωτόδικο δικαστήριο */}
                          {formData.case.criminal_decision_level === 'first' && (
                            <>
                              <div>
                                <Label>Ο κατηγορούμενος παραστάθηκε ή εκπροσωπήθηκε; *</Label>
                                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                  <button
                                    data-testid="criminal-present-yes-btn"
                                    onClick={() => updateFormData('case', 'criminal_was_present', 'yes')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.criminal_was_present === 'yes' 
                                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Ναι
                                  </button>
                                  <button
                                    data-testid="criminal-present-no-btn"
                                    onClick={() => updateFormData('case', 'criminal_was_present', 'no')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.criminal_was_present === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Όχι
                                  </button>
                                </div>
                              </div>

                              {formData.case.criminal_was_present === 'yes' && (
                                <div>
                                  <Label htmlFor="criminal-hearing-date">Ημερομηνία δικασίμου που εκδόθηκε η απόφαση *</Label>
                                  <LargeDatePicker id="criminal-hearing-date" data-testid="criminal-hearing-date-input" value={formData.case.criminal_hearing_date} onChange={(e) => updateFormData('case', 'criminal_hearing_date', e.target.value)} />
                                  <div className="text-sm text-slate-600 mt-2 space-y-1">
                                    <p>• Έφεση: 10 ημέρες από συζήτηση</p>
                                    <p>• Αίτηση ακύρωσης διαδικασίας: 15 ημέρες από έκδοση</p>
                                  </div>
                                </div>
                              )}

                              {formData.case.criminal_was_present === 'no' && (
                                <>
                                  <div>
                                    <Label htmlFor="criminal-decision-publication-date">Ημερομηνία δημοσίευσης *</Label>
                                    <LargeDatePicker id="criminal-decision-publication-date" data-testid="criminal-decision-publication-date-input" value={formData.case.criminal_decision_date} onChange={(e) => updateFormData('case', 'criminal_decision_date', e.target.value)} />
                                    <p className="text-sm text-slate-600 mt-1">
                                      Αίτηση ακύρωσης: 15 ημέρες από δημοσίευση
                                    </p>
                                  </div>
                                  <div>
                                    <Label htmlFor="criminal-service-date">Ημερομηνία επίδοσης απόφασης *</Label>
                                    <LargeDatePicker id="criminal-service-date" data-testid="criminal-service-date-input" value={formData.case.criminal_service_date} onChange={(e) => updateFormData('case', 'criminal_service_date', e.target.value)} />
                                    <p className="text-sm text-slate-600 mt-1">
                                      Έφεση: 10 ημέρες από επίδοση
                                    </p>
                                  </div>
                                </>
                              )}
                            </>
                          )}

                          {/* Δευτεροβάθμιο δικαστήριο */}
                          {formData.case.criminal_decision_level === 'second' && (
                            <>
                              <div>
                                <Label>Ο κατηγορούμενος παραστάθηκε ή εκπροσωπήθηκε; *</Label>
                                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                  <button
                                    data-testid="criminal-second-present-yes-btn"
                                    onClick={() => updateFormData('case', 'criminal_was_present', 'yes')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.criminal_was_present === 'yes' 
                                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Ναι
                                  </button>
                                  <button
                                    data-testid="criminal-second-present-no-btn"
                                    onClick={() => updateFormData('case', 'criminal_was_present', 'no')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.criminal_was_present === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Όχι
                                  </button>
                                </div>
                              </div>

                              {formData.case.criminal_was_present === 'yes' && (
                                <div>
                                  <Label htmlFor="criminal-clean-copy-date">Ημερομηνία καθαρογραφής *</Label>
                                  <LargeDatePicker id="criminal-clean-copy-date" data-testid="criminal-clean-copy-date-input" value={formData.case.criminal_clean_copy_date} onChange={(e) => updateFormData('case', 'criminal_clean_copy_date', e.target.value)} />
                                  <p className="text-sm text-slate-600 mt-1">
                                    Προθεσμία αναίρεσης: 20 ημέρες από καθαρογραφή
                                  </p>
                                </div>
                              )}

                              {formData.case.criminal_was_present === 'no' && (
                                <>
                                  <div>
                                    <Label htmlFor="criminal-second-decision-date">Ημερομηνία έκδοσης *</Label>
                                    <LargeDatePicker id="criminal-second-decision-date" data-testid="criminal-second-decision-date-input" value={formData.case.criminal_decision_date} onChange={(e) => updateFormData('case', 'criminal_decision_date', e.target.value)} />
                                    <p className="text-sm text-slate-600 mt-1">
                                      Αίτηση ακύρωσης διαδικασίας: 15 ημέρες από έκδοση
                                    </p>
                                  </div>
                                  <div>
                                    <Label htmlFor="criminal-second-service-date">Ημερομηνία επίδοσης (προαιρετικό)</Label>
                                    <LargeDatePicker id="criminal-second-service-date" data-testid="criminal-second-service-date-input" value={formData.case.criminal_service_date} onChange={(e) => updateFormData('case', 'criminal_service_date', e.target.value)} />
                                    <p className="text-sm text-slate-600 mt-1">
                                      Άσκηση Αναίρεσης: 20 ημέρες από επίδοση (άρθρο 507 παρ. 1 ΚΠΔ)
                                    </p>
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {formData.case.procedure_type === 'civil' && (
            <>
              <div>
                <Label>Είδος πράξης *</Label>
                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                  <button
                    data-testid="document-btn"
                    onClick={() => updateFormData('case', 'action_type', 'document')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.action_type === 'document' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Δικόγραφο
                  </button>
                  <button
                    data-testid="other-action-btn"
                    onClick={() => updateFormData('case', 'action_type', 'other')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      formData.case.action_type === 'other' 
                        ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' 
                        : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                    }`}
                  >
                    Άλλη διαδικαστική πράξη
                  </button>
                </div>
              </div>

              {formData.case.procedure_type === 'civil' && formData.case.action_type === 'other' && (
                <>
                  <div>
                    <Label>Η εγγραφή αφορά *</Label>
                    <Select
                      value={formData.case.civil_other_type}
                      onValueChange={(value) => updateFormData('case', 'civil_other_type', value)}
                    >
                      <SelectTrigger data-testid="civil-other-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                        <SelectValue placeholder="Επιλέξτε τύπο πράξης" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hearing_date">Ορισμός ημερομηνίας δικασίμου (άρθρο 237 ΚΠολΔ)</SelectItem>
                        <SelectItem value="postponement">Αναβολή συζήτησης</SelectItem>
                        <SelectItem value="sworn_statement">Λήψη ένορκης βεβαίωσης</SelectItem>
                        <SelectItem value="mediation_session">Συνεδρία διαμεσολάβησης</SelectItem>
                        <SelectItem value="cancellation">Ματαίωση συζήτησης</SelectItem>
                        <SelectItem value="withdrawal">Παραίτηση από δικόγραφο</SelectItem>
                        <SelectItem value="final_decision_service">Επίδοση οριστικής απόφασης</SelectItem>
                        <SelectItem value="final_decision_publication">Δημοσίευση οριστικής απόφασης</SelectItem>
                        <SelectItem value="final_rejection">Τελεσίδικη απόρριψη αγωγής για τυπικούς λόγους</SelectItem>
                        <SelectItem value="payment_order_issue">Έκδοση διαταγής πληρωμής</SelectItem>
                        <SelectItem value="payment_order_service">Επίδοση διαταγής πληρωμής ή απόδοσης μισθίου</SelectItem>
                        <SelectItem value="katataxi_prosklisi">Επίδοση πρόσκλησης για πίνακα κατάταξης</SelectItem>
                        <SelectItem value="diataxi_prodikastiki">Έκδοση Διάταξης προδικαστικού ελέγχου (237 ΚΠολΔ)</SelectItem>
                        <SelectItem value="enforcement_action">Πράξη αναγκαστικής εκτέλεσης</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Ορισμός ημερομηνίας δικασίμου */}
                  {formData.case.civil_other_type === 'hearing_date' && (
                    <div>
                      <Label htmlFor="hearing-date-237">Ημερομηνία δικασίμου *</Label>
                      <LargeDatePicker id="hearing-date-237" data-testid="hearing-date-237-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Κατάθεση αποδεικτικού επίδοσης κλήσης: 15 ημέρες πριν τη δικάσιμο
                      </p>
                    </div>
                  )}

                  {/* Αναβολή συζήτησης */}
                  {formData.case.civil_other_type === 'postponement' && (
                    <div>
                      <Label htmlFor="postponement-date">Νέα ημερομηνία δικασίμου *</Label>
                      <LargeDatePicker id="postponement-date" data-testid="postponement-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Οι προθεσμίες επαναϋπολογίζονται με βάση τη νέα δικάσιμο
                      </p>
                    </div>
                  )}

                  {/* Λήψη ένορκης βεβαίωσης */}
                  {formData.case.civil_other_type === 'sworn_statement' && (
                    <div>
                      <Label htmlFor="sworn-statement-date">Ημερομηνία λήψης ένορκης βεβαίωσης *</Label>
                      <LargeDatePicker id="sworn-statement-date" data-testid="sworn-statement-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Κλήτευση αντιδίκου: 2 εργάσιμες ημέρες πριν — θα δημιουργηθεί και εγγραφή «Γνωστοποίηση λήψης ένορκης βεβαίωσης» (άρθρα 421-422 ΚΠολΔ)
                      </p>
                    </div>
                  )}

                  {/* Συνεδρία διαμεσολάβησης */}
                  {formData.case.civil_other_type === 'mediation_session' && (
                    <div>
                      <Label htmlFor="mediation-session-date">Ημερομηνία συνεδρίας διαμεσολάβησης *</Label>
                      <LargeDatePicker id="mediation-session-date" data-testid="mediation-session-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Η ημερομηνία που όρισε ο διαμεσολαβητής
                      </p>
                    </div>
                  )}

                  {/* Ματαίωση συζήτησης */}
                  {formData.case.civil_other_type === 'cancellation' && (
                    <div>
                      <Label htmlFor="cancellation-date">Ημερομηνία ματαίωσης *</Label>
                      <LargeDatePicker id="cancellation-date" data-testid="cancellation-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία επαναφοράς με κλήση: 60 ημέρες από ματαίωση
                      </p>
                    </div>
                  )}

                  {/* Παραίτηση από δικόγραφο */}
                  {formData.case.civil_other_type === 'withdrawal' && (
                    <div>
                      <Label htmlFor="withdrawal-date">Ημερομηνία παραίτησης *</Label>
                      <LargeDatePicker id="withdrawal-date" data-testid="withdrawal-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία εκ νέου άσκησης αγωγής: 6 μήνες από παραίτηση
                      </p>
                    </div>
                  )}

                  {/* Επίδοση οριστικής απόφασης */}
                  {formData.case.civil_other_type === 'final_decision_service' && (
                    <>
                      <div>
                        <Label>Η απόφαση εκδόθηκε από πρωτοβάθμιο δικαστήριο; *</Label>
                        <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                          <button
                            data-testid="first-instance-yes-btn"
                            onClick={() => updateFormData('case', 'is_first_instance', 'yes')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              formData.case.is_first_instance === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                            }`}
                          >
                            Ναι
                          </button>
                          <button
                            data-testid="first-instance-no-btn"
                            onClick={() => updateFormData('case', 'is_first_instance', 'no')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              formData.case.is_first_instance === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                            }`}
                          >
                            Όχι
                          </button>
                        </div>
                      </div>

                      {formData.case.is_first_instance === 'yes' && (
                        <>
                          <div>
                            <Label>Η απόφαση αφορά μικροδιαφορά; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="small-claims-yes-btn"
                                onClick={() => updateFormData('case', 'is_small_claims', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_small_claims === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="small-claims-no-btn"
                                onClick={() => updateFormData('case', 'is_small_claims', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_small_claims === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          {formData.case.is_small_claims === 'yes' && (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800 font-medium">
                                Δεν προβλέπεται η άσκηση έφεσης για μικροδιαφορές
                              </p>
                            </div>
                          )}

                          {formData.case.is_small_claims === 'no' && (
                            <>
                              <div>
                                <Label>Η απόφαση εκδόθηκε ερήμην; *</Label>
                                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                  <button
                                    data-testid="default-yes-btn"
                                    onClick={() => updateFormData('case', 'is_default_judgment', 'yes')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.is_default_judgment === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Ναι
                                  </button>
                                  <button
                                    data-testid="default-no-btn"
                                    onClick={() => updateFormData('case', 'is_default_judgment', 'no')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.is_default_judgment === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Όχι
                                  </button>
                                </div>
                              </div>

                              {formData.case.is_default_judgment === 'yes' && (
                                <div>
                                  <Label htmlFor="decision-service-date">Ημερομηνία επίδοσης *</Label>
                                  <LargeDatePicker id="decision-service-date" data-testid="decision-service-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                                  <p className="text-sm text-slate-600 mt-1">
                                    Προθεσμία έφεσης και ανακοπής ερημοδικίας: 30 ημέρες από επίδοση
                                  </p>
                                </div>
                              )}

                              {formData.case.is_default_judgment === 'no' && (
                                <div>
                                  <Label htmlFor="decision-service-date-2">Ημερομηνία επίδοσης *</Label>
                                  <LargeDatePicker id="decision-service-date-2" data-testid="decision-service-date-2-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                                  <p className="text-sm text-slate-600 mt-1">
                                    Προθεσμία έφεσης: 30 ημέρες από επίδοση
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {formData.case.is_first_instance === 'no' && (
                        <>
                          <div>
                            <Label>Η απόφαση είναι τελεσίδικη; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="final-yes-btn"
                                onClick={() => updateFormData('case', 'is_final', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_final === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="final-no-btn"
                                onClick={() => updateFormData('case', 'is_final', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_final === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          {formData.case.is_final === 'yes' && (
                            <div>
                              <Label htmlFor="cassation-service-date">Ημερομηνία επίδοσης *</Label>
                              <LargeDatePicker id="cassation-service-date" data-testid="cassation-service-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία αίτησης αναίρεσης: 30 ημέρες από επίδοση (90 για κάτοικο εξωτερικού/αγνώστου)
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {(formData.case.civil_other_date || formData.case.is_small_claims === 'yes') && (
                        <div>
                          <Label htmlFor="decision-result">Αποτέλεσμα απόφασης</Label>
                          <Select
                            value={formData.case.decision_result}
                            onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                          >
                            <SelectTrigger data-testid="decision-result-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                              <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="accepted">Δεκτή</SelectItem>
                              <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                              <SelectItem value="rejected">Απόρριψη</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}

                  {/* Δημοσίευση οριστικής απόφασης */}
                  {formData.case.civil_other_type === 'final_decision_publication' && (
                    <>
                      <div>
                        <Label htmlFor="publication-date">Ημερομηνία δημοσίευσης *</Label>
                        <LargeDatePicker id="publication-date" data-testid="publication-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                        <p className="text-sm text-slate-600 mt-1">
                          Καταχρηστική προθεσμία έφεσης/αναίρεσης: 1 έτος (δημοσίευση από 1.1.2026) ή 2 έτη (δημοσίευση έως 31.12.2025) — αυτόματη επιλογή βάσει ημερομηνίας
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="decision-result-pub">Αποτέλεσμα απόφασης</Label>
                        <Select
                          value={formData.case.decision_result}
                          onValueChange={(value) => updateFormData('case', 'decision_result', value)}
                        >
                          <SelectTrigger data-testid="decision-result-pub-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε αποτέλεσμα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accepted">Δεκτή</SelectItem>
                            <SelectItem value="partially_accepted">Εν μέρει δεκτή</SelectItem>
                            <SelectItem value="rejected">Απόρριψη</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {/* Τελεσίδικη απόρριψη */}
                  {formData.case.civil_other_type === 'final_rejection' && (
                    <div>
                      <Label htmlFor="rejection-date">Ημερομηνία έκδοσης απόφασης *</Label>
                      <LargeDatePicker id="rejection-date" data-testid="rejection-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία εκ νέου άσκησης αγωγής: 6 μήνες από δημοσίευση τελεσίδικης απόφασης
                      </p>
                    </div>
                  )}

                  {/* Έκδοση διαταγής πληρωμής */}
                  {formData.case.civil_other_type === 'payment_order_issue' && (
                    <div>
                      <Label htmlFor="payment-order-issue-date">Ημερομηνία έκδοσης *</Label>
                      <LargeDatePicker id="payment-order-issue-date" data-testid="payment-order-issue-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία επίδοσης: 2 μήνες από την έκδοση
                      </p>
                    </div>
                  )}

                  {/* Επίδοση πρόσκλησης για πίνακα κατάταξης */}
                  {formData.case.civil_other_type === 'katataxi_prosklisi' && (
                    <div>
                      <Label htmlFor="katataxi-prosklisi-date">Ημερομηνία επίδοσης πρόσκλησης *</Label>
                      <LargeDatePicker id="katataxi-prosklisi-date" data-testid="katataxi-prosklisi-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία ανακοπής κατά πίνακα κατάταξης: 12 εργάσιμες ημέρες από την επίδοση (άρθρο 979 παρ. 2 ΚΠολΔ)
                      </p>
                    </div>
                  )}

                  {/* Έκδοση Διάταξης προδικαστικού ελέγχου */}
                  {formData.case.civil_other_type === 'diataxi_prodikastiki' && (
                    <div>
                      <Label htmlFor="diataxi-prodikastiki-date">Ημερομηνία έκδοσης Διάταξης *</Label>
                      <LargeDatePicker id="diataxi-prodikastiki-date" data-testid="diataxi-prodikastiki-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                      <p className="text-sm text-slate-600 mt-1">
                        Προθεσμία αντίλεξης του ενάγοντος κατά της Διάταξης: 5 ημέρες από την έκδοση (άρθρο 237 ΚΠολΔ, ν. 5221/2025)
                      </p>
                    </div>
                  )}

                  {/* Επίδοση διαταγής πληρωμής ή απόδοσης μισθίου */}
                  {formData.case.civil_other_type === 'payment_order_service' && (
                    <>
                      <div>
                        <Label>Τύπος επίδοσης *</Label>
                        <Select
                          value={formData.case.payment_order_service_type}
                          onValueChange={(value) => updateFormData('case', 'payment_order_service_type', value)}
                        >
                          <SelectTrigger data-testid="payment-order-service-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε τύπο" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="payment_first">Διαταγή πληρωμής - Πρώτη επίδοση</SelectItem>
                            <SelectItem value="payment_second">Διαταγή πληρωμής - Δεύτερη επίδοση</SelectItem>
                            <SelectItem value="rent">Διαταγή απόδοσης μισθίου</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.case.payment_order_service_type === 'payment_first' && (
                        <div>
                          <Label htmlFor="payment-first-service-date">Ημερομηνία επίδοσης *</Label>
                          <LargeDatePicker id="payment-first-service-date" data-testid="payment-first-service-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                          <p className="text-sm text-slate-600 mt-1">
                            Προθεσμία ανακοπής: 15 εργάσιμες ημέρες από επίδοση
                          </p>
                        </div>
                      )}

                      {formData.case.payment_order_service_type === 'payment_second' && (
                        <div>
                          <Label htmlFor="payment-second-service-date">Ημερομηνία δεύτερης επίδοσης *</Label>
                          <LargeDatePicker id="payment-second-service-date" data-testid="payment-second-service-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                          <p className="text-sm text-slate-600 mt-1">
                            Προθεσμία ανακοπής: 10 εργάσιμες ημέρες από επίδοση
                          </p>
                        </div>
                      )}

                      {formData.case.payment_order_service_type === 'rent' && (
                        <div>
                          <Label htmlFor="rent-order-service-date">Ημερομηνία επίδοσης *</Label>
                          <LargeDatePicker id="rent-order-service-date" data-testid="rent-order-service-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                          <p className="text-sm text-slate-600 mt-1">
                            Προθεσμία ανακοπής: 15 εργάσιμες ημέρες από επίδοση
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Πράξη αναγκαστικής εκτέλεσης */}
                  {formData.case.civil_other_type === 'enforcement_action' && (
                    <>
                      <div>
                        <Label>Ένδικο βοήθημα *</Label>
                        <Select
                          value={formData.case.enforcement_remedy}
                          onValueChange={(value) => updateFormData('case', 'enforcement_remedy', value)}
                        >
                          <SelectTrigger data-testid="enforcement-remedy-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε ένδικο βοήθημα" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="article_933">Ανακοπή άρθρου 933 ΚΠολΔ</SelectItem>
                            <SelectItem value="article_954">Ανακοπή άρθρου 954 ΚΠολΔ</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.case.enforcement_remedy === 'article_933' && (
                        <>
                          <div>
                            <Label>Η ανακοπή αφορά *</Label>
                            <Select
                              value={formData.case.enforcement_article}
                              onValueChange={(value) => updateFormData('case', 'enforcement_article', value)}
                            >
                              <SelectTrigger data-testid="enforcement-article-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                                <SelectValue placeholder="Επιλέξτε" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="934a_seizure">934α - Κατάσχεση</SelectItem>
                                <SelectItem value="934a_immediate">934α - Άμεση εκτέλεση</SelectItem>
                                <SelectItem value="934b_last_action">934β - Τελευταία πράξη</SelectItem>
                                <SelectItem value="934b_movables">934β - Πλειστηριασμός κινητών</SelectItem>
                                <SelectItem value="934b_immovables">934β - Πλειστηριασμός ακινήτων</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {formData.case.enforcement_article === '934a_seizure' && (
                            <div>
                              <Label htmlFor="seizure-date">Ημερομηνία κατάσχεσης *</Label>
                              <LargeDatePicker id="seizure-date" data-testid="seizure-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία: 45 ημέρες από κατάσχεση
                              </p>
                            </div>
                          )}

                          {formData.case.enforcement_article === '934a_immediate' && (
                            <div>
                              <Label htmlFor="immediate-date">Ημερομηνία επίδοσης επιταγής *</Label>
                              <LargeDatePicker id="immediate-date" data-testid="immediate-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία: 30 ημέρες από επίδοση επιταγής
                              </p>
                            </div>
                          )}

                          {formData.case.enforcement_article === '934b_last_action' && (
                            <div>
                              <Label htmlFor="last-action-date">Ημερομηνία τελευταίας πράξης *</Label>
                              <LargeDatePicker id="last-action-date" data-testid="last-action-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία: 30 ημέρες από την τελευταία πράξη
                              </p>
                            </div>
                          )}

                          {formData.case.enforcement_article === '934b_movables' && (
                            <div>
                              <Label htmlFor="movables-auction-date">Ημερομηνία πλειστηριασμού *</Label>
                              <LargeDatePicker id="movables-auction-date" data-testid="movables-auction-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία: 30 ημέρες από τον πλειστηριασμό
                              </p>
                            </div>
                          )}

                          {formData.case.enforcement_article === '934b_immovables' && (
                            <div>
                              <Label htmlFor="immovables-registry-date">Ημερομηνία μεταγραφής κατασχετήριας έκθεσης *</Label>
                              <LargeDatePicker id="immovables-registry-date" data-testid="immovables-registry-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                              <p className="text-sm text-slate-600 mt-1">
                                Προθεσμία: 60 ημέρες από μεταγραφή
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {formData.case.enforcement_remedy === 'article_954' && (
                        <>
                          <div>
                            <Label>Η επιταγή προς εκτέλεση επιδόθηκε πριν την 1.1.2026; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2">
                              {[{ v: 'yes', l: 'Ναι (παλαιό: 15 εργάσιμες)' }, { v: 'no', l: 'Όχι (νέο: 30 εργάσιμες)' }].map(({ v, l }) => (
                                <button
                                  key={v}
                                  type="button"
                                  data-testid={`enf-954-regime-${v}-btn`}
                                  onClick={() => updateFormData('case', 'enf_954_old_regime', v)}
                                  className={`flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                                    (formData.case.enf_954_old_regime || 'no') === v ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                  }`}
                                >
                                  {l}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="article-954-date">Ημερομηνία πλειστηριασμού *</Label>
                            <LargeDatePicker id="article-954-date" data-testid="article-954-date-input" value={formData.case.civil_other_date} onChange={(e) => updateFormData('case', 'civil_other_date', e.target.value)} />
                            <p className="text-sm text-slate-600 mt-1">
                              {formData.case.enf_954_old_regime === 'yes'
                                ? 'Προθεσμία κατάθεσης: έως 15 εργάσιμες ημέρες πριν τον πλειστηριασμό (προ ν. 5221/2025)'
                                : 'Προθεσμία κατάθεσης: έως 30 εργάσιμες ημέρες πριν τον πλειστηριασμό (ν. 5221/2025)'}
                            </p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {formData.case.action_type === 'document' && (
                <>
                  <div>
                    <Label htmlFor="court-type">Αρμόδιο δικαστήριο *</Label>
                    <Select
                      value={formData.case.court_type}
                      onValueChange={(value) => updateFormData('case', 'court_type', value)}
                    >
                      <SelectTrigger data-testid="court-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                        <SelectValue placeholder="Επιλέξτε δικαστήριο" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single_first">Μονομελές Πρωτοδικείο</SelectItem>
                        <SelectItem value="multi_first">Πολυμελές Πρωτοδικείο</SelectItem>
                        <SelectItem value="single_appeal">Μονομελές Εφετείο</SelectItem>
                        <SelectItem value="triple_appeal">Τριμελές Εφετείο</SelectItem>
                        <SelectItem value="supreme">Άρειος Πάγος</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.case.court_type && (
                    <>
                      {formData.case.court_type !== 'supreme' && (
                      <div>
                        <Label htmlFor="court-location">Έδρα δικαστηρίου *</Label>
                        <Select
                          value={formData.case.court_location}
                          onValueChange={(value) => updateFormData('case', 'court_location', value)}
                        >
                          <SelectTrigger data-testid="court-location-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε έδρα" />
                          </SelectTrigger>
                          <SelectContent>
                            {getSortedLocations('civil', formData.case.court_type).map(location => (
                              <SelectItem key={location} value={location}>{location}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      <div>
                        <Label htmlFor="document-type">Είδος δικογράφου *</Label>
                        <Select
                          value={formData.case.document_type}
                          onValueChange={(value) => updateFormData('case', 'document_type', value)}
                        >
                          <SelectTrigger data-testid="document-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                            <SelectValue placeholder="Επιλέξτε είδος δικογράφου" />
                          </SelectTrigger>
                          <SelectContent>
                            {getDocumentTypes(formData.case.court_type).map(doc => (
                              <SelectItem key={doc.value} value={doc.value}>{doc.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Στοιχεία υπόθεσης - εμφανίζεται μετά την επιλογή δικογράφου */}
                      {formData.case.document_type && (
                        <div className="mt-6 p-4 bg-stone-50 rounded-lg border border-stone-200">
                          <h4 className="text-md font-semibold text-slate-900 mb-4">Στοιχεία υπόθεσης</h4>
                          
                          <div className="space-y-4">
                            <ClientRoleSelect
                              value={formData.case.client_role}
                              options={getClientRoleOptions(formData.case.procedure_type, formData.case.document_type)}
                              onChange={(v) => updateFormData('case', 'client_role', v)}
                              testId="client-role-select"
                            />
                            <div>
                              <Label htmlFor="opponent-details">Στοιχεία αντιδίκου</Label>
                              <Input
                                id="opponent-details"
                                data-testid="opponent-details-input"
                                placeholder="π.χ. Ονοματεπώνυμο, ΑΦΜ, Διεύθυνση"
                                value={formData.case.opponent_details}
                                onChange={(e) => updateFormData('case', 'opponent_details', toUpperCaseNoAccents(e.target.value))}
                                className="h-11 bg-white border-stone-300 rounded-lg items-center"
                                autoComplete="off"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="gak">ΓΑΚ (Γενικός αριθμός κατάθεσης)</Label>
                              <Input
                                id="gak"
                                data-testid="gak-input"
                                placeholder="π.χ. 12345/2025"
                                value={formData.case.gak || ''}
                                onChange={(e) => updateFormData('case', 'gak', e.target.value)}
                                className="h-11 bg-white border-stone-300 rounded-lg items-center"
                                autoComplete="off"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="case-description-type">Περιγραφή υπόθεσης</Label>
                              <Select
                                value={formData.case.case_description_type}
                                onValueChange={(value) => updateFormData('case', 'case_description_type', value)}
                              >
                                <SelectTrigger data-testid="case-description-type-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                                  <SelectValue placeholder="Επιλέξτε είδος υπόθεσης" />
                                </SelectTrigger>
                                <SelectContent>
                                  {caseDescriptionTypes.map(desc => (
                                    <SelectItem key={desc.value} value={desc.value}>{desc.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Χειρόγραφη καταχώρηση αν επιλεγεί "Άλλο" */}
                            {formData.case.case_description_type === 'other' && (
                              <div>
                                <Label htmlFor="case-description-custom">Περιγραφή (χειρόγραφα)</Label>
                                <Input
                                  id="case-description-custom"
                                  data-testid="case-description-custom-input"
                                  placeholder="Εισάγετε περιγραφή υπόθεσης"
                                  value={formData.case.case_description_custom}
                                  onChange={(e) => updateFormData('case', 'case_description_custom', e.target.value)}
                                  className="h-11 bg-white border-stone-300 rounded-lg items-center"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Αγωγή τακτική διαδικασία - Detailed fields */}
                      {formData.case.document_type === 'lawsuit' && (
                        <>
                          <div>
                            <Label htmlFor="lawsuit-filing-date">Ημερομηνία κατάθεσης αγωγής *</Label>
                            <LargeDatePicker id="lawsuit-filing-date" data-testid="lawsuit-filing-date-input" value={formData.case.lawsuit_filing_date} onChange={(e) => updateFormData('case', 'lawsuit_filing_date', e.target.value)} />
                          </div>

                          <div>
                            <Label htmlFor="lawsuit-hearing-date">Δικάσιμος (προαιρετικό — χειροκίνητη καταχώρηση)</Label>
                            <LargeDatePicker id="lawsuit-hearing-date" data-testid="lawsuit-hearing-date-input" value={formData.case.lawsuit_hearing_date} onChange={(e) => updateFormData('case', 'lawsuit_hearing_date', e.target.value)} />
                            <p className="text-xs text-slate-500 mt-1">Αν έχει ήδη οριστεί δικάσιμος, συμπληρώστε την εδώ για να δημιουργηθεί αυτόματη εγγραφή.</p>
                          </div>

                          <div>
                            <Label>Υπάρχει εναγόμενος κάτοικος εξωτερικού ή αγνώστου διαμονής; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="foreign-defendant-yes-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="foreign-defendant-no-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          <div>
                            <Label>Η αγωγή είναι εμπράγματη; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="real-property-yes-btn"
                                onClick={() => updateFormData('case', 'is_real_property', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_real_property === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="real-property-no-btn"
                                onClick={() => updateFormData('case', 'is_real_property', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.is_real_property === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          {formData.case.lawsuit_filing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>
                              
                              {formData.case.is_real_property === 'yes' && (
                                <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                  <input
                                    type="checkbox"
                                    data-testid="deadline-registry-checkbox"
                                    checked={selectedDeadlines.includes('eggrafi_ktimatologio')}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDeadlines([...selectedDeadlines, 'eggrafi_ktimatologio']);
                                      } else {
                                        setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'eggrafi_ktimatologio'));
                                      }
                                    }}
                                    className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">Εγγραφή στο Κτηματολόγιο</p>
                                    <p className="text-xs text-slate-600 mt-1">30 ημέρες από κατάθεση αγωγής</p>
                                  </div>
                                </label>
                              )}

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-service-checkbox"
                                  checked={selectedDeadlines.includes('epidosi_agogis')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDeadlines([...selectedDeadlines, 'epidosi_agogis']);
                                    } else {
                                      setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'epidosi_agogis'));
                                    }
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία επίδοσης αγωγής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από κατάθεση (άρθρο 215 παρ. 2 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-proposals-checkbox"
                                  checked={selectedDeadlines.includes('katathesi_protaseon')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDeadlines([...selectedDeadlines, 'katathesi_protaseon']);
                                    } else {
                                      setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'katathesi_protaseon'));
                                    }
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '120' : '90'} ημέρες από επίδοση αγωγής (άρθρο 237 παρ. 1 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-addition-checkbox"
                                  checked={selectedDeadlines.includes('prosthiki_antikrousi')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDeadlines([...selectedDeadlines, 'prosthiki_antikrousi']);
                                    } else {
                                      setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'prosthiki_antikrousi'));
                                    }
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία κατάθεσης προσθήκης/αντίκρουσης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    15 ημέρες από συμπλήρωση προθεσμίας προτάσεων, ώρα 12:00 μμ (άρθρο 237 παρ. 2 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-counterclaim-checkbox"
                                  checked={selectedDeadlines.includes('antagogi_paremvasi')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDeadlines([...selectedDeadlines, 'antagogi_paremvasi']);
                                    } else {
                                      setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'antagogi_paremvasi'));
                                    }
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία κατάθεσης/επίδοσης ανταγωγής, παρέμβασης, προσεπίκλησης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '90' : '60'} ημέρες από κατάθεση αγωγής (άρθρο 238 παρ. 1 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-intervention-checkbox"
                                  checked={selectedDeadlines.includes('paremvasi_meta_anakoinosi')}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedDeadlines([...selectedDeadlines, 'paremvasi_meta_anakoinosi']);
                                    } else {
                                      setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'paremvasi_meta_anakoinosi'));
                                    }
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία παρέμβασης μετά από ανακοίνωση/προσεπίκληση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    90 ημέρες από κατάθεση αγωγής (άρθρο 238 παρ. 1 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              {/* Deadline Preview */}
                              <DeadlinePreview
                                templateId="agogi_237"
                                baseDate={formData.case.lawsuit_filing_date}
                                selectedDeadlineIds={selectedDeadlines}
                                options={{
                                  foreign_resident: formData.case.has_foreign_defendant === 'yes',
                                  is_public_entity: formData.case.is_public_entity === true, mediation_required: formData.case.mediation_required === true,
                                  is_real_property: formData.case.is_real_property === 'yes'
                                }}
                                visible={selectedDeadlines.length > 0}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Αγωγή Μικροδιαφορών (Single Peace, Court Type single_peace με small_claims) */}
                      {formData.case.document_type === 'small_claims' && (
                        <>
                          <div>
                            <Label htmlFor="smallclaims-filing-date">Ημερομηνία κατάθεσης αγωγής μικροδιαφορών *</Label>
                            <LargeDatePicker
                              id="smallclaims-filing-date"
                              data-testid="smallclaims-filing-date-input"
                              value={formData.case.lawsuit_filing_date}
                              onChange={(e) => updateFormData('case', 'lawsuit_filing_date', e.target.value)}
                            />
                            <p className="text-xs text-slate-600 mt-1">
                              Άρθρο 468 ΚΠολΔ — διαδικασία μικροδιαφορών
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="smallclaims-hearing-date">Δικάσιμος (προαιρετικό — χειροκίνητη καταχώρηση)</Label>
                            <LargeDatePicker
                              id="smallclaims-hearing-date"
                              data-testid="smallclaims-hearing-date-input"
                              value={formData.case.lawsuit_hearing_date}
                              onChange={(e) => updateFormData('case', 'lawsuit_hearing_date', e.target.value)}
                            />
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                            testidPrefix="smallclaims-public-entity"
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          {formData.case.lawsuit_filing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Όλες οι προθεσμίες υπολογίζονται από την κατάθεση της αγωγής</p>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-smallclaims-service-checkbox"
                                  checked={selectedDeadlines.includes('epidosi_mikrodif')}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedDeadlines([...selectedDeadlines, 'epidosi_mikrodif']);
                                    else setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'epidosi_mikrodif'));
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση αγωγής</p>
                                  <p className="text-xs text-slate-600 mt-1">10 ημέρες από κατάθεση (άρθρο 468 παρ. 1 ΚΠολΔ)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-smallclaims-proposals-checkbox"
                                  checked={selectedDeadlines.includes('protaseis_mikrodif')}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedDeadlines([...selectedDeadlines, 'protaseis_mikrodif']);
                                    else setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'protaseis_mikrodif'));
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">30 ημέρες από κατάθεση (άρθρο 468 παρ. 2 ΚΠολΔ)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-smallclaims-addition-checkbox"
                                  checked={selectedDeadlines.includes('prosthiki_mikrodif')}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedDeadlines([...selectedDeadlines, 'prosthiki_mikrodif']);
                                    else setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'prosthiki_mikrodif'));
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προσθήκης/αντίκρουσης</p>
                                  <p className="text-xs text-slate-600 mt-1">35 ημέρες από κατάθεση (άρθρο 468 παρ. 2 ΚΠολΔ)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-smallclaims-counterclaim-checkbox"
                                  checked={selectedDeadlines.includes('antagogi_paremvasi_mikrodif')}
                                  onChange={(e) => {
                                    if (e.target.checked) setSelectedDeadlines([...selectedDeadlines, 'antagogi_paremvasi_mikrodif']);
                                    else setSelectedDeadlines(selectedDeadlines.filter(d => d !== 'antagogi_paremvasi_mikrodif'));
                                  }}
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Ανταγωγή, Παρέμβαση κλπ</p>
                                  <p className="text-xs text-slate-600 mt-1">20 ημέρες από κατάθεση (άρθρο 468 παρ. 3 ΚΠολΔ)</p>
                                </div>
                              </label>

                              <DeadlinePreview
                                templateId="mikrodifores_katathesi"
                                baseDate={formData.case.lawsuit_filing_date}
                                selectedDeadlineIds={selectedDeadlines}
                                options={{ is_public_entity: formData.case.is_public_entity === true }}
                                visible={selectedDeadlines.length > 0}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {/* Αγωγή ειδικής διαδικασίας - Δικάσιμος */}
                      {formData.case.document_type === 'special_lawsuit' && (
                        <>
                          <div className="space-y-4">
                            <Label>Ημερομηνία δικασίμου *</Label>
                            <InlineDatePicker 
                              id="hearing-date" 
                              data-testid="hearing-date-input" 
                              value={formData.case.hearing_date} 
                              onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} 
                            />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>
                              
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-service-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση αγωγής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    30 ημέρες πριν τη δικάσιμο (άρθρο 591 παρ. 1 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-proposals-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Έως τη δικάσιμο (άρθρο 591 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-addition-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    5 ημέρες από τη δικάσιμο, ώρα 12:00 μμ (άρθρο 591 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-counterclaim-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Ανταγωγή</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    8 ημέρες από τη δικάσιμο (άρθρο 591 παρ. 1ζ ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-intervention-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Παρέμβαση, Προσεπίκληση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    10 ημέρες πριν τη δικάσιμο (άρθρο 591 παρ. 1β ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-special-intervention-after-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Παρέμβαση μετά από ανακοίνωση/προσεπίκληση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    5 ημέρες πριν τη δικάσιμο (άρθρο 591 παρ. 1β ΚΠολΔ)
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Αναψηλάφηση - Δικάσιμος */}
                      {formData.case.document_type === 'revision' && (
                        <>
                          <div>
                            <Label htmlFor="hearing-date-revision">Ημερομηνία δικασίμου *</Label>
                            <LargeDatePicker id="hearing-date-revision" data-testid="hearing-date-revision-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Η εγγραφή της δικασίμου θα δημιουργηθεί αυτόματα</p>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-revision-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση αναψηλάφησης</p>
                                  <p className="text-xs text-slate-600 mt-1">30 πλήρεις ημέρες πριν τη δικάσιμο (άρθρο 498 παρ. 2 ΚΠολΔ, εξαιρείται Αύγουστος)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-revision-service-foreign-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση αναψηλάφησης (εξωτερικό / αγνώστου διαμονής)</p>
                                  <p className="text-xs text-slate-600 mt-1">60 ημέρες πριν τη δικάσιμο (άρθρο 498 παρ. 2 ΚΠολΔ, εξαιρείται Αύγουστος)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-revision-additional-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση πρόσθετων λόγων</p>
                                  <p className="text-xs text-slate-600 mt-1">30 ημέρες πριν τη δικάσιμο (άρθρο 520 παρ. 2 ΚΠολΔ, εξαιρείται Αύγουστος)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-revision-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">Στη δικάσιμο (άρθρο 544 ΚΠολΔ)</p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-revision-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προσθήκης - αντίκρουσης</p>
                                  <p className="text-xs text-slate-600 mt-1">3 εργάσιμες ημέρες μετά τη δικάσιμο, ώρα 12:00 (άρθρο 524 παρ. 2 ΚΠολΔ)</p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Τριτανακοπή */}
                      {formData.case.document_type === 'third_party_obj' && (
                        <>
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <Label className="text-sm font-medium text-slate-900">Διαδικασία εκδίκασης *</Label>
                            <p className="text-xs text-slate-600 mt-1">Η τριτανακοπή εκδικάζεται κατά τη διαδικασία με την οποία εκδόθηκε η προσβαλλόμενη απόφαση (άρθρο 587 ΚΠολΔ)</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                              <button
                                type="button"
                                data-testid="trit-ordinary-btn"
                                onClick={() => updateFormData('case', 'trit_diadikasia', 'ordinary')}
                                className={`px-4 py-3 rounded-md text-sm font-medium transition-all border ${
                                  formData.case.trit_diadikasia === 'ordinary'
                                    ? 'bg-red-700 text-white border-red-700'
                                    : 'bg-white text-slate-700 border-stone-300 hover:border-stone-400'
                                }`}
                              >
                                Τακτική διαδικασία
                                <span className="block text-xs font-normal mt-0.5 opacity-90">(υπολογισμός από κατάθεση)</span>
                              </button>
                              <button
                                type="button"
                                data-testid="trit-special-btn"
                                onClick={() => updateFormData('case', 'trit_diadikasia', 'special')}
                                className={`px-4 py-3 rounded-md text-sm font-medium transition-all border ${
                                  formData.case.trit_diadikasia === 'special'
                                    ? 'bg-red-700 text-white border-red-700'
                                    : 'bg-white text-slate-700 border-stone-300 hover:border-stone-400'
                                }`}
                              >
                                Ειδική διαδικασία
                                <span className="block text-xs font-normal mt-0.5 opacity-90">(υπολογισμός από δικάσιμο)</span>
                              </button>
                            </div>
                          </div>

                          {formData.case.trit_diadikasia === 'ordinary' && (
                            <>
                              <div>
                                <Label htmlFor="trit-filing-date">Ημερομηνία κατάθεσης τριτανακοπής *</Label>
                                <LargeDatePicker id="trit-filing-date" data-testid="trit-filing-date-input" value={formData.case.trit_filing_date} onChange={(e) => updateFormData('case', 'trit_filing_date', e.target.value)} />
                              </div>

                              <div>
                                <Label>Ο καθ' ου διαμένει στο εξωτερικό ή είναι αγνώστου διαμονής; *</Label>
                                <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                  <button
                                    data-testid="trit-foreign-yes-btn"
                                    onClick={() => updateFormData('case', 'has_foreign_defendant', 'yes')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.has_foreign_defendant === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Ναι
                                  </button>
                                  <button
                                    data-testid="trit-foreign-no-btn"
                                    onClick={() => updateFormData('case', 'has_foreign_defendant', 'no')}
                                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                      formData.case.has_foreign_defendant === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                    }`}
                                  >
                                    Όχι
                                  </button>
                                </div>
                              </div>

                              <PublicEntityToggle
                                value={formData.case.is_public_entity}
                                onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                                testidPrefix="trit-public-entity"
                              />

                              <MediationToggle
                                value={formData.case.mediation_required}
                                onChange={(v) => updateFormData('case', 'mediation_required', v)}
                              />

                              {formData.case.trit_filing_date && formData.case.has_foreign_defendant && (
                                <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                                  <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση τριτανακοπής</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από την κατάθεση (άρθρο 215 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.has_foreign_defendant === 'yes' ? '120' : '90'} ημέρες από την κατάθεση (άρθρο 237 παρ. 1 ΚΠολΔ)</p>
                                    </div>
                                  </label>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                      <p className="text-xs text-slate-600 mt-1">15 ημέρες από τη λήξη της προθεσμίας προτάσεων (άρθρο 237 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                </div>
                              )}
                            </>
                          )}

                          {formData.case.trit_diadikasia === 'special' && (
                            <>
                              <div>
                                <Label htmlFor="hearing-date-trit">Ημερομηνία δικασίμου *</Label>
                                <LargeDatePicker id="hearing-date-trit" data-testid="hearing-date-trit-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                              </div>

                              {formData.case.hearing_date && (
                                <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                                  <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                                  <p className="text-xs text-slate-600 mb-3">Η εγγραφή της δικασίμου θα δημιουργηθεί αυτόματα</p>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-special-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση τριτανακοπής</p>
                                      <p className="text-xs text-slate-600 mt-1">30 ημέρες πριν τη δικάσιμο (άρθρο 591 παρ. 1 ΚΠολΔ)</p>
                                    </div>
                                  </label>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-special-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">Έως τη δικάσιμο (άρθρο 591 ΚΠολΔ)</p>
                                    </div>
                                  </label>

                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="deadline-trit-special-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                      <p className="text-xs text-slate-600 mt-1">5 εργάσιμες ημέρες από τη δικάσιμο, ώρα 12:00 (άρθρο 591 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {/* Εκουσία Δικαιοδοσία - Δικάσιμος */}
                      {formData.case.document_type === 'voluntary_app' && (
                        <>
                          <div className="space-y-4">
                            <Label>Ημερομηνία δικασίμου *</Label>
                            <InlineDatePicker 
                              id="hearing-date-voluntary" 
                              data-testid="hearing-date-voluntary-input" 
                              value={formData.case.hearing_date} 
                              onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} 
                            />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4">
                              <Label htmlFor="voluntary-service-days" className="text-sm font-medium text-slate-900">Προθεσμία επίδοσης (ημέρες πριν τη δικάσιμο)</Label>
                              <p className="text-xs text-slate-600 mt-1 mb-2">
                                Συμπληρώστε ποια είναι η προθεσμία επίδοσης που ορίστηκε στο δικόγραφο (π.χ. 10 = 10 ημέρες πριν)
                              </p>
                              <Input
                                id="voluntary-service-days"
                                data-testid="voluntary-service-days-input"
                                type="number"
                                min="0"
                                placeholder="π.χ. 10"
                                value={formData.case.custom_voluntary_service_days}
                                onChange={(e) => updateFormData('case', 'custom_voluntary_service_days', e.target.value)}
                                className="h-10 bg-white border-stone-300"
                              />
                            </div>
                          )}

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-voluntary-proposals-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Έως τη δικάσιμο (Εκουσία)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-voluntary-addition-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    5 εργάσιμες ημέρες από τη δικάσιμο, ώρα 12:00 μμ (Εκουσία)
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ασφαλιστικά Μέτρα - Δικάσιμος */}
                      {formData.case.document_type === 'interim_app' && (
                        <>
                          <div className="space-y-4">
                            <Label>Ημερομηνία δικασίμου *</Label>
                            <InlineDatePicker 
                              id="hearing-date-interim" 
                              data-testid="hearing-date-interim-input" 
                              value={formData.case.hearing_date} 
                              onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} 
                            />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4">
                              <Label htmlFor="interim-service-days" className="text-sm font-medium text-slate-900">Προθεσμία επίδοσης (ημέρες πριν τη δικάσιμο)</Label>
                              <p className="text-xs text-slate-600 mt-1 mb-2">
                                Συμπληρώστε ποια είναι η προθεσμία επίδοσης που ορίστηκε στο δικόγραφο (π.χ. 5 = 5 ημέρες πριν)
                              </p>
                              <Input
                                id="interim-service-days"
                                data-testid="interim-service-days-input"
                                type="number"
                                min="0"
                                placeholder="π.χ. 5"
                                value={formData.case.custom_interim_service_days}
                                onChange={(e) => updateFormData('case', 'custom_interim_service_days', e.target.value)}
                                className="h-10 bg-white border-stone-300"
                              />
                            </div>
                          )}

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-interim-memo-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση σημειώματος</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Στη δικάσιμο ή εντός προθεσμίας που οριστεί (Ασφαλιστικά)
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ομάδα Β: Mode selector για 5 doc types (objection_dp / appeal / objection_default / cassation / objection_933) */}
                      {['objection_dp', 'appeal', 'objection_default', 'cassation', 'objection_933', 'rent_objection', 'objection_954'].includes(formData.case.document_type) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <Label className="text-sm font-medium text-slate-900">Τύπος υπολογισμού *</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                            <button
                              type="button"
                              data-testid="calc-mode-deadline-btn"
                              onClick={() => updateFormData('case', 'calculation_mode', 'deadline_calc')}
                              className={`px-4 py-3 rounded-md text-sm font-medium transition-all border ${
                                formData.case.calculation_mode === 'deadline_calc'
                                  ? 'bg-red-700 text-white border-red-700'
                                  : 'bg-white text-slate-700 border-stone-300 hover:border-stone-400'
                              }`}
                            >
                              Υπολογισμός προθεσμίας
                              <span className="block text-xs font-normal mt-0.5 opacity-90">
                                (πριν την άσκηση)
                              </span>
                            </button>
                            <button
                              type="button"
                              data-testid="calc-mode-filed-btn"
                              onClick={() => updateFormData('case', 'calculation_mode', 'filed')}
                              className={`px-4 py-3 rounded-md text-sm font-medium transition-all border ${
                                formData.case.calculation_mode === 'filed'
                                  ? 'bg-red-700 text-white border-red-700'
                                  : 'bg-white text-slate-700 border-stone-300 hover:border-stone-400'
                              }`}
                            >
                              Υπολογισμός για ασκηθέν δικόγραφο
                              <span className="block text-xs font-normal mt-0.5 opacity-90">
                                (έχει ήδη ασκηθεί)
                              </span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Ομάδα Β: Mode='filed' - Δικάσιμος + Προθεσμίες για ασκηθέν δικόγραφο */}
                      {['objection_dp', 'appeal', 'objection_default', 'cassation', 'objection_933'].includes(formData.case.document_type) && formData.case.calculation_mode === 'filed' && (
                        <>
                          <div>
                            <Label htmlFor="filed-hearing-date">Ημερομηνία δικασίμου *</Label>
                            <LargeDatePicker id="filed-hearing-date" data-testid="filed-hearing-date-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Σύμφωνα με το είδος δικογράφου - οι προθεσμίες θα υπολογιστούν με βάση τη δικάσιμο</p>

                              {/* Έφεση */}
                              {formData.case.document_type === 'appeal' && (
                                <>
                                  <div className="mb-3">
                                    <Label>Η έφεση κατατέθηκε πριν την 1.1.2026; *</Label>
                                    <div className="flex items-center gap-2 sm:gap-3 mt-2">
                                      {[{ v: 'yes', l: 'Ναι (παλαιό καθεστώς)' }, { v: 'no', l: 'Όχι (ν. 5221/2025)' }].map(({ v, l }) => (
                                        <button
                                          key={v}
                                          type="button"
                                          data-testid={`filed-appeal-regime-${v}-btn`}
                                          onClick={() => updateFormData('case', 'filed_appeal_old_regime', v)}
                                          className={`flex-1 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                                            (formData.case.filed_appeal_old_regime || 'no') === v ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                          }`}
                                        >
                                          {l}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  {formData.case.filed_appeal_old_regime !== 'yes' && (
                                    <div className="mb-3">
                                      <Label htmlFor="efesi-filing-date">Ημερομηνία κατάθεσης έφεσης *</Label>
                                      <LargeDatePicker id="efesi-filing-date" data-testid="efesi-filing-date-input" value={formData.case.efesi_filing_date} onChange={(e) => updateFormData('case', 'efesi_filing_date', e.target.value)} />
                                      <p className="text-xs text-slate-600 mt-1">Απαιτείται — οι προθεσμίες επίδοσης/πρόσθετων λόγων/αντέφεσης υπολογίζονται πλέον από την κατάθεση</p>
                                    </div>
                                  )}
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-appeal-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση έφεσης</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.filed_appeal_old_regime === 'yes' ? '30 ημέρες πριν τη δικάσιμο (60 αν εξωτερικό/αγν.διαμονής - 498 παρ. 2 ΚΠολΔ)' : '30 ημέρες από την κατάθεση (495 παρ. 2 ΚΠολΔ, ν. 5221/2025)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-appeal-additional-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Πρόσθετοι λόγοι</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.filed_appeal_old_regime === 'yes' ? '30 ημέρες πριν τη δικάσιμο (520 παρ. 2 ΚΠολΔ)' : '40 ημέρες από τη λήξη προθεσμίας επίδοσης — σύνολο 70 από κατάθεση (520 παρ. 2 ΚΠολΔ, ν. 5221/2025)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-appeal-counter-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Αντέφεση</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.filed_appeal_old_regime === 'yes' ? '30 ημέρες πριν τη δικάσιμο (523 παρ. 2 ΚΠολΔ)' : '40 ημέρες από τη λήξη προθεσμίας επίδοσης — σύνολο 70 από κατάθεση (523 ΚΠολΔ, ν. 5221/2025)'}</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-appeal-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">Δικάσιμος</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-appeal-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                      <p className="text-xs text-slate-600 mt-1">{formData.case.filed_appeal_old_regime === 'yes' ? '3 εργάσιμες μετά τη δικάσιμο, ώρα 12:00 (524 παρ. 2 ΚΠολΔ)' : '5 εργάσιμες μετά τη δικάσιμο, ώρα 12:00 (524 ΚΠολΔ, ν. 5221/2025)'}</p>
                                    </div>
                                  </label>
                                </>
                              )}

                              {/* Ανακοπή ερημοδικίας */}
                              {formData.case.document_type === 'objection_default' && (
                                <>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-erimodik-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση ανακοπής Ερημοδικίας</p>
                                      <p className="text-xs text-slate-600 mt-1">30 ημέρες πριν τη δικάσιμο (60 αν εξωτερικό - 498 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-erimodik-additional-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Πρόσθετοι λόγοι</p>
                                      <p className="text-xs text-slate-600 mt-1">30 ημέρες πριν τη δικάσιμο (520 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-erimodik-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">Δικάσιμος</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-erimodik-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                      <p className="text-xs text-slate-600 mt-1">3 εργάσιμες μετά τη δικάσιμο, ώρα 12:00 (524 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                </>
                              )}

                              {/* Αναίρεση */}
                              {formData.case.document_type === 'cassation' && (
                                <>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-cassation-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση αίτησης Αναίρεσης</p>
                                      <p className="text-xs text-slate-600 mt-1">60 πλήρεις ημέρες πριν τη δικάσιμο (90 αν εξωτερικό - 568 παρ. 4 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-cassation-additional-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Πρόσθετοι λόγοι Αναίρεσης</p>
                                      <p className="text-xs text-slate-600 mt-1">30 πλήρεις ημέρες πριν τη δικάσιμο (569 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-cassation-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">20 πλήρεις ημέρες πριν τη δικάσιμο (570 παρ. 1 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-cassation-memo-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση υπομνήματος</p>
                                      <p className="text-xs text-slate-600 mt-1">3 εργάσιμες μετά τη δικάσιμο, ώρα 12:00 (570 παρ. 1 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                </>
                              )}

                              {/* Ανακοπή 933 */}
                              {formData.case.document_type === 'objection_933' && (
                                <>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-933-service-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Επίδοση ανακοπής</p>
                                      <p className="text-xs text-slate-600 mt-1">20 ημέρες πριν τη δικάσιμο (933 παρ. 2 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-933-additional-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Πρόσθετοι λόγοι</p>
                                      <p className="text-xs text-slate-600 mt-1">8 ημέρες πριν τη δικάσιμο (933 παρ. 1 ΚΠολΔ)</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-933-proposals-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                      <p className="text-xs text-slate-600 mt-1">Έως τη συζήτηση</p>
                                    </div>
                                  </label>
                                  <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                    <input type="checkbox" data-testid="filed-933-addition-checkbox" className="mt-1 h-5 w-5 cursor-pointer accent-red-700" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                      <p className="text-xs text-slate-600 mt-1">5 ημέρες από τη δικάσιμο, ώρα 12:00</p>
                                    </div>
                                  </label>
                                </>
                              )}

                              {/* Ανακοπή κατά ΔΠ - ειδική διαδικασία */}
                              {formData.case.document_type === 'objection_dp' && (
                                <p className="text-xs text-slate-600 italic">
                                  Για ασκηθείσα ανακοπή κατά ΔΠ, οι προθεσμίες της ειδικής διαδικασίας υπολογίζονται με βάση τη δικάσιμο. Η εγγραφή της δικασίμου θα δημιουργηθεί αυτόματα.
                                </p>
                              )}

                              {/* Ανακοπή ΔΑΠΜ / 954 - μόνο εγγραφή δικασίμου */}
                              {(formData.case.document_type === 'rent_objection' || formData.case.document_type === 'objection_954') && (
                                <p className="text-xs text-slate-600 italic" data-testid="filed-info-note">
                                  Για ασκηθείσα ανακοπή, θα δημιουργηθεί αυτόματα η εγγραφή της δικασίμου.
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Έφεση - Δικάσιμος */}
                      {(formData.case.document_type === 'appeal_single_first' || formData.case.document_type === 'appeal_peace' || (formData.case.document_type === 'appeal' && formData.case.calculation_mode === 'deadline_calc')) && (
                        <>
                          <div>
                            <Label htmlFor="hearing-date-appeal">Ημερομηνία δικασίμου *</Label>
                            <LargeDatePicker id="hearing-date-appeal" data-testid="hearing-date-appeal-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                          </div>

                          {formData.case.hearing_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Επιλέξτε προθεσμίες προς Καταχώρηση</h4>
                              <p className="text-xs text-slate-600 mb-3">Επιλέξτε τις προθεσμίες που θέλετε να καταχωρηθούν στο ημερολόγιο</p>
                              
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-service-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση έφεσης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    30 ημέρες πριν τη δικάσιμο (άρθρο 518 παρ. 1 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-proposals-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Έως τη δικάσιμο (άρθρο 518 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-addition-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προσθήκη - Αντίκρουση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    5 ημέρες από τη δικάσιμο, ώρα 12:00 μμ (άρθρο 518 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-objection-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Αντίρρηση κατά παραδεκτού έφεσης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    10 ημέρες πριν τη δικάσιμο (άρθρο 519 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-prosthetoi-logoi-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Πρόσθετοι λόγοι έφεσης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Κατάθεση και επίδοση 30 ημέρες πριν τη δικάσιμο (άρθρο 520 παρ. 2 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-appeal-antefesi-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Αντέφεση</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    Κατάθεση και επίδοση 30 ημέρες πριν τη δικάσιμο (άρθρο 523 παρ. 2 ΚΠολΔ)
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Αίτηση αναίρεσης - Από επίδοση Απόφασης */}
                      {formData.case.document_type === 'cassation' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label htmlFor="decision-service-date">Ημερομηνία επίδοσης Απόφασης *</Label>
                            <LargeDatePicker id="decision-service-date" data-testid="decision-service-date-input" value={formData.case.lawsuit_service_date} onChange={(e) => updateFormData('case', 'lawsuit_service_date', e.target.value)} />
                          </div>

                          <div>
                            <Label>Ο διάδικος διαμένει στο εξωτερικό ή είναι αγνώστου διαμονής; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="cassation-foreign-yes-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="cassation-foreign-no-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                            testidPrefix="cassation-public-entity"
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          {formData.case.lawsuit_service_date && formData.case.has_foreign_defendant && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμία αναίρεσης</h4>
                              <p className="text-sm text-slate-600 mb-3">
                                Προθεσμία άσκησης αναίρεσης: {formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από την επίδοση της απόφασης (άρθρο 564 παρ. 1 ΚΠολΔ). 
                                Δεν υπολογίζεται ο Αύγουστος.
                              </p>
                              
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-cassation-filing-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία άσκησης Αναίρεσης</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από επίδοση (564 παρ. 1 ΚΠολΔ, εξαιρείται Αύγουστος)
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ανακοπή κατά Διαταγής Πληρωμής (ΔΠ) */}
                      {formData.case.document_type === 'objection_dp' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label htmlFor="dp-service-date">Ημερομηνία επίδοσης Διαταγής Πληρωμής *</Label>
                            <LargeDatePicker id="dp-service-date" data-testid="dp-service-date-input" value={formData.case.lawsuit_service_date} onChange={(e) => updateFormData('case', 'lawsuit_service_date', e.target.value)} />
                          </div>

                          <div>
                            <Label>Ο διάδικος διαμένει στο εξωτερικό ή είναι αγνώστου διαμονής; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="dp-foreign-yes-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="dp-foreign-no-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="dp-hearing-date">Ημερομηνία δικασίμου (αν έχει οριστεί)</Label>
                            <LargeDatePicker id="dp-hearing-date" data-testid="dp-hearing-date-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                            testidPrefix="dp-public-entity"
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          {formData.case.lawsuit_service_date && formData.case.has_foreign_defendant && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμίες ανακοπής κατά ΔΠ</h4>
                              <p className="text-sm text-slate-600 mb-3">
                                Επιλέξτε τις προθεσμίες που θέλετε να υπολογιστούν:
                              </p>
                              
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-dp-filing-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία άσκησης Ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '30' : '15'} ημέρες από επίδοση ΔΠ (632 παρ. 1 ΚΠολΔ, εξαιρείται Αύγουστος)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-dp-service-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από κατάθεση (215 παρ. 2 ΚΠολΔ, εξαιρείται Αύγουστος)
                                  </p>
                                </div>
                              </label>

                              {formData.case.hearing_date && (
                                <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                  <input
                                    type="checkbox"
                                    data-testid="deadline-dp-proposals-checkbox"
                                    className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                    <p className="text-xs text-slate-600 mt-1">
                                      Στη δικάσιμο (632 ΚΠολΔ)
                                    </p>
                                  </div>
                                </label>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Ανακοπή κατά Διαταγής Απόδοσης Μισθίου — υπολογισμός προθεσμίας */}
                      {formData.case.document_type === 'rent_objection' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label htmlFor="dapm-service-date">Ημερομηνία επίδοσης Διαταγής Απόδοσης Μισθίου *</Label>
                            <LargeDatePicker id="dapm-service-date" data-testid="dapm-service-date-input" value={formData.case.lawsuit_service_date} onChange={(e) => updateFormData('case', 'lawsuit_service_date', e.target.value)} />
                          </div>

                          {formData.case.lawsuit_service_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμία προς Καταχώρηση</h4>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-dapm-filing-checkbox" defaultChecked className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία άσκησης ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">15 εργάσιμες ημέρες από την επίδοση της διαταγής (άρθρο 662Ε παρ. 1 ΚΠολΔ)</p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ανακοπή άρθρου 954 ΚΠολΔ — υπολογισμός προθεσμίας */}
                      {formData.case.document_type === 'objection_954' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label htmlFor="auction-954-date">Ημερομηνία πλειστηριασμού *</Label>
                            <LargeDatePicker id="auction-954-date" data-testid="auction-954-date-input" value={formData.case.enf_event_date} onChange={(e) => updateFormData('case', 'enf_event_date', e.target.value)} />
                          </div>

                          <div>
                            <Label>Η επιταγή προς εκτέλεση επιδόθηκε πριν την 1.1.2026; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="enf-954-old-yes-btn"
                                onClick={() => updateFormData('case', 'enf_954_old', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.enf_954_old === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="enf-954-old-no-btn"
                                onClick={() => updateFormData('case', 'enf_954_old', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.enf_954_old === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          {formData.case.enf_event_date && formData.case.enf_954_old && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμία προς Καταχώρηση</h4>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input type="checkbox" data-testid="deadline-954-filing-checkbox" defaultChecked className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία κατάθεσης ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">Έως {formData.case.enf_954_old === 'yes' ? '15' : '30'} εργάσιμες ημέρες πριν τον πλειστηριασμό (άρθρο 954 παρ. 4 ΚΠολΔ{formData.case.enf_954_old === 'yes' ? ' — επιταγή προ 1.1.2026' : ', ν. 5221/2025'})</p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ανακοπή άρθρου 933 ΚΠολΔ — branching 934α/934β */}
                      {formData.case.document_type === 'objection_933' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label>Περίπτωση 934 ΚΠολΔ *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                type="button"
                                data-testid="enf-934-kind-934a-btn"
                                onClick={() => { updateFormData('case', 'enf_934_kind', '934a'); updateFormData('case', 'enf_934b', ''); updateFormData('case', 'enf_event_date', ''); }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.enf_934_kind === '934a' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                934α (κατάσχεση/άμεση εκτ.)
                              </button>
                              <button
                                type="button"
                                data-testid="enf-934-kind-934b-btn"
                                onClick={() => { updateFormData('case', 'enf_934_kind', '934b'); updateFormData('case', 'enf_934a', ''); updateFormData('case', 'enf_event_date', ''); }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.enf_934_kind === '934b' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                934β (μεταγενέστερες πράξεις)
                              </button>
                            </div>
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                            testidPrefix="enf-public-entity"
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          {formData.case.enf_934_kind === '934a' && (
                            <div>
                              <Label>Είδος *</Label>
                              <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                                <button
                                  type="button"
                                  data-testid="enf-934a-seizure-btn"
                                  onClick={() => { updateFormData('case', 'enf_934a', 'seizure'); updateFormData('case', 'enf_event_date', ''); }}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.case.enf_934a === 'seizure' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                  }`}
                                >
                                  Κατάσχεση (45 ημ.)
                                </button>
                                <button
                                  type="button"
                                  data-testid="enf-934a-immediate-btn"
                                  onClick={() => { updateFormData('case', 'enf_934a', 'immediate'); updateFormData('case', 'enf_event_date', ''); }}
                                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    formData.case.enf_934a === 'immediate' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                  }`}
                                >
                                  Άμεση εκτέλεση (30 ημ.)
                                </button>
                              </div>
                            </div>
                          )}

                          {formData.case.enf_934_kind === '934b' && (
                            <div>
                              <Label>Είδος μεταγενέστερης πράξης *</Label>
                              <Select value={formData.case.enf_934b} onValueChange={(v) => { updateFormData('case', 'enf_934b', v); updateFormData('case', 'enf_event_date', ''); }}>
                                <SelectTrigger data-testid="enf-934b-select" className="h-11 bg-white border-stone-300 mt-2"><SelectValue placeholder="Επιλέξτε..." /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="last_action">Τελευταία πράξη εκτέλεσης (30 ημ.)</SelectItem>
                                  <SelectItem value="auction_movables">Πλειστηριασμός κινητών (30 ημ.)</SelectItem>
                                  <SelectItem value="auction_immovables">Πλειστηριασμός ακινήτων (60 ημ. από μεταγραφή)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          {/* Ημερομηνία γεγονότος — εμφανίζεται όταν επιλεγεί υποπερίπτωση */}
                          {((formData.case.enf_934_kind === '934a' && formData.case.enf_934a) || (formData.case.enf_934_kind === '934b' && formData.case.enf_934b)) && (
                            <div>
                              <Label htmlFor="enf-event-date">
                                {formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'seizure' && 'Ημερομηνία κατάσχεσης *'}
                                {formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'immediate' && 'Ημερομηνία επίδοσης επιταγής *'}
                                {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'last_action' && 'Ημερομηνία τελευταίας πράξης *'}
                                {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_movables' && 'Ημερομηνία πλειστηριασμού *'}
                                {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_immovables' && 'Ημερομηνία μεταγραφής κατασχετήριας *'}
                              </Label>
                              <LargeDatePicker id="enf-event-date" data-testid="enf-event-date-input" value={formData.case.enf_event_date} onChange={(e) => updateFormData('case', 'enf_event_date', e.target.value)} />
                            </div>
                          )}

                          {/* Warning όταν δεν έχει επιλεχθεί τίποτα ή η ημερομηνία */}
                          {(!formData.case.enf_934_kind || (formData.case.enf_934_kind === '934a' && !formData.case.enf_934a) || (formData.case.enf_934_kind === '934b' && !formData.case.enf_934b) || !formData.case.enf_event_date) && (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                              <p className="text-sm text-amber-800">
                                ⚠️ Δεν έχει ξεκινήσει η σχετική προθεσμία. Επιλέξτε περίπτωση 934 και συμπληρώστε την ημερομηνία γεγονότος.
                              </p>
                            </div>
                          )}

                          {formData.case.enf_event_date && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμία άσκησης Ανακοπής 933</h4>
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-933-filing-checkbox"
                                  defaultChecked
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία άσκησης Ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'seizure' && '45 ημέρες από την κατάσχεση (934 παρ. 1 περ. α ΚΠολΔ, εξαιρείται Αύγουστος)'}
                                    {formData.case.enf_934_kind === '934a' && formData.case.enf_934a === 'immediate' && '30 ημέρες από την επίδοση επιταγής (934 παρ. 1 περ. β ΚΠολΔ, εξαιρείται Αύγουστος)'}
                                    {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'last_action' && '30 ημέρες από την τελευταία πράξη εκτέλεσης (934 παρ. 1 περ. γ ΚΠολΔ, εξαιρείται Αύγουστος)'}
                                    {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_movables' && '30 ημέρες από τον πλειστηριασμό (934 παρ. 1 περ. β ΚΠολΔ, εξαιρείται Αύγουστος)'}
                                    {formData.case.enf_934_kind === '934b' && formData.case.enf_934b === 'auction_immovables' && '60 ημέρες από τη μεταγραφή της κατακυρωτικής (934 παρ. 1 περ. ε ΚΠολΔ, εξαιρείται Αύγουστος)'}
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Ανακοπή ερημοδικίας */}
                      {formData.case.document_type === 'objection_default' && formData.case.calculation_mode === 'deadline_calc' && (
                        <>
                          <div>
                            <Label htmlFor="erimodik-service-date">Ημερομηνία επίδοσης Ερήμην απόφασης *</Label>
                            <LargeDatePicker id="erimodik-service-date" data-testid="erimodik-service-date-input" value={formData.case.lawsuit_service_date} onChange={(e) => updateFormData('case', 'lawsuit_service_date', e.target.value)} />
                          </div>

                          <div>
                            <Label>Ο διάδικος διαμένει στο εξωτερικό ή είναι αγνώστου διαμονής; *</Label>
                            <div className="flex items-center gap-2 sm:gap-3 mt-2" style={{ width: '100%' }}>
                              <button
                                data-testid="erimodik-foreign-yes-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'yes')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'yes' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Ναι
                              </button>
                              <button
                                data-testid="erimodik-foreign-no-btn"
                                onClick={() => updateFormData('case', 'has_foreign_defendant', 'no')}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                  formData.case.has_foreign_defendant === 'no' ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
                                }`}
                              >
                                Όχι
                              </button>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="erimodik-hearing-date">Ημερομηνία δικασίμου (αν έχει οριστεί)</Label>
                            <LargeDatePicker id="erimodik-hearing-date" data-testid="erimodik-hearing-date-input" value={formData.case.hearing_date} onChange={(e) => updateFormData('case', 'hearing_date', e.target.value)} />
                          </div>

                          <PublicEntityToggle
                            value={formData.case.is_public_entity}
                            onChange={(v) => updateFormData('case', 'is_public_entity', v)}
                            testidPrefix="erimodik-public-entity"
                          />

                          <MediationToggle
                            value={formData.case.mediation_required}
                            onChange={(v) => updateFormData('case', 'mediation_required', v)}
                          />

                          {formData.case.lawsuit_service_date && formData.case.has_foreign_defendant && (
                            <div className="bg-stone-50 border border-stone-300 rounded-lg p-4 space-y-3">
                              <h4 className="font-semibold text-slate-900 mb-3">Προθεσμίες ανακοπής Ερημοδικίας</h4>
                              <p className="text-sm text-slate-600 mb-3">
                                Επιλέξτε τις προθεσμίες που θέλετε να υπολογιστούν:
                              </p>
                              
                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-erimodik-filing-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Προθεσμία άσκησης Ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '30' : '15'} ημέρες από επίδοση απόφασης (501 παρ. 1 ΚΠολΔ, εξαιρείται Αύγουστος)
                                  </p>
                                </div>
                              </label>

                              <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  data-testid="deadline-erimodik-service-checkbox"
                                  className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-slate-900">Επίδοση ανακοπής</p>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {formData.case.has_foreign_defendant === 'yes' ? '60' : '30'} ημέρες από κατάθεση (215 παρ. 2 ΚΠολΔ, εξαιρείται Αύγουστος)
                                  </p>
                                </div>
                              </label>

                              {formData.case.hearing_date && (
                                <label className="flex items-start gap-3 p-3 bg-white rounded border border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
                                  <input
                                    type="checkbox"
                                    data-testid="deadline-erimodik-proposals-checkbox"
                                    className="mt-1 h-5 w-5 cursor-pointer appearance-none border-2 border-stone-300 rounded checked:bg-red-700 checked:border-red-700 relative checked:after:content-['✓'] checked:after:text-white checked:after:absolute checked:after:top-[-2px] checked:after:left-[2px] checked:after:text-sm checked:after:font-bold"
                                  />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">Κατάθεση προτάσεων</p>
                                    <p className="text-xs text-slate-600 mt-1">
                                      Στη δικάσιμο (501 ΚΠολΔ)
                                    </p>
                                  </div>
                                </label>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      );
    } else if (formData.caseOption === 'existing') {
      return (
        <div className="space-y-6" data-testid="existing-case-select">
          <h3 className="flex items-center gap-2.5 text-sm sm:text-base font-semibold text-slate-900 tracking-tight">Επιλογή υπόθεσης</h3>
          <div>
            <Label htmlFor="case-select">Επιλέξτε υπόθεση *</Label>
            <Select
              value={formData.selectedCaseId}
              onValueChange={(value) => {
                setFormData(prev => ({ ...prev, selectedCaseId: value }));
                // Open the case modal when a case is selected
                if (value && onOpenCaseModal) {
                  onOpenCaseModal(value);
                }
              }}
            >
              <SelectTrigger data-testid="case-select" className="h-11 bg-white border-stone-300 rounded-lg items-center">
                <SelectValue placeholder="Επιλέξτε υπόθεση" />
              </SelectTrigger>
              <SelectContent>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {getCaseDisplayText(c, allEntries)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    return null;
  };

  const canSubmit = () => {
    // Πρέπει να έχει επιλεγεί τύπος εγγραφής
    if (!formData.entryType) return false;
    
    // Πρέπει να έχει επιλεγεί επιλογή πελάτη (νέος ή υπάρχων)
    if (!formData.clientOption) return false;
    
    // Έλεγχος για νέο πελάτη - ΟΛΑ τα βασικά πεδία είναι υποχρεωτικά
    if (formData.clientOption === 'new') {
      // Πρέπει να έχει επιλεγεί τύπος πελάτη
      if (!formData.clientType) return false;
      
      // Έλεγχος βασικών πεδίων ανάλογα με τον τύπο
      if (formData.clientType === 'natural') {
        // Για φυσικό πρόσωπο: μόνο Επώνυμο & Όνομα υποχρεωτικά
        if (!formData.client.surname || !formData.client.name) {
          return false;
        }
      } else if (formData.clientType === 'legal') {
        // Για νομικό πρόσωπο: μόνο Επωνυμία υποχρεωτική
        if (!formData.client.company_name) {
          return false;
        }
      }
    } else if (formData.clientOption === 'existing') {
      // Για υπάρχοντα πελάτη πρέπει να έχει επιλεγεί ID
      if (!formData.selectedClientId) return false;
    }

    // Για δικαστική πράξη, χρειάζεται και υπόθεση με πλήρη στοιχεία
    if (formData.entryType === 'court_action') {
      // Πρέπει να έχει επιλεγεί επιλογή υπόθεσης
      if (!formData.caseOption) return false;
      
      if (formData.caseOption === 'new') {
        // Για νέα υπόθεση, υποχρεωτικά: τύπος διαδικασίας
        // Η περιγραφή είναι προαιρετική
        if (!formData.case.procedure_type) {
          return false;
        }
      } else if (formData.caseOption === 'existing') {
        // Για υπάρχουσα υπόθεση, πρέπει να έχει επιλεγεί ID
        if (!formData.selectedCaseId) return false;
      }
    }
    
    // Για εξωδικαστική απασχόληση (ΠΕΔΙΟ 4)
    if (formData.entryType === 'other_activity') {
      // Πρέπει να έχει επιλεγεί τύπος (ραντεβού ή εργασία)
      if (!formData.case.appointment_type) return false;
      
      // Για ραντεβού: υποχρεωτική ημερομηνία
      if (formData.case.appointment_type === 'appointment') {
        if (!formData.case.appointment_date) return false;
      }
      
      // Για εργασία: υποχρεωτικός τύπος προθεσμίας και ημερομηνία
      if (formData.case.appointment_type === 'task') {
        if (!formData.case.task_has_deadline) return false;
        if (formData.case.task_has_deadline === 'specific_date' && !formData.case.task_date) return false;
        if (formData.case.task_has_deadline === 'deadline' && !formData.case.task_deadline) return false;
        // Υποχρεωτική περιγραφή για εργασία
        if (!formData.case.appointment_description) return false;
      }
      
      // Αν επιλέχθηκε προσθήκη αμοιβής, πρέπει να υπάρχει ποσό
      if (formData.case.appointment_add_to_financial && !formData.case.appointment_fee) return false;
    }
    
    // Όλοι οι έλεγχοι πέρασαν
    return true;
  };

  // ========== BULK DICTATION HANDLER ==========
  // Maps the JSON returned by GPT-5.2 to the formData state, pre-filling fields.
  const applyDictation = (data) => {
    if (!data || typeof data !== 'object') return;

    setFormData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));

      // Always treat as court_action when legal info is extracted
      if (data.procedure_type || data.action_type || data.gak || data.hearing_date) {
        next.entryType = 'court_action';
      }

      // Try to match an existing client by surname / company name (case-insensitive, accent-insensitive)
      const norm = (s) => toUpperCaseNoAccents(String(s || '').trim());
      const surnameKey = norm(data.client_surname);
      const companyKey = norm(data.client_company_name);
      let matchedClient = null;
      if (surnameKey || companyKey) {
        matchedClient = clients.find((c) => {
          const cs = norm(c.surname);
          const cc = norm(c.company_name);
          return (surnameKey && cs && cs === surnameKey) || (companyKey && cc && cc === companyKey);
        });
      }

      if (matchedClient) {
        next.clientOption = 'existing';
        next.selectedClientId = matchedClient.id;
        next.caseOption = 'new';
        fetchClientCases(matchedClient.id);
      } else if (data.client_surname || data.client_name || data.client_company_name) {
        next.clientOption = 'new';
        next.clientType = data.client_company_name ? 'company' : 'individual';
        next.client = {
          ...next.client,
          surname: data.client_surname ? toUpperCaseNoAccents(data.client_surname) : '',
          name: data.client_name ? toUpperCaseNoAccents(data.client_name) : '',
          company_name: data.client_company_name ? toUpperCaseNoAccents(data.client_company_name) : '',
        };
        next.caseOption = 'new';
      }

      // Case fields
      const c = next.case;
      if (data.procedure_type) c.procedure_type = data.procedure_type;
      if (data.action_type) c.action_type = data.action_type;
      if (data.court_type) c.court_type = data.court_type;
      if (data.court_location) c.court_location = data.court_location;
      if (data.gak) c.gak = data.gak;
      if (data.opponent_details) c.opponent_details = toUpperCaseNoAccents(data.opponent_details);
      if (data.case_description_type) c.case_description_type = data.case_description_type;
      if (data.case_description_custom) c.case_description_custom = data.case_description_custom;
      if (data.hearing_date) c.hearing_date = data.hearing_date;
      if (data.lawsuit_filing_date) c.lawsuit_filing_date = data.lawsuit_filing_date;
      if (data.lawsuit_service_date) c.lawsuit_service_date = data.lawsuit_service_date;

      // Entry-level fields
      if (data.title) next.entry.title = data.title;
      if (data.notes) next.entry.notes = data.notes;

      return next;
    });

    // Scroll the form so the user sees the populated sections
    setTimeout(() => {
      if (formEndRef.current) {
        formEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  };

  return (
    <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
    {/* Hidden fields to trick browser autocomplete */}
    <input type="text" name="prevent_autofill" id="prevent_autofill" style={{ display: 'none' }} />
    <input type="password" name="password_fake" id="password_fake" style={{ display: 'none' }} />
    <div className="space-y-6">
      {/* Entry Type Selection */}
      {renderEntryTypeSelection()}

      {/* Client Selection */}
      {formData.entryType && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent my-6"></div>
          {renderClientSelection()}
        </>
      )}

      {/* Client Form */}
      {formData.clientOption && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent my-6"></div>
          {renderClientForm()}
        </>
      )}

      {/* Case Selection (only for court actions) */}
      {formData.entryType === 'court_action' && formData.clientOption && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent my-6"></div>
          {renderCaseSelection()}
        </>
      )}

      {/* Case Form */}
      {formData.entryType === 'court_action' && formData.caseOption && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent my-6"></div>
          {renderCaseForm()}
        </>
      )}

      {/* ΠΕΔΙΟ 4 - Ραντεβού / Εξωδικαστική απασχόληση */}
      {formData.entryType === 'other_activity' && formData.clientOption && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent my-6"></div>
          {renderAppointmentForm()}
        </>
      )}

      {/* Auto-scroll reference point */}
      <div ref={formEndRef} />

      {/* Action Buttons */}
      <div className="flex gap-3 pt-5 mt-1 border-t border-stone-200">
        <Button
          data-testid="submit-btn"
          onClick={handleSubmit}
          disabled={!canSubmit() || isSubmitting}
          className="flex-1 h-11 rounded-xl bg-red-700 text-white font-semibold hover:bg-red-800 shadow-lg shadow-red-900/20 hover:shadow-red-900/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {isSubmitting ? 'Δημιουργία...' : 'Δημιουργία'}
        </Button>

        <Button
          data-testid="cancel-btn"
          variant="outline"
          onClick={onCancel}
          className="h-11 px-6 rounded-xl border-stone-300 text-slate-600 hover:bg-stone-50 hover:text-slate-900 transition-colors duration-200"
        >
          Ακύρωση
        </Button>
      </div>
    </div>
    </form>
  );
};

export default EntryWizard;